/**
 * Data access. Every Supabase query the app makes lives here, so the column
 * lists stay in one place and a schema change has one place to be fixed.
 */

import type {
  Balance,
  BalanceInput,
  ClosingTicks,
  PlanTerms,
  QuickbooksBillState,
} from "@roof-hawk/core";
import type {
  AiMaterialsRow,
  AiReadingRow,
  AiReason,
  AiSource,
  AiTermsRow,
  ChangeOrder,
  Cents,
  Contact,
  Crew,
  DocumentSignature,
  Estimate,
  EstimateItem,
  EstimatePayment,
  InvoiceSnapshot,
  Job,
  JobChecklistItem,
  JobContractParts,
  JobFileEntry,
  LadderInput,
  MarketingContact,
  MarketingPartner,
  MarketingPayment,
  NumericFromDb,
  JobExpense,
  PresentedDocument,
  MaterialOrder,
  MaterialReturn,
  Product,
  RoofLayout,
  ScopeQualifier,
  Supplier,
  TaskInvoice,
  TaskLatePlan,
  TaskReceipt,
  TaskSupplierMail,
  TaskMaterialReturn,
  SupplierMailKind,
  TaskMaterialOrder,
  TaskCall,
  TaskMention,
  TaskOutbound,
  TaskPayment,
  TaskProposal,
  TaskReading,
  TaskRow,
  MaterialsCheckRow,
  TaskMaterialStep,
  TaskWorkOrder,
  DerivedTask,
  UnsignedWork,
  PaymentTermsCheckRow,
  EstimateLineSold,
  QuoteCheckProduct,
  PricedSupplier,
  ProductPrice,
  QuoteMatchSuggestion,
  QuoteSiblingOrder,
  RoofTemplate,
  RoofTemplateItem,
  SignedContract,
  SoldOption,
  TemplateAnswers,
  WorkOrder,
} from "@roof-hawk/core";

import {
  AI_DISMISSED_DAYS,
  MATERIALS_WARN_AT,
  computeLadder,
  jobBalance,
  jobPlanStanding,
  planSentCents,
  documentPackages,
  unsignedChangeOrderWarning,
  unsignedWork,
  TERMS_WARN_AT,
  expenseBillAmount,
  invoiceAmount,
  contractAsSigned,
  signatureFor,
  boughtUpgrades,
  type CertificateEntry,
  type ChangeOrderEntry,
  type CompletionCertificate,
  type ChangeOrderDocument,
  type ChangeOrderWithdrawal,
  signedChangeOrderTotal,
  paymentSchedule,
  FINAL_SLOT,
  invoiceTxnDate,
  materialBillAmount,
  paidSlotsFromCustomerPayments,
  alreadyBeingCollected,
  linkMoneySaidInTheBank,
  isCheckDeposit,
  linksStillLiveBesideTheFinal,
  offScheduleMoney,
  slotsInTheBank,
  PAYMENT_SPLIT_THRESHOLD,
  payoutBillAmount,
  receiptRecordName,
  signedJobContractValues,
  signedContractOptions,
  signingFirstPaymentTyped,
  typedFirstPaymentAtSigning,
  toCents,
  cents,
  REPLY_READ_DAYS,
  replyText,
  type InboxMessage,
  type Signals,
  type WallMeasurement,
  SUPPLIER_MAIL_FILE_KIND,
  planManualSupplierFiling,
  senderMatchesSupplier,
  supplierDocumentTitle,
} from "@roof-hawk/core";

import { supabase, unwrap } from "./supabase";

/**
 * What every list screen reads off a job.
 *
 * A column missing here is not a missing column — it arrives as `undefined`,
 * TypeScript is satisfied because `Job` says it exists, and whatever reads it
 * quietly answers as though the job had nothing there. That is how "Signed
 * this month" read $0.00 through a month with four signings: `listSummary`
 * asks every job for `signed_contract_at`, and this list never fetched it.
 * `packages/core/test/listSummary.test.ts` decides which job lands in which
 * figure; this decides whether the figure has anything to decide from.
 */
const JOB_LIST_COLUMNS = `
  id,job_number,job_name,bid_type,stage,sends_at,created_at,inspection_at,work_date,
  signed_contract_at,
  follow_up_at,last_contact_at,lost_reason,lost_at,lost_note,
  rep_id,alt_rep_id,pm_id,coordinator_id,contact_id,is_test,
  inspector_id,inspector_crew_id,inspector_2_id,inspector_2_crew_id,
  inspection_event_error,
  address,city,state,zip,lat,lng,lead_source,
  contact:contacts!jobs_contact_id_fkey(id,full_name,first_name,last_name,company,address,city,state,zip,cell,phone,phone_label,email,exclude_personal_texts),
  rep:profiles!jobs_rep_id_fkey(full_name,phone)
`;

export async function fetchJobs(): Promise<Job[]> {
  return unwrap<Job[]>(
    await supabase
      .from("jobs")
      .select(JOB_LIST_COLUMNS)
      .order("created_at", { ascending: false }),
  );
}

/**
 * The four sums behind every job's contract value, for the list screens.
 *
 * One row per active estimate, company-wide, in one request. That is small —
 * an estimate, not its line items — which is the whole point of the view: the
 * alternative is `fetchLadderInputs` over every job on screen, which is seven
 * tables and tens of thousands of rows to print one figure a line.
 *
 * `contractAmount` in packages/core turns these into money. Nothing here adds
 * anything up (CLAUDE.md rule 5).
 */
export async function fetchJobContractParts(): Promise<JobContractParts[]> {
  return unwrap<JobContractParts[]>(
    await supabase
      .from("job_contract_parts")
      .select(
        "job_id,estimate_id,line_total,line_discount,change_orders,office_discount",
      )
      // Past PostgREST's default page of 1,000. There is one row per active
      // estimate, so this is thousands of jobs' worth of headroom.
      .limit(10000),
  );
}

/**
 * When each job's estimate was approved, for the jobs where one was.
 *
 * This stands in for a missing `signed_contract_at` and nothing else — see
 * `soldDate` in packages/core for the order and the reason. Kept as its own
 * small request rather than a column on `job_contract_parts`, because that
 * view is applied by hand to production and a select naming a column the
 * database does not have yet refuses the whole query, not just the new figure.
 *
 * Only approved estimates carry a date, so this is a handful of rows.
 */
/** One page of approvals. Supabase's `db-max-rows`, the same as the calls. */
const APPROVAL_PAGE = 1000;

/** Enough pages for many years of sold jobs, stated rather than implied. */
const APPROVAL_LIMIT = 20000;

export async function fetchEstimateApprovals(): Promise<Map<string, string>> {
  // Ordered ascending, so the first approval on a job is the one kept: a job
  // priced two ways and approved twice was sold on the day of the first yes.
  //
  // Read a page at a time, and not because the list is long today — it is a
  // few dozen rows. An approval is never cleared, so this table only ever
  // grows, and the day it passes a thousand the server would answer with the
  // oldest thousand, no error and nothing missing on the screen to see. What
  // it would drop is the most recent approvals, which are the only ones "this
  // month" can be counted from: the figure would read low and then zero, which
  // is the bug this whole change exists to fix, arriving a second time by a
  // different door. `.limit()` does not help — see `fetchCalls`, where asking
  // for five thousand returned one thousand.
  const byJob = new Map<string, string>();
  let read = 0;

  for (;;) {
    const page = unwrap<{ job_id: string; approved_at: string }[]>(
      await supabase
        .from("estimates")
        .select("job_id,approved_at")
        .not("approved_at", "is", null)
        .order("approved_at")
        .range(read, read + APPROVAL_PAGE - 1),
    );

    for (const row of page) {
      if (!byJob.has(row.job_id)) byJob.set(row.job_id, row.approved_at);
    }

    read += page.length;
    if (page.length < APPROVAL_PAGE || read >= APPROVAL_LIMIT) break;
  }

  return byJob;
}

/**
 * When each job's "Estimate Sent" and "Signed Contract" steps were ticked, for
 * the closing percentage on the sold report (`closingReport` in core).
 *
 * Paged for the same reason as the approvals above: every job adds rows and
 * none are removed, and past a thousand the server would quietly drop the
 * newest — the very ones "this month" is counted from.
 */
export async function fetchClosingTicks(): Promise<ClosingTicks> {
  const estimateSent = new Map<string, string>();
  const signedContract = new Map<string, string>();
  let read = 0;

  for (;;) {
    const page = unwrap<
      { job_id: string; label: string; completed_at: string | null }[]
    >(
      await supabase
        .from("job_checklist_items")
        .select("job_id,label,completed_at")
        .eq("phase", "prospect")
        .eq("status", "done")
        .in("label", ["Estimate Sent", "Signed Contract"])
        .order("id")
        .range(read, read + APPROVAL_PAGE - 1),
    );

    for (const row of page) {
      const into =
        row.label === "Estimate Sent" ? estimateSent : signedContract;
      // A tick with no time on it is still a tick; it counts under "all time".
      into.set(row.job_id, row.completed_at ?? "");
    }

    read += page.length;
    if (page.length < APPROVAL_PAGE || read >= APPROVAL_LIMIT) break;
  }

  return { estimateSent, signedContract };
}

/** The managed list of reasons a job can be written off. Active ones only. */
export async function fetchLostReasons(): Promise<
  { id: string; name: string }[]
> {
  return unwrap<{ id: string; name: string }[]>(
    await supabase
      .from("lost_reasons")
      .select("id,name")
      .eq("active", true)
      .order("sort_order")
      .order("name"),
  );
}

/**
 * What the ongoing-jobs board needs beyond the job list: every approved job's
 * checklist, and a rough size for each so the $1k deposit rule can be applied.
 *
 * The checklists come back in one request rather than one per job — the board
 * decides its columns from them, and the rule doing the deciding lives in
 * `productionColumn` so there is only one copy of it.
 */
export async function fetchProductionBoard(jobIds: readonly string[]): Promise<{
  items: JobChecklistItem[];
  sizes: Record<string, number>;
  /** The typed first payment per job, where a rep set one. */
  firstPayments: Record<string, number>;
  /**
   * What each job was signed for, before anything signed since.
   *
   * The deposit is half of this and stays there, so a change order approved
   * after the deposit arrived adds its whole amount to the Final (ADR 0205).
   * Absent for a job with no e-signature.
   */
  signedContracts: Record<string, number>;
  /** The typed middle draws — slots 2 and 3 — per job, where a rep set any. */
  middlePayments: Record<string, Record<number, number>>;
  /** The payment slots whose money is in the Mercury account, per job. */
  paidSlots: Record<string, number[]>;
  /** Paid links that settle no slot, per job (ADR 0141). */
  offSchedule: Record<string, number>;
  /** What each of those slots' Mercury requests actually billed, per job. */
  billedBySlot: Record<string, Record<number, number>>;
  /** Money already sent that the bank has not seen, per job. Usually absent. */
  onItsWay: Record<string, number>;
  /**
   * What an in-house payment plan adds, per job that has one or has had
   * payments recorded toward one (ADR 0286).
   */
  plans?: Record<string, ReturnType<typeof planBalanceInput>>;
}> {
  if (jobIds.length === 0) {
    return {
      items: [],
      sizes: {},
      firstPayments: {},
      signedContracts: {},
      middlePayments: {},
      paidSlots: {},
      offSchedule: {},
      billedBySlot: {},
      onItsWay: {},
    };
  }

  const [
    items,
    sizes,
    schedule,
    requests,
    deposits,
    handed,
    signedBases,
    jobPlans,
    planPayments,
  ] = await Promise.all([
    supabase
      .from("job_checklist_items")
      .select(
        "id,job_id,phase,label,status,item_type,allow_na,sort_order,completed_at,completed_by",
      )
      .in("job_id", jobIds as string[]),
    supabase
      .from("job_contract_size")
      .select("job_id,approx_contract")
      .in("job_id", jobIds as string[]),
    // For the balance. Without it a job whose rep agreed $5,000 up front on an
    // $18,000 roof would show half the roof as collected, which is a number
    // nobody agreed to.
    // Every slot the rep typed, not only the first. A paid draw is money in
    // the account and has to come off the balance, and the FINAL payment is
    // the contract less all three — reading slot 1 alone made it too big.
    supabase
      .from("estimate_payments")
      .select("slot, amount, estimates!inner(job_id, is_active)")
      .in("estimates.job_id", jobIds as string[])
      .eq("estimates.is_active", true)
      .in("slot", [1, 2, 3]),
    // Money that is actually in the account, from Mercury's two answers about
    // it. Jonathon, 2026-09-06: "what counts as paid should be when that
    // specific money hits our bank account in mercury."
    //
    // An invoice Mercury raised and collected...
    supabase
      .from("mercury_payment_requests")
      // What each paid request billed, for the same reason `fetchJobBalance`
      // asks for it: slot 4 does not bill the Final row.
      //
      // Every request, not only the paid ones, since 2026-09-14: what is on
      // its way comes off the same rows, and the board has to say what the
      // job screen and the letter say. `is_manual` comes with them rather
      // than being filtered here: a manual link bills no slot (ADR 0141),
      // but its money is as much on its way as any other.
      .select(
        "id,job_id,slot,state,is_manual,amount_cents,payment_started_at," +
          "mercury_deposit_id",
      )
      .in("job_id", jobIds as string[]),
    // ...and a settled credit read off the account, confirmed against a job by
    // a person. One card payment can be both; they are recorded per slot, and
    // a slot is paid once.
    supabase
      .from("mercury_deposits")
      // `description` so a check deposit can be told from a card payout, and
      // the amounts because a credit that ticked no slot is money the
      // schedule does not account for (ADR 0141).
      .select("id,job_id,slots,description,amount_cents,charge_cents")
      .in("job_id", jobIds as string[])
      .eq("state", "confirmed"),
    // ...and money handed over in person — cash, and checks that have
    // cleared (ADR 0125). The same third source `fetchJobBalanceInput` reads,
    // so the board and the job's own card agree.
    supabase
      // `mercury_deposit_id` as well: a recorded payment pointing at a credit
      // is what stops that credit being counted a second time.
      // ...and `mercury_payment_request_id`: a link a person said was in
      // the bank is counted, and is no longer on its way (ADR 0312).
      .from("customer_payments")
      .select(
        "job_id,state,slots,amount_cents,toward_plan,mercury_deposit_id," +
          "mercury_payment_request_id",
      )
      .in("job_id", jobIds as string[])
      .eq("state", "paid"),
    // What each job was signed for. Only the deposit's default reads it, to
    // stop that default moving when a change order is approved (ADR 0205).
    fetchSignedContractBases(jobIds),
    // An in-house payment plan, and the checks toward one, which count even
    // once the plan is gone (ADR 0286).
    fetchJobPlans(jobIds as string[]),
    fetchPlanPayments(jobIds),
  ]);

  const rows =
    unwrap<{ job_id: string; approx_contract: NumericFromDb }[]>(sizes);

  const signedContracts: Record<string, number> = {};
  for (const [jobId, amount] of signedBases) {
    if (amount > 0) signedContracts[jobId] = amount;
  }

  const firstPayments: Record<string, number> = {};
  const middlePayments: Record<string, Record<number, number>> = {};
  for (const row of unwrap<
    {
      slot: number | string;
      amount: NumericFromDb;
      estimates: { job_id: string } | null;
    }[]
  >(schedule)) {
    const jobId = row.estimates?.job_id;
    if (!jobId || row.amount == null || row.amount === "") continue;
    const slot = Number(row.slot);
    const amount = toCents(row.amount);
    if (slot === 1) {
      firstPayments[jobId] = amount;
      continue;
    }
    // A typed zero is a real answer — "nothing at this stage" — and it is not
    // a payment, so it is not carried and the line is simply not shown.
    if (amount <= 0) continue;
    const map = middlePayments[jobId] ?? (middlePayments[jobId] = {});
    map[slot] = amount;
  }

  const paidSlots: Record<string, number[]> = {};
  const markPaid = (jobId: string | null, slots: readonly unknown[]) => {
    if (!jobId) return;
    const already = paidSlots[jobId] ?? (paidSlots[jobId] = []);
    for (const slot of slots) {
      const n = Number(slot);
      if (Number.isFinite(n) && !already.includes(n)) already.push(n);
    }
  };
  const allRequests = unwrap<
    {
      id: string;
      job_id: string;
      slot: number | null;
      state: string;
      is_manual: boolean | null;
      amount_cents: number | null;
      payment_started_at: string | null;
      mercury_deposit_id: string | null;
    }[]
  >(requests);

  const billedBySlot: Record<string, Record<number, number>> = {};
  for (const row of allRequests.filter((one) => one.state === "paid")) {
    // A link raised by hand bills no slot. What its money does to the balance
    // is read off the credit that arrived, below.
    if (row.is_manual || row.slot == null) continue;
    /*
     * Read for what it BILLED, not for whether it is paid.
     *
     * `state = 'paid'` is Mercury's answer about an invoice — the payment was
     * taken — and for a card that is two or three days before the money is in
     * the account. What counts as paid is the credit itself (ADR 0139, and
     * Jonathon's rule of 2026-09-06 that it is the money hitting the bank).
     * `slotsInTheBank` in packages/core states that, and this is where it used
     * to be quietly the other way.
     */
    // Already cents in this column. A row with no amount is left out, and
    // `jobBalance` falls back to the schedule for that slot.
    const billed = Math.round(Number(row.amount_cents ?? 0));
    if (billed > 0) {
      const map = billedBySlot[row.job_id] ?? (billedBySlot[row.job_id] = {});
      map[Number(row.slot)] = billed;
    }
  }
  /**
   * Money that settled no scheduled payment, per job (ADR 0141).
   *
   * Read off the credits rather than the requests: a link raised by hand is
   * money once the bank has it and not before, which is the same rule as
   * everything else here.
   */
  const offSchedule: Record<string, number> = {};
  const confirmedDeposits = unwrap<
    {
      id: string;
      job_id: string | null;
      slots: number[] | null;
      description: string | null;
      amount_cents: number | null;
      charge_cents: number | null;
    }[]
  >(deposits);
  const handedRows = unwrap<
    {
      job_id: string;
      state: string;
      slots: number[] | null;
      amount_cents: number | null;
      toward_plan: boolean | null;
      mercury_deposit_id: string | null;
      mercury_payment_request_id: string | null;
    }[]
  >(handed);
  // Credits that a recorded check or cash payment already accounts for. Money
  // handed over counts from the moment it is recorded (ADR 0125), and the
  // credit that appears when somebody walks it to the bank is that same money
  // — counted here as well, a $6,630 check pays for $13,260 of roof (Josh,
  // 2026-09-14).
  const handedOverDeposits = new Set(
    handedRows
      .map((row) => row.mercury_deposit_id)
      .filter((id): id is string => Boolean(id)),
  );
  // What each link BILLED, which is the surcharge-free figure. The 3% on a
  // card is not part of the contract and must not settle any of it.
  const billedByDeposit = new Map<string, number>();
  for (const row of allRequests) {
    if (!row.mercury_deposit_id) continue;
    const billed = Math.round(Number(row.amount_cents ?? 0));
    if (billed > 0) billedByDeposit.set(row.mercury_deposit_id, billed);
  }
  for (const row of confirmedDeposits) {
    markPaid(row.job_id, row.slots ?? []);
    // A confirmed credit that ticked no slot is money the schedule does not
    // account for — a link raised by hand, most often (ADR 0141).
    if (!row.job_id) continue;
    offSchedule[row.job_id] =
      (offSchedule[row.job_id] ?? 0) +
      offScheduleMoney([
        {
          slots: row.slots,
          amountCents: row.amount_cents,
          chargeCents: row.charge_cents,
          billedCents: billedByDeposit.get(row.id) ?? null,
          handedOver:
            handedOverDeposits.has(row.id) ||
            isCheckDeposit({ description: row.description }),
        },
      ]);
  }
  for (const row of handedRows) {
    markPaid(row.job_id, paidSlotsFromCustomerPayments([row]));
  }
  // A link off the schedule a person said was in the bank counts at what it
  // billed until its credit is confirmed, then the credit counts (ADR 0312).
  const confirmedIds = new Set(confirmedDeposits.map((row) => row.id));
  const handedByJob = new Map<string, typeof handedRows>();
  for (const row of handedRows) {
    const list = handedByJob.get(row.job_id);
    if (list) list.push(row);
    else handedByJob.set(row.job_id, [row]);
  }
  const requestsByJob = new Map<string, typeof allRequests>();
  for (const row of allRequests) {
    const list = requestsByJob.get(row.job_id);
    if (list) list.push(row);
    else requestsByJob.set(row.job_id, [row]);
  }
  for (const [jobId, list] of handedByJob) {
    const said = linkMoneySaidInTheBank(
      list.map((row) => ({
        state: row.state,
        amountCents: row.amount_cents,
        slots: row.slots ?? [],
        towardPlan: row.toward_plan === true,
        mercuryRequestId: row.mercury_payment_request_id,
        depositId: row.mercury_deposit_id,
      })),
      (requestsByJob.get(jobId) ?? []).map((row) => ({
        id: row.id,
        mercuryDepositId: row.mercury_deposit_id,
      })),
      confirmedIds,
    );
    if (said > 0) offSchedule[jobId] = (offSchedule[jobId] ?? 0) + said;
  }
  const saidInTheBank = new Set(
    handedRows
      .map((row) => row.mercury_payment_request_id)
      .filter((id): id is string => Boolean(id)),
  );

  /*
   * What each customer has sent that the bank has not seen.
   *
   * The board used to read "Owes $13,260" on a job whose customer had paid the
   * $6,630 deposit by card on Friday, while the letter that went out the same
   * afternoon asked for $6,630 and said the rest was on its way (Josh,
   * 2026-09-14). Jonathon: show it as on the way, everywhere. The rule is
   * `alreadyBeingCollected` in packages/core, the same one the letter and the
   * job screen use, so the three cannot drift apart.
   */
  const onItsWay: Record<string, number> = {};
  for (const [jobId, list] of requestsByJob) {
    const amount = alreadyBeingCollected(
      list.map((row) => ({
        state: String(row.state),
        amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
        paymentStartedAt: row.payment_started_at ?? null,
        slot: row.slot ?? null,
        // Confirmed, not merely claimed: the claim is written before the
        // confirmation and reading it as arrival billed a customer twice.
        landed: Boolean(
          row.mercury_deposit_id && confirmedIds.has(row.mercury_deposit_id),
        ),
        saidInTheBank: saidInTheBank.has(row.id),
      })),
      paidSlots[jobId] ?? [],
    );
    if (amount > 0) onItsWay[jobId] = amount;
  }

  return {
    items: unwrap<JobChecklistItem[]>(items),
    sizes: Object.fromEntries(
      rows.map((r) => [r.job_id, toCents(r.approx_contract)]),
    ),
    firstPayments,
    signedContracts,
    middlePayments,
    paidSlots,
    billedBySlot,
    onItsWay,
    offSchedule,
    plans: Object.fromEntries(
      [...new Set([...jobPlans.keys(), ...planPayments.keys()])].map(
        (jobId) => [
          jobId,
          planBalanceInput(jobPlans.get(jobId), planPayments.get(jobId) ?? []),
        ],
      ),
    ),
  };
}

/**
 * Every work order on a job, as the checklist reads them.
 *
 * The two work steps are decided by these — a job can be a roof, a roof plus
 * other trades, or no roof at all, and the work orders are the only place that
 * already knows which.
 *
 * Only the live ones. Work priced on an option the customer did not buy must
 * never hold the checklist open waiting to be scheduled.
 */
export async function fetchJobWorkOrders(jobId: string): Promise<WorkOrder[]> {
  return unwrap<WorkOrder[]>(
    await supabase
      .from("work_orders")
      .select(
        "id,job_id,trades,status,scheduled_at,start_at,completed_at,crew_id, crew:crews(name,contact_name,is_in_house)",
      )
      .eq("job_id", jobId)
      .eq("is_live", true)
      .order("scheduled_at", { nullsFirst: false }),
  );
}

/**
 * Scheduled work orders, for the calendar. Every crew's day, not just a roof.
 *
 * `start_at` comes back as well as `scheduled_at`, and it is the one the
 * calendar places the crew on. `scheduled_at` is a checklist stamp — the moment
 * somebody pressed "Schedule" — so an order ticked on Monday for the following
 * Tuesday was appearing on Monday. See `bookedDay`.
 *
 * Still filtered on `scheduled_at`, because that is what makes an order
 * scheduled at all. An order with a start date and no tick is a plan; an order
 * with a tick and no start date is scheduled without a day yet, and the
 * calendar has nowhere to put it until somebody says when.
 */
export async function fetchScheduledWork(): Promise<
  {
    id: string;
    job_id: string;
    trades: string | null;
    scheduled_at: string;
    start_at: string | null;
    end_at: string | null;
    completed_at: string | null;
    crew?: { name: string | null; contact_name?: string | null } | null;
  }[]
> {
  return unwrap(
    await supabase
      .from("work_orders")
      .select(
        "id,job_id,trades,scheduled_at,start_at,end_at,completed_at, crew:crews(name,contact_name)",
      )
      .not("scheduled_at", "is", null)
      .eq("is_live", true)
      .order("start_at", { nullsFirst: false }),
  );
}

/**
 * Everything the ladder needs for a set of jobs, in one round of queries.
 *
 * The contact page adds up what someone has been worth, and those figures have
 * to agree with the estimate card to the cent — so they are computed the same
 * way, by `computeLadder`, from the same rows. Fetching per job would be a
 * query storm; these are bulk reads keyed by the jobs asked for.
 */
/**
 * What a person has done to the calculated expenses on one estimate.
 *
 * Two maps, both keyed by expense key — the shape `applyOverrides` and
 * `applyRemovals` take. One query rather than two: the facts live in the same
 * row and a screen that had one without the other would show a struck-off
 * expense still counting, or a corrected amount at the figure the rule gave.
 *
 * Every row is read, not just the adjusted ones. Filtering in the query would
 * mean two filters that have to agree, and PostgREST's `or` across a null test
 * and a boolean is the kind of thing that is wrong once and quiet forever. The
 * table holds one row per calculated expense at most.
 */
export interface AutoExpenseAdjustments {
  overrides: Record<string, unknown>;
  removals: Record<string, boolean>;
}

export async function fetchAutoExpenseAdjustments(
  estimateId: string,
): Promise<AutoExpenseAdjustments> {
  const rows = unwrap<
    { expense_key: string; amount_override: unknown; removed: boolean | null }[]
  >(
    await supabase
      .from("auto_expense_payments")
      .select("expense_key,amount_override,removed")
      .eq("estimate_id", estimateId),
  );
  const overrides: Record<string, unknown> = {};
  const removals: Record<string, boolean> = {};
  for (const row of rows) {
    if (row.amount_override != null)
      overrides[row.expense_key] = row.amount_override;
    if (row.removed) removals[row.expense_key] = true;
  }
  return { overrides, removals };
}

/**
 * Each option a job is selling, and what its own ladder says it is worth.
 *
 * What `invoiceAmount` adds up, beside the signatures. `LadderInput["estimate"]`
 * is a Pick of the money columns; the row `fetchLadderInputs` handed back
 * carries its id, which is what ties an option to the signature that bought it.
 */
export function soldOptions(
  jobType: string | null | undefined,
  inputs: readonly Omit<LadderInput, "jobType">[] | undefined,
): SoldOption[] {
  return (inputs ?? []).map((input) => ({
    estimateId: (input.estimate as unknown as { id: string }).id,
    ladderTotal: computeLadder({ ...input, jobType }).grandTotal,
    signedChangeOrders: input.changeOrders
      .filter((row) => Boolean(row.signed_at))
      .map((row) => ({
        amount: signedChangeOrderTotal([row]),
        signedAt: String(row.signed_at),
      })),
  }));
}

export async function fetchLadderInputs(
  jobIds: readonly string[],
): Promise<Map<string, Omit<LadderInput, "jobType">[]>> {
  const out = new Map<string, Omit<LadderInput, "jobType">[]>();
  if (jobIds.length === 0) return out;

  const ids = jobIds as string[];
  const estimates = unwrap<Estimate[]>(
    await supabase
      .from("estimates")
      .select("*")
      .in("job_id", ids)
      .eq("is_active", true)
      .order("created_at"),
  );
  if (estimates.length === 0) return out;

  const estimateIds = estimates.map((e) => e.id);
  const [
    items,
    changeOrders,
    workOrders,
    materialOrders,
    expenses,
    returns,
    layouts,
    overrides,
    surcharges,
    insuranceLines,
    signedBy,
  ] = await Promise.all([
    supabase.from("estimate_items").select("*").in("estimate_id", estimateIds),
    supabase.from("change_orders").select("*").in("estimate_id", estimateIds),
    // Orders belong to their estimate now, so they are fetched by it. A job
    // selling two options costs what both of them cost, and neither one is
    // charged for the other's material.
    supabase
      .from("work_orders")
      .select(
        // The crew's insurance columns as well as its name: the ladder
        // rates liability and comp off what each certificate says.
        "*, crew:crews(name,contact_name,has_gl,gl_expires_on,has_wc,wc_expires_on,is_in_house), items:work_order_items(*)",
      )
      .in("estimate_id", estimateIds),
    supabase
      .from("material_orders")
      .select("*, items:material_order_items(*)")
      .in("estimate_id", estimateIds),
    supabase.from("job_expenses").select("*").in("estimate_id", estimateIds),
    supabase
      .from("material_returns")
      .select("*")
      .in("estimate_id", estimateIds),
    // job_measurements. There has never been a `roof_layouts` table: that is
    // the filename of migration 20260731220000, which fills this one out, and
    // whoever wrote this query on 2026-08-04 read the filename as a table name
    // — a mistake CLAUDE.md repeated, which is where it was probably copied
    // from. So every call has thrown since the day it was written, silently,
    // because the callers swallowed it. Found on 2026-08-22 when a QuickBooks
    // invoice never got an amount; the Sold scoreboard, contact lifetime value
    // and the estimate email had simply never read a measurement in
    // production.
    supabase.from("job_measurements").select("*").in("job_id", ids),
    supabase
      .from("auto_expense_payments")
      .select("estimate_id,expense_key,amount_override,removed")
      .in("estimate_id", estimateIds),
    // The 3% a customer added by paying on a credit card.
    //
    // Only `paid` rows. The fee is charged when the invoice is raised, but it
    // counts on the estimate when the money actually lands — Jonathon's words
    // were "the actual amount charged AND PAID by the customer". It also means
    // a customer who changes their mind, or pays an older bank transfer link
    // instead, never leaves a fee on the estimate that nobody paid.
    supabase
      .from("mercury_payment_requests")
      .select("estimate_id,surcharge_cents,state")
      .in("estimate_id", estimateIds)
      .eq("state", "paid"),
    // Insurance budgeted for a sub who has no work order yet. Same crew
    // columns as the work orders above, and for the same reason: the rate is
    // read off that crew's certificates, not off a percentage.
    supabase
      .from("estimate_insurance_lines")
      .select(
        "id,estimate_id,crew_id,labor_amount,trade, crew:crews(name,contact_name,has_gl,gl_expires_on,has_wc,wc_expires_on,is_in_house)",
      )
      .in("estimate_id", estimateIds),
    // What each option was signed for: the Mountain Brook permit is worked
    // out on it once there is one (ADR 0289), the same figure the pink form's
    // valuation box reads.
    fetchSignedContracts(ids),
  ]);

  type ByJob = { job_id: string };
  type ByEstimate = { estimate_id: string };
  const group = <T>(rows: T[], key: (row: T) => string) => {
    const map = new Map<string, T[]>();
    for (const row of rows) {
      const k = key(row);
      const list = map.get(k);
      if (list) list.push(row);
      else map.set(k, [row]);
    }
    return map;
  };

  const itemsBy = group(
    unwrap<(EstimateItem & ByEstimate)[]>(items),
    (r) => r.estimate_id,
  );
  const cosBy = group(
    unwrap<(ChangeOrder & ByEstimate)[]>(changeOrders),
    (r) => r.estimate_id,
  );
  const wosBy = group(
    unwrap<(WorkOrder & ByEstimate)[]>(workOrders),
    (r) => r.estimate_id,
  );
  const insBy = group(
    unwrap<(EstimateInsuranceLine & ByEstimate)[]>(insuranceLines),
    (r) => r.estimate_id,
  );
  const mosBy = group(
    unwrap<(MaterialOrder & ByEstimate)[]>(materialOrders),
    (r) => r.estimate_id,
  );
  const expBy = group(
    unwrap<(JobExpense & ByEstimate)[]>(expenses),
    (r) => r.estimate_id,
  );
  const retBy = group(
    unwrap<({ amount: unknown; tax_rate?: unknown } & ByEstimate)[]>(returns),
    (r) => r.estimate_id,
  );
  const feeBy = group(
    unwrap<({ surcharge_cents: unknown } & ByEstimate)[]>(surcharges),
    (r) => r.estimate_id,
  );
  const layBy = group(
    unwrap<
      ({ squares?: unknown; waste_pct?: unknown; layers?: unknown } & ByJob)[]
    >(layouts),
    (r) => r.job_id,
  );
  // Two maps per estimate, keyed by expense key — the shapes `applyOverrides`
  // and `applyRemovals` want. A row exists for any calculated expense somebody
  // has touched at all, including one they only set a payment type on, so each
  // fact is tested rather than assumed from the row being there.
  const ovrBy = new Map<string, Record<string, unknown>>();
  const rmBy = new Map<string, Record<string, boolean>>();
  for (const row of unwrap<
    ({
      expense_key: string;
      amount_override: unknown;
      removed: boolean | null;
    } & ByEstimate)[]
  >(overrides)) {
    if (row.amount_override != null) {
      const forEstimate = ovrBy.get(row.estimate_id) ?? {};
      forEstimate[row.expense_key] = row.amount_override;
      ovrBy.set(row.estimate_id, forEstimate);
    }
    if (row.removed) {
      const forEstimate = rmBy.get(row.estimate_id) ?? {};
      forEstimate[row.expense_key] = true;
      rmBy.set(row.estimate_id, forEstimate);
    }
  }

  for (const estimate of estimates) {
    const jobId = estimate.job_id;
    const estimateWorkOrders = wosBy.get(estimate.id) ?? [];
    const sofar = out.get(jobId) ?? [];

    out.set(jobId, [
      ...sofar,
      {
        estimate,
        items: itemsBy.get(estimate.id) ?? [],
        changeOrders: cosBy.get(estimate.id) ?? [],
        workOrders: estimateWorkOrders,
        materialOrders: mosBy.get(estimate.id) ?? [],
        expenses: expBy.get(estimate.id) ?? [],
        returns: retBy.get(estimate.id) ?? [],
        // `surcharge_cents` is integer cents already, unlike every money
        // column beside it. The ladder's field is named `amountCents` for
        // exactly that reason.
        cardSurcharges: (feeBy.get(estimate.id) ?? []).map((row) => ({
          amountCents: row.surcharge_cents,
        })),
        // The roof is measured once, so the one expense priced off the
        // measurement — the manufacturer warranty, at $7 or $10 a square with
        // waste — is charged once. Every other bucket moved to `estimate_id`
        // under ADR 0030, but a measurement is a fact about the building and
        // has nowhere else to go, so handing the same squares to both active
        // estimates on a multi-option job would put two warranties on one roof.
        //
        // The first active estimate carries them (`estimates` is ordered by
        // `created_at` above, so this is stable rather than whichever row the
        // planner returned). It is the closest thing to a rule: the option that
        // has been on the job longest is the one the roof was measured for.
        layouts: sofar.length === 0 ? (layBy.get(jobId) ?? []) : [],
        workOrderCrews: estimateWorkOrders.map((o) => o.crew ?? null),
        insuranceLines: insBy.get(estimate.id) ?? [],
        // Amounts somebody typed over the calculated expenses. Fetched here so
        // that every screen computing a ladder gets them without asking: an
        // edited lift that only the estimate card knew about would make the
        // job list disagree with the job.
        autoExpenseOverrides: ovrBy.get(estimate.id) ?? null,
        autoExpenseRemovals: rmBy.get(estimate.id) ?? null,
        signature: signatureFor(estimate.id, signedBy.get(jobId)),
      },
    ]);
  }

  return out;
}

/**
 * Work orders that are finished, or already through the payout review.
 *
 * The dashboard sorts them into this week's run; in-house crews are dropped by
 * `payoutRun` rather than here, so the rule lives in one place.
 *
 * `job.is_test` comes back so the board can drop practice jobs: nobody is owed
 * anything for a roof that was never installed, and a payout run is money
 * leaving the company. Filtered on the board rather than in the query, because
 * a PostgREST filter on an embedded row filters the embedding, not the parent.
 *
 * Every completed work order, whichever estimate it belongs to. Deliberately not filtered on `is_live`. A crew that finished the work is
 * owed for it, and switching the job to another estimate afterwards does not
 * change that. Two approved payouts vanished from the board that way before
 * this was fixed (ADR 0160).
 */
export async function fetchPayoutQueue() {
  const orders = unwrap<Record<string, unknown>[]>(
    await supabase
      .from("work_orders")
      .select(
        `id,job_id,order_number,trades,status,scheduled_at,completed_at,
         tax_rate,reconcile_amt,
         payout_approved_at,payout_approved_by,payout_amount,
         payout_rejected_at,payout_rejected_by,payout_note,payout_sent_at,
         payout_held_at,payout_held_by,
         holder:profiles!work_orders_payout_held_by_fkey(full_name),
         payout_paid_at,payout_paid_by,
         payout_paid_manually_at,payout_paid_manually_by,
         crew:crews(name,contact_name,is_in_house),
         items:work_order_items(qty,unit_price),
         job:jobs!work_orders_job_id_fkey(job_number, is_test, contact:contacts!jobs_contact_id_fkey(full_name))`,
      )
      .not("completed_at", "is", null)
      .order("completed_at"),
  );

  // Which of the approved, unpaid ones QuickBooks already has a bill for, so
  // Undo can warn before it takes one back (ADR 0179). Only those rows offer
  // Undo, which keeps the list of ids short.
  const undoable = orders
    .filter((o) => o.payout_approved_at && !o.payout_paid_at)
    .map((o) => String(o.id));
  const billed = new Set(
    undoable.length === 0
      ? []
      : unwrap<{ source_id: string }[]>(
          await supabase
            .from("quickbooks_bills")
            .select("source_id")
            .eq("source_kind", "crew_payout")
            .eq("state", "sent")
            .in("source_id", undoable),
        ).map((b) => b.source_id),
  );
  return orders.map((o) => ({ ...o, bill_sent: billed.has(String(o.id)) }));
}

export async function fetchContacts(): Promise<Contact[]> {
  return unwrap<Contact[]>(
    await supabase.from("contacts").select("*").order("full_name"),
  );
}

export async function fetchJob(id: string): Promise<Job | null> {
  const { data, error } = await supabase
    .from("jobs")
    .select(
      `*,
       contact:contacts!jobs_contact_id_fkey(*),
       rep:profiles!jobs_rep_id_fkey(full_name,phone),
       creator:profiles!jobs_created_by_fkey(full_name)`,
    )
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Job) ?? null;
}

/**
 * Whether a job's options add up, read now.
 *
 * The database sets `multi_option` for itself whenever an estimate is added,
 * deleted or retyped (ADR 0123), so the copy a screen loaded with the job can
 * be out of date the moment somebody changes an estimate. Anything that freezes
 * it into a document, or tells a rep who set it, asks here first.
 */
export async function fetchJobMultiOption(
  jobId: string,
): Promise<{ multi_option: boolean; multi_option_manual: boolean }> {
  const { data, error } = await supabase
    .from("jobs")
    .select("multi_option,multi_option_manual")
    .eq("id", jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  const row = data as {
    multi_option?: boolean | null;
    multi_option_manual?: boolean | null;
  } | null;
  if (!row) throw new Error("That job no longer exists.");
  return {
    multi_option: Boolean(row.multi_option),
    multi_option_manual: Boolean(row.multi_option_manual),
  };
}

export async function fetchChecklist(
  jobId: string,
): Promise<JobChecklistItem[]> {
  return unwrap<JobChecklistItem[]>(
    await supabase
      .from("job_checklist_items")
      .select("*")
      .eq("job_id", jobId)
      .order("phase")
      .order("sort_order"),
  );
}

export async function fetchContact(id: string): Promise<Contact | null> {
  const { data, error } = await supabase
    .from("contacts")
    .select("*")
    .eq("id", id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Contact) ?? null;
}

export async function fetchContactJobs(contactId: string): Promise<Job[]> {
  return unwrap<Job[]>(
    await supabase
      .from("jobs")
      .select(
        `id,job_number,job_name,stage,bid_type,job_type,address,city,state,zip,created_at,sends_at,
         lost_reason,lost_at,lead_source,
         rep:profiles!jobs_rep_id_fkey(full_name,phone)`,
      )
      .eq("contact_id", contactId)
      .order("created_at", { ascending: false }),
  );
}

/** Reference data cached for the estimate and order editors. */
export interface ReferenceData {
  products: Product[];
  suppliers: Supplier[];
  crews: Crew[];
}

export async function fetchReferenceData(): Promise<ReferenceData> {
  const [products, suppliers, crews] = await Promise.all([
    supabase
      .from("products")
      .select("*")
      .eq("active", true)
      .order("sort_order")
      .order("name"),
    supabase
      .from("suppliers")
      .select("*")
      .eq("active", true)
      .order("sort_order")
      .order("name"),
    supabase
      .from("crews")
      .select("*")
      .eq("active", true)
      .order("sort_order")
      .order("name"),
  ]);
  return {
    products: unwrap<Product[]>(products),
    suppliers: unwrap<Supplier[]>(suppliers),
    crews: unwrap<Crew[]>(crews),
  };
}

/**
 * The job behind a work order, in the shape a crew booking needs.
 *
 * Its own read rather than a join on the order, because the work order editor
 * is opened from three screens and only one of them already holds the job. What
 * a crew needs is the address they are driving to and the number to quote on
 * the phone — nothing about money.
 */
export async function fetchJobForBooking(jobId: string): Promise<Job | null> {
  const { data, error } = await supabase
    .from("jobs")
    .select(
      "id,job_number,address,city,state,zip, " +
        "contact:contacts!jobs_contact_id_fkey(full_name,cell,phone,address,city,state,zip)",
    )
    .eq("id", jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Job | null) ?? null;
}

/** `company_settings`, the single row the customer documents quote. */
export interface CompanySettingsRow {
  name: string | null;
  address: string | null;
  email: string | null;
  /** Where a customer emails to cancel. Falls back to `email` on the contract. */
  cancellation_email: string | null;
  phone: string | null;
  /** Printed on the receipt's letterhead (ADR 0125). */
  website: string | null;
  license_number: string | null;
  /** The company's licence with each city, for its permit form (ADR 0289). */
  permit_city_license_numbers?: Record<string, unknown> | null;
  certifications: string[] | null;
  reviews: string | null;
  insurance: string | null;
  deck_sheet_price: number | string | null;
  deck_board_price: number | string | null;
  extra_layer_price: number | string | null;
  financing_heading: string | null;
  /** Where "Get Prequalified" and "Apply for financing" send the customer. */
  financing_application_url: string | null;
  /**
   * Where "See our work" sends the customer from the proposal — our own
   * playlist of finished jobs. Empty leaves that page off the document.
   */
  work_gallery_url: string | null;
  /** Whether the company may offer financing at all. Off until the lender approves. */
  financing_offered: boolean | null;
  /**
   * The term and the rate every estimate starts from — typed once in Tools
   * rather than on every job, and overridden per estimate when a deal differs
   * (ADR 0201). Null anywhere leaves the $13-per-$1,000 illustration in place.
   */
  financing_term_months: number | null;
  financing_apr: number | string | null;
  /**
   * The lending programmes the customer's payment calculator offers, as
   * stored. Read through `financingPrograms()` in core, never directly — it
   * drops anything unusable and falls back to the built-in list (ADR 0202).
   */
  financing_programs: unknown;
  /**
   * Whether the partner network is used at all. Off since 2026-09-14: built,
   * never used, and hidden rather than deleted (ADR 0144).
   */
  partners_enabled: boolean | null;
  /**
   * Whether a material order is checked against the estimate it came from.
   * Off since 2026-09-21: built, right, and too noisy on a real order, so it
   * is hidden rather than deleted (ADR 0190).
   */
  quote_check_enabled: boolean | null;
  show_financing_default: boolean | null;
  show_financing_qr_default: boolean | null;
  /**
   * Whose connected Gmail carries a note tag when the note's own author has no
   * mailbox — an agent, always, and a rep who has not connected Google. Null
   * means those notifications are not sent.
   */
  notify_from_profile_id: string | null;
  /**
   * Whose connected Gmail carries Thursday morning's payout reminder, and who
   * it reaches. Either one empty means the reminder is not sent at all — that
   * is the switch (ADR 0177).
   */
  payout_reminder_from_profile_id: string | null;
  payout_reminder_profile_ids: string[] | null;
  /** The last calendar date it went out for, so it cannot go twice. */
  payout_reminder_sent_for: string | null;
  /** The same pair for the monthly insurance email on the 1st (ADR 0275). */
  insurance_email_from_profile_id?: string | null;
  insurance_email_profile_ids?: string[] | null;
  insurance_email_sent_for?: string | null;
  /**
   * Who finished jobs are sent to close — Kelsey. Their board gets a tab and
   * their own address gets the summary. Absent on a database not migrated yet,
   * which reads as nobody (ADR 0273).
   */
  closeout_profile_id?: string | null;
}

/**
 * Who the Thursday payout reminder comes from and goes to.
 *
 * Both are profiles rather than addresses: the mail leaves through the sender's
 * connected Google account, and a recipient list of typed-in addresses is a
 * list nobody updates when somebody leaves.
 */
export async function setPayoutReminder(input: {
  fromProfileId: string | null;
  profileIds: string[];
}): Promise<void> {
  const { error } = await supabase
    .from("company_settings")
    .update({
      payout_reminder_from_profile_id: input.fromProfileId,
      payout_reminder_profile_ids: input.profileIds,
    })
    .eq("id", 1);
  if (error) throw new Error(error.message);
}

/** Who the monthly insurance email comes from and goes to (ADR 0275). */
export async function setInsuranceEmail(input: {
  fromProfileId: string | null;
  profileIds: string[];
}): Promise<void> {
  const { error } = await supabase
    .from("company_settings")
    .update({
      insurance_email_from_profile_id: input.fromProfileId,
      insurance_email_profile_ids: input.profileIds,
    })
    .eq("id", 1);
  if (error) throw new Error(error.message);
}

/**
 * Every work order whose crew has been paid, with what the insurance report
 * needs: the amount, the trades, and the crew's certificates (ADR 0275).
 *
 * A page at a time, as the insurance email reads them. The server answers any
 * one request with at most 1,000 rows and no sign the rest exist, and sorted
 * oldest first the rows it drops are the newest: this month, the very figure
 * Lysa pays comp from. `id` breaks ties in `paid_at` so a page boundary
 * neither repeats nor skips an order.
 */
const PAID_ORDER_PAGE = 1000;

export async function fetchPaidWorkOrders() {
  const all: Record<string, unknown>[] = [];
  for (;;) {
    const page = unwrap<Record<string, unknown>[]>(
      await supabase
        .from("work_orders")
        .select(
          `id,order_number,paid_at,payout_amount,trades,crew_id,
           tax_rate,reconcile_amt,
           crew:crews(id,name,contact_name,is_in_house,has_gl,gl_expires_on,has_wc,wc_expires_on),
           items:work_order_items(qty,unit_price),
           job:jobs!work_orders_job_id_fkey(job_number, is_test, contact:contacts!jobs_contact_id_fkey(full_name))`,
        )
        .not("paid_at", "is", null)
        .order("paid_at")
        .order("id")
        .range(all.length, all.length + PAID_ORDER_PAGE - 1),
    );
    all.push(...page);
    if (page.length < PAID_ORDER_PAGE) return all;
  }
}

/**
 * Choose the mailbox that carries a tag nobody else can send.
 *
 * Null is a real answer and the way to turn it off: the notification is not
 * sent, and the tool that would have sent it says so rather than reporting a
 * success nobody received.
 */
export async function setNotifyFromProfile(
  profileId: string | null,
): Promise<void> {
  const { error } = await supabase
    .from("company_settings")
    .update({ notify_from_profile_id: profileId })
    .eq("id", 1);
  if (error) throw new Error(error.message);
}

/**
 * The company's own details, as the customer documents quote them.
 *
 * One row, always id 1. Read rather than joined because it changes about once a
 * year and every presented document needs it.
 */
export async function fetchCompanySettings(): Promise<CompanySettingsRow | null> {
  const { data, error } = await supabase
    .from("company_settings")
    .select("*")
    .eq("id", 1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as CompanySettingsRow | null) ?? null;
}

/**
 * Present an estimate as a locked customer document.
 *
 * One call, because it is one transaction: the previous live document on the
 * job is voided, the new one is written at the next revision pointing back at
 * it, and its option is attached. Doing this as separate inserts left a sent —
 * and therefore frozen — proposal with no option behind it if the second one
 * failed, which is a customer link that loads a document and then refuses the
 * signature forever. See 20260809210000.
 */
/** A product offered as a proposal upgrade, with everything needed to price it. */
export interface UpgradeProduct {
  id: string;
  name: string;
  description: string | null;
  default_price: NumericFromDb;
  measurement: string | null;
  coverage: NumericFromDb;
  waste: NumericFromDb;
  unit: string | null;
  proposal_upgrade_order: number;
}

/**
 * The upgrades, in the order the customer reads them.
 *
 * `proposal_upgrade_order` is null for everything that is not one, so this is
 * the whole list and the ordering in a single column — see the 20260815130000
 * migration for why those are the same question.
 */
export async function fetchUpgradeProducts(): Promise<UpgradeProduct[]> {
  return unwrap<UpgradeProduct[]>(
    await supabase
      .from("products")
      .select(
        "id, name, description, default_price, measurement, coverage, waste, unit, proposal_upgrade_order",
      )
      .not("proposal_upgrade_order", "is", null)
      .eq("active", true)
      .order("proposal_upgrade_order"),
  );
}

/**
 * The protective clauses, as the customer's document needs them (ADR 0100).
 *
 * `when_to_use` is left behind on purpose: it is the rule the `scope-draft`
 * prompt reads when choosing a clause, and nothing on the customer's side has
 * any business with it. Inactive rows are left behind too — switching one off
 * in Tools takes it off every proposal that has not been sent, which is what
 * an active flag is for.
 *
 * Ordered the way Tools orders them, because that is the order they print in.
 */
export async function fetchScopeQualifiers(): Promise<ScopeQualifier[]> {
  return unwrap<ScopeQualifier[]>(
    await supabase
      .from("scope_qualifiers")
      .select("id, body")
      .eq("active", true)
      .order("sort_order")
      .order("name"),
  );
}

/**
 * The walkthroughs, and which product each one belongs to.
 *
 * Three rows at the time of writing, so the whole set is fetched and matched in
 * memory rather than filtered by the product ids on one estimate: it is one
 * request either way, and this one is cacheable and does not grow a URL with
 * every line item.
 *
 * The path becomes a public URL here, against whichever Supabase this build
 * talks to — the column holds a path precisely so the test project does not
 * quote production's. Public, and so not signed: unlike a customer's own roof
 * photographs there is nothing in these to protect, and a link that expires
 * would break a proposal opened next week. See the 20260905110000 migration.
 */
export interface ProductVideoRow {
  productId: string;
  url: string;
  posterUrl: string | null;
  title: string;
  caption: string | null;
}

export async function fetchProductVideos(): Promise<ProductVideoRow[]> {
  const rows = unwrap<
    {
      id: string;
      name: string;
      video_path: string;
      video_poster_path: string | null;
      video_title: string | null;
      video_caption: string | null;
    }[]
  >(
    await supabase
      .from("products")
      .select(
        "id, name, video_path, video_poster_path, video_title, video_caption",
      )
      .not("video_path", "is", null)
      .eq("active", true),
  );

  const publicUrl = (path: string) =>
    supabase.storage.from("product-videos").getPublicUrl(path).data.publicUrl;

  return rows.map((row) => ({
    productId: row.id,
    url: publicUrl(row.video_path),
    posterUrl: row.video_poster_path ? publicUrl(row.video_poster_path) : null,
    // The catalogue name is a warehouse label — "Pipeboot Replacement Repair".
    // A heading is written for the customer, and falls back to the name only
    // so a video somebody adds without one still appears.
    title: row.video_title?.trim() || row.name,
    caption: row.video_caption?.trim() || null,
  }));
}

/**
 * One product's walkthrough, as the Tools screen manages it.
 *
 * Every product is listed by the tool, not only the ones with a video, so this
 * is the shape of a row that may or may not have one yet.
 */
export interface ProductVideoAdmin {
  id: string;
  name: string;
  category: string | null;
  video_path: string | null;
  video_poster_path: string | null;
  video_title: string | null;
  video_caption: string | null;
}

/** Every product carrying a walkthrough, newest wording first for the tool. */
export async function fetchProductVideoAdmin(): Promise<ProductVideoAdmin[]> {
  return unwrap<ProductVideoAdmin[]>(
    await supabase
      .from("products")
      .select(
        "id, name, category, video_path, video_poster_path, video_title, video_caption",
      )
      .not("video_path", "is", null)
      .order("name"),
  );
}

/** The public URL of an object in the walkthrough bucket. */
export function productVideoUrl(path: string): string {
  return supabase.storage.from("product-videos").getPublicUrl(path).data
    .publicUrl;
}

/**
 * Put a file in the walkthrough bucket, at a path nothing else has used.
 *
 * Never `upsert`. A proposal already sent carries the public URL of the video
 * and still that were live when it was frozen (ADR 0032), so an object, once
 * written, is never written again: overwriting it would change what a customer
 * watches under a document that is supposed to be a record. Each upload gets
 * its own path from `productVideoPath`, and a collision is an error rather
 * than a silent replacement.
 */
export async function uploadProductVideoFile(
  path: string,
  file: Blob,
  contentType: string,
): Promise<string> {
  const { error } = await supabase.storage
    .from("product-videos")
    .upload(path, file, { contentType, upsert: false });
  if (error) throw new Error(error.message);
  return path;
}

/**
 * Take a product's walkthrough away — from proposals not yet built.
 *
 * Clears the product's columns and deletes nothing. Every proposal already
 * sent holds the public URL of that video and still, and a frozen proposal is
 * a record (ADR 0032): deleting the object would leave a broken player on a
 * document the customer may open next month. The files cost pennies to keep.
 */
export async function clearProductVideo(product: {
  id: string;
}): Promise<void> {
  await updateRow("products", product.id, {
    video_path: null,
    video_poster_path: null,
    video_title: null,
    video_caption: null,
  });
}

/**
 * Present one estimate, or a packet of them, as a locked customer document.
 *
 * Always `present_packet`, even for one: it is the single implementation of
 * voiding the live document, taking the next revision and attaching the
 * options, and routing the ordinary case through the same code is what stops
 * the two paths drifting. `present_estimate` still exists as a wrapper for
 * anything holding an older bundle — see the 20260815105000 migration.
 *
 * `estimateIds` is in the order that decides which bought option is the main
 * one (version order, `mainEstimateOrder`), not the order the customer reads
 * them in, which the snapshot holds (ADR 0294).
 */
export async function presentEstimate(input: {
  jobId: string;
  estimateIds: readonly string[];
  snapshot: unknown;
  contractVersion: string;
  title: string;
  delivery: string;
  recipientName: string | null;
  recipientEmail: string | null;
  recipientPhone: string | null;
}): Promise<{ id: string; token: string; revision: number }> {
  const { data, error } = await supabase.rpc("present_packet", {
    p_job_id: input.jobId,
    p_estimate_ids: input.estimateIds,
    p_snapshot: input.snapshot,
    p_contract_version: input.contractVersion,
    p_title: input.title,
    p_delivery: input.delivery,
    p_recipient_name: input.recipientName,
    p_recipient_email: input.recipientEmail,
    p_recipient_phone: input.recipientPhone,
  });
  if (error) throw new Error(error.message);
  return data as { id: string; token: string; revision: number };
}

/** A `change_order_documents` row, as the estimate card reads it. */
export interface ChangeOrderDocumentRecord {
  id: string;
  job_id: string;
  estimate_id: string;
  number: number;
  token: string;
  status: string;
  sent_at: string;
  expires_at: string;
  viewed_at: string | null;
  snapshot: unknown;
}

/**
 * Every change order presented on this job, for Docs.
 *
 * By job rather than by estimate, unlike `fetchChangeOrderDocuments`: Docs is
 * the job's whole paper trail and a job that switched estimates still signed
 * what it signed. The acceptance comes with it, because who signed it and when
 * is half of what the list is read for (ADR 0205).
 */
export async function fetchJobChangeOrderDocuments(
  jobId: string,
): Promise<ChangeOrderEntry[]> {
  const rows = unwrap<
    {
      id: string;
      number: number;
      status: string;
      token: string;
      sent_at: string | null;
      expires_at: string | null;
      created_at: string;
      acceptance?:
        | {
            accepted_at: string;
            signer_name: string;
            change_total: NumericFromDb;
          }[]
        | {
            accepted_at: string;
            signer_name: string;
            change_total: NumericFromDb;
          }
        | null;
    }[]
  >(
    await supabase
      .from("change_order_documents")
      .select(
        `id,number,status,token,sent_at,expires_at,created_at,
         acceptance:change_order_acceptances(accepted_at,signer_name,change_total)`,
      )
      .eq("job_id", jobId)
      .order("created_at", { ascending: false }),
  );
  return rows.map((row) => {
    // PostgREST returns an embedded one-to-many as an array and a one-to-one
    // as an object, and which it picks depends on the constraints it can see.
    const acceptance = Array.isArray(row.acceptance)
      ? row.acceptance[0]
      : row.acceptance;
    return {
      id: row.id,
      number: row.number,
      status: row.status,
      token: row.token,
      sent_at: row.sent_at,
      expires_at: row.expires_at,
      created_at: row.created_at,
      accepted_at: acceptance?.accepted_at ?? null,
      signer_name: acceptance?.signer_name ?? null,
      // `change_total` is a decimal of DOLLARS, like every money column on the
      // change order tables and unlike every `*_cents` column beside them.
      change_total:
        acceptance?.change_total == null
          ? null
          : toCents(acceptance.change_total),
    };
  });
}

/**
 * The certificates of completion sent on this job, for Docs (ADR 0308).
 *
 * The signature comes along embedded, the way a change order's acceptance
 * does, so the list says who signed and when without a second query.
 *
 * A failed read is an empty list, not an error. Docs loads this beside the
 * proposals, files and change orders in one go, and the app deploys before
 * production is migrated: until the table exists there, throwing would take
 * every job's signed contracts off Docs with it (the 2026-08-18 outage in
 * CLAUDE.md). Missing certificates is the smaller harm.
 */
export async function fetchJobCertificates(
  jobId: string,
): Promise<CertificateEntry[]> {
  const result = await supabase
    .from("completion_certificates")
    .select(
      `id, status, token, sent_at, created_at, snapshot, copy_sent_at, copy_error,
       signature:completion_certificate_signatures(signed_at, signer_name)`,
    )
    .eq("job_id", jobId)
    .order("created_at", { ascending: false });
  if (result.error) {
    console.warn("Certificates of completion could not be read", result.error);
    return [];
  }
  const rows = unwrap<
    {
      id: string;
      status: string;
      token: string;
      sent_at: string | null;
      created_at: string;
      snapshot: { homeowner?: string | null } | null;
      copy_sent_at: string | null;
      copy_error: string | null;
      signature?:
        | { signed_at: string; signer_name: string }[]
        | { signed_at: string; signer_name: string }
        | null;
    }[]
  >(result);
  return rows.map((row) => {
    const signature = Array.isArray(row.signature)
      ? row.signature[0]
      : row.signature;
    return {
      id: row.id,
      status: row.status,
      token: row.token,
      sent_at: row.sent_at,
      created_at: row.created_at,
      homeowner: row.snapshot?.homeowner ?? null,
      signed_at: signature?.signed_at ?? null,
      signer_name: signature?.signer_name ?? null,
      copy_sent_at: row.copy_sent_at,
      copy_error: row.copy_error,
    };
  });
}

/** One certificate read back out of the freezer, for the office. */
export interface CertificateRecord {
  document: CompletionCertificate;
  signature: {
    signerName: string;
    signerEmail: string | null;
    signedDate: string | null;
    signedAt: string;
  } | null;
}

/**
 * A certificate as the customer saw it, opened by the office — the frozen
 * snapshot off the row, under the signed-in user's own row level security,
 * never the customer's link (ADR 0032).
 */
export async function fetchCertificateRecord(
  certificateId: string,
): Promise<CertificateRecord> {
  const { data, error } = await supabase
    .from("completion_certificates")
    .select(
      `snapshot,
       signature:completion_certificate_signatures(signer_name,signer_email,signed_date,signed_at)`,
    )
    .eq("id", certificateId)
    .single();
  if (error) throw new Error(error.message);
  const row = data as {
    snapshot: CompletionCertificate;
    signature?:
      | {
          signer_name: string;
          signer_email: string | null;
          signed_date: string | null;
          signed_at: string;
        }[]
      | {
          signer_name: string;
          signer_email: string | null;
          signed_date: string | null;
          signed_at: string;
        }
      | null;
  };
  const signature = Array.isArray(row.signature)
    ? row.signature[0]
    : row.signature;
  return {
    document: row.snapshot,
    signature: signature
      ? {
          signerName: signature.signer_name,
          signerEmail: signature.signer_email,
          signedDate: signature.signed_date,
          signedAt: signature.signed_at,
        }
      : null,
  };
}

/**
 * Freeze a certificate of completion and mint its link. One call, so a live
 * one is withdrawn and the new one written together (ADR 0308).
 */
export async function presentCompletionCertificate(input: {
  jobId: string;
  snapshot: CompletionCertificate;
}): Promise<{ id: string; token: string }> {
  const { data, error } = await supabase.rpc("present_completion_certificate", {
    p_job_id: input.jobId,
    p_snapshot: input.snapshot,
  });
  if (error) throw new Error(error.message);
  return data as { id: string; token: string };
}

/** Withdraw a certificate nobody has signed. A signed one is left alone. */
export async function voidCompletionCertificate(id: string): Promise<void> {
  const { error } = await supabase.rpc("void_completion_certificate", {
    p_certificate_id: id,
  });
  if (error) throw new Error(error.message);
}

/** One change order read back out of the freezer, for the office. */
export interface ChangeOrderRecord {
  document: ChangeOrderDocument;
  signature: {
    signerName: string;
    signerEmail: string | null;
    signedDate: string | null;
    acceptedAt: string;
  } | null;
  /** Signed lines deleted since (ADR 0181). Shown beside, never instead. */
  withdrawn: ChangeOrderWithdrawal[];
}

/**
 * A change order as the customer saw it, opened by the office.
 *
 * The same idea as `fetchProposalRecord`, for the same reason: the customer's
 * `/c/<token>` link is a one-way door, and opening it as staff on a live
 * document stamps a view onto the audit trail that no customer made. The
 * frozen snapshot is read straight off the row under the signed-in user's own
 * row level security and drawn by `ChangeOrderView`, the component the
 * customer used (ADR 0032).
 */
export async function fetchChangeOrderRecord(
  documentId: string,
): Promise<ChangeOrderRecord> {
  const [document, withdrawals] = await Promise.all([
    supabase
      .from("change_order_documents")
      .select(
        `snapshot,
         acceptance:change_order_acceptances(signer_name,signer_email,signed_date,accepted_at)`,
      )
      .eq("id", documentId)
      .single(),
    supabase
      .from("change_order_withdrawals")
      .select("change_order_id, withdrawn_at")
      .eq("document_id", documentId),
  ]);
  if (document.error) throw new Error(document.error.message);
  // An empty list means nothing was withdrawn. A failed read must not read as
  // that, or the office is shown a total the customer never agreed to.
  if (withdrawals.error) throw new Error(withdrawals.error.message);

  const row = document.data as {
    snapshot: ChangeOrderDocument;
    acceptance?:
      | {
          signer_name: string;
          signer_email: string | null;
          signed_date: string | null;
          accepted_at: string;
        }[]
      | {
          signer_name: string;
          signer_email: string | null;
          signed_date: string | null;
          accepted_at: string;
        }
      | null;
  };
  const acceptance = Array.isArray(row.acceptance)
    ? row.acceptance[0]
    : row.acceptance;
  return {
    document: row.snapshot,
    signature: acceptance
      ? {
          signerName: acceptance.signer_name,
          signerEmail: acceptance.signer_email,
          signedDate: acceptance.signed_date,
          acceptedAt: acceptance.accepted_at,
        }
      : null,
    withdrawn: (
      (withdrawals.data ?? []) as {
        change_order_id: string;
        withdrawn_at: string;
      }[]
    ).map((line) => ({
      lineId: String(line.change_order_id),
      withdrawnAt: String(line.withdrawn_at),
    })),
  };
}

/** The change orders this estimate has sent out, newest first. */
export async function fetchChangeOrderDocuments(
  estimateId: string,
): Promise<ChangeOrderDocumentRecord[]> {
  return unwrap<ChangeOrderDocumentRecord[]>(
    await supabase
      .from("change_order_documents")
      .select(
        "id,job_id,estimate_id,number,token,status,sent_at,expires_at,viewed_at,snapshot",
      )
      .eq("estimate_id", estimateId)
      .order("number", { ascending: false }),
  );
}

/** The paragraphs a change order carries, as Tools has them today. */
export async function fetchChangeOrderTerms(): Promise<
  { id: string; name: string; body: string }[]
> {
  return unwrap<{ id: string; name: string; body: string }[]>(
    await supabase
      .from("change_order_terms")
      .select("id,name,body")
      .eq("active", true)
      .order("sort_order"),
  );
}

/**
 * Freeze this estimate's unsigned change orders as a customer document.
 *
 * One RPC rather than three writes: withdrawing the live document, writing the
 * new one and pointing the lines at it have to happen together, or a rep who
 * loses signal halfway sends somebody a link to a page with no work on it.
 */
export async function presentChangeOrder(input: {
  jobId: string;
  estimateId: string;
  snapshot: unknown;
}): Promise<{ id: string; token: string; number: number }> {
  const { data, error } = await supabase.rpc("present_change_order", {
    p_job_id: input.jobId,
    p_estimate_id: input.estimateId,
    p_snapshot: input.snapshot,
  });
  if (error) throw new Error(error.message);
  return data as { id: string; token: string; number: number };
}

/**
 * How much of this job's contract nobody has signed for, in a sentence.
 *
 * The active estimate's change orders and no others: a rival estimate's lines
 * are not in the contract, so warning about them would be warning about money
 * nobody is being asked for. Null when there is nothing to say, which is the
 * ordinary case and renders nothing.
 */
export async function fetchUnsignedChangeOrderWarning(
  jobId: string,
): Promise<string | null> {
  const estimates = unwrap<{ id: string }[]>(
    await supabase
      .from("estimates")
      .select("id")
      .eq("job_id", jobId)
      .eq("is_active", true),
  );
  if (estimates.length === 0) return null;
  const rows = unwrap<
    { id: string; amount: unknown; signed_at: string | null }[]
  >(
    await supabase
      .from("change_orders")
      .select("id,amount,signed_at,sort_order")
      .in(
        "estimate_id",
        estimates.map((e) => e.id),
      ),
  );
  return unsignedChangeOrderWarning(rows);
}

/**
 * Estimates on these jobs with work going ahead and no signature (issue #463):
 * a placed material order, a crew booked or further, or an invoice. The job
 * page shows them and the to-do list asks for the signature.
 *
 * Asks only for rows that could count — placed orders, booked crews, invoices
 * that are not void — so a job's many draft orders never come back.
 */
export async function fetchUnsignedWork(
  jobIds: readonly string[],
): Promise<UnsignedWork[]> {
  if (jobIds.length === 0) return [];
  const [jobs, estimates, materialOrders, workOrders, invoices] =
    await Promise.all([
      supabase
        .from("jobs")
        .select("id,signed_contract_at,signed_contract_path")
        .in("id", jobIds),
      // Signed ones too: a job with any estimate signed in the app is judged
      // estimate by estimate, and one with none may have been signed on paper.
      supabase
        .from("estimates")
        .select("id,job_id,name,status")
        .in("job_id", jobIds),
      supabase
        .from("material_orders")
        .select("estimate_id,status")
        .in("job_id", jobIds)
        .not("status", "eq", "not_placed"),
      supabase
        .from("work_orders")
        .select("estimate_id,status")
        .in("job_id", jobIds)
        .in("status", ["scheduled", "completed", "paid"]),
      supabase
        .from("invoices")
        .select("status,estimate_id,invoice_estimates(estimate_id)")
        .in("job_id", jobIds)
        .not("status", "eq", "void"),
    ]);
  return unsignedWork({
    jobs: unwrap<
      {
        id: string;
        signed_contract_at: string | null;
        signed_contract_path: string | null;
      }[]
    >(jobs),
    estimates:
      unwrap<
        { id: string; job_id: string; name: string | null; status: string }[]
      >(estimates),
    materialOrders:
      unwrap<{ estimate_id: string | null; status: string }[]>(materialOrders),
    workOrders:
      unwrap<{ estimate_id: string | null; status: string }[]>(workOrders),
    invoices: unwrap<
      {
        status: string;
        estimate_id: string | null;
        invoice_estimates: { estimate_id: string }[] | null;
      }[]
    >(invoices).map((invoice) => ({
      status: invoice.status,
      estimate_ids: [
        ...(invoice.estimate_id ? [invoice.estimate_id] : []),
        ...(invoice.invoice_estimates ?? []).map((row) => row.estimate_id),
      ],
    })),
  });
}

/** Every column the terms warning reads (ADR 0226). */
const TERMS_CHECK_COLUMNS =
  "job_id,fingerprint,probability,quote,quote_kind,quote_at,dismissed_fingerprint," +
  "about,dismissed_about";

/**
 * What Jev found comparing this job's conversation with its payment schedule
 * (ADR 0226), or null when it has not been checked. `paymentTermsWarning`
 * decides whether it says anything.
 */
export async function fetchPaymentTermsCheck(
  jobId: string,
): Promise<PaymentTermsCheckRow | null> {
  return unwrap<PaymentTermsCheckRow | null>(
    await supabase
      .from("payment_terms_checks")
      .select(TERMS_CHECK_COLUMNS)
      .eq("job_id", jobId)
      .maybeSingle(),
  );
}

/** "This is fine": hides the warning until what Jev was shown changes. */
export async function dismissPaymentTermsCheck(jobId: string): Promise<void> {
  const { error } = await supabase.rpc("dismiss_payment_terms_check", {
    p_job_id: jobId,
  });
  if (error) throw new Error(error.message);
}

/** Withdraw a change order the customer has not signed. */
export async function voidChangeOrderDocument(documentId: string) {
  const { error } = await supabase.rpc("void_change_order_document", {
    p_document_id: documentId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Insurance budgeted by hand for a sub with no work order.
 *
 * Only the labour and the crew are stored; the liability and comp on them are
 * worked out on read by `computeAutoExpenses`, at that crew's own rates. See
 * migration 20260915100000.
 */
export interface EstimateInsuranceLine {
  id: string;
  estimate_id: string;
  crew_id: string;
  /** Dollars, like every other money column on a row. */
  labor_amount: number | null;
  trade: string | null;
  sort_order: number | null;
  crew?:
    | (Pick<
        Crew,
        | "name"
        | "contact_name"
        | "has_gl"
        | "gl_expires_on"
        | "has_wc"
        | "wc_expires_on"
        | "is_in_house"
      > | null)
    | null;
}

/**
 * The line names of several estimates at once, for labelling the options.
 *
 * Three screens list every option on a job — the builder's tabs, the Present
 * tick-list and the copy-from picker — and since 2026-09-15 an option is
 * called after its first line rather than "Estimate 2"
 * (`estimateOptionLabel`). None of them fetches another estimate's items for
 * anything else, and none of them needs the whole row: five columns, because
 * `estimateOptionLabel` skips a line that is not being done, one the customer
 * cannot see, and one in a section that never prints (`printsInPackage`).
 */
export interface EstimateLineName {
  estimate_id: string;
  description: string | null;
  section: string | null;
  work_doing: boolean | null;
  customer_visible: boolean | null;
}

export async function fetchEstimateLineNames(
  estimateIds: readonly string[],
): Promise<Map<string, EstimateLineName[]>> {
  const out = new Map<string, EstimateLineName[]>();
  if (estimateIds.length === 0) return out;
  const rows = unwrap<EstimateLineName[]>(
    await supabase
      .from("estimate_items")
      .select("estimate_id,description,section,work_doing,customer_visible")
      .in("estimate_id", estimateIds as string[])
      .order("sort_order"),
  );
  for (const row of rows) {
    const list = out.get(row.estimate_id);
    if (list) list.push(row);
    else out.set(row.estimate_id, [row]);
  }
  return out;
}

/** Everything the estimate card shows for one job. */
export interface EstimateBundle {
  estimates: Estimate[];
  items: EstimateItem[];
  payments: EstimatePayment[];
  changeOrders: ChangeOrder[];
  workOrders: WorkOrder[];
  materialOrders: MaterialOrder[];
  expenses: JobExpense[];
  returns: MaterialReturn[];
  insuranceLines: EstimateInsuranceLine[];
}

export async function fetchEstimates(jobId: string): Promise<Estimate[]> {
  return unwrap<Estimate[]>(
    await supabase
      .from("estimates")
      .select("*")
      .eq("job_id", jobId)
      .order("version"),
  );
}

/**
 * Everything hanging off one estimate.
 *
 * All of it by `estimate_id` — an estimate's lines, its work order, its
 * material order, its other expenses and its returns are its own (ADR 0030).
 * There is nothing left to fetch by job, which is why this no longer needs to
 * be told which job it is.
 */
export async function fetchEstimateChildren(
  estimateId: string,
): Promise<Omit<EstimateBundle, "estimates">> {
  const [
    items,
    payments,
    changeOrders,
    workOrders,
    materialOrders,
    expenses,
    returns,
    insuranceLines,
  ] = await Promise.all([
    supabase
      .from("estimate_items")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("sort_order"),
    supabase
      .from("estimate_payments")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("slot"),
    supabase
      .from("change_orders")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("sort_order"),
    // This estimate's own work and material, and nobody else's. Pricing the
    // same roof three ways used to show all three sets of orders on all three
    // estimates and treble the cost of each.
    supabase
      .from("work_orders")
      .select(
        // The crew's insurance columns as well as its name: the ladder
        // rates liability and comp off what each certificate says.
        "*, crew:crews(name,contact_name,has_gl,gl_expires_on,has_wc,wc_expires_on,is_in_house), items:work_order_items(*)",
      )
      .eq("estimate_id", estimateId)
      .order("created_at"),
    supabase
      .from("material_orders")
      .select("*, supplier:suppliers(name), items:material_order_items(*)")
      .eq("estimate_id", estimateId)
      .order("created_at"),
    // Other Expenses is the estimate's fourth bucket, alongside its lines, its
    // work order and its material order. Jonathon, 2026-08-04: general
    // liability, permit, warranty and the lift live here; the dump trailer is a
    // line on the work order and delivery is a line on the material order. The
    // first four are calculated rather than typed and are not in this table at
    // all — computeAutoExpenses derives them from this estimate's own template,
    // warranty and revenue.
    supabase
      .from("job_expenses")
      .select("*")
      .eq("estimate_id", estimateId)
      .order("sort_order"),
    supabase
      .from("material_returns")
      .select("*, supplier:suppliers(name)")
      .eq("estimate_id", estimateId)
      .order("sort_order"),
    // The subs somebody budgeted insurance for without raising a work order.
    // The crew's certificate columns come with it — the rate is read off them
    // rather than being a percentage anybody could type.
    supabase
      .from("estimate_insurance_lines")
      .select(
        "id,estimate_id,crew_id,labor_amount,trade,sort_order, crew:crews(name,contact_name,has_gl,gl_expires_on,has_wc,wc_expires_on,is_in_house)",
      )
      .eq("estimate_id", estimateId)
      .order("created_at"),
  ]);

  return {
    items: unwrap<EstimateItem[]>(items),
    payments: unwrap<EstimatePayment[]>(payments),
    changeOrders: unwrap<ChangeOrder[]>(changeOrders),
    workOrders: unwrap<WorkOrder[]>(workOrders),
    materialOrders: unwrap<MaterialOrder[]>(materialOrders),
    expenses: unwrap<JobExpense[]>(expenses),
    returns: unwrap<MaterialReturn[]>(returns),
    insuranceLines: unwrap<EstimateInsuranceLine[]>(insuranceLines),
  };
}

/** A row on the Activity tab: checklist completions, stage changes and notes. */
export interface ActivityRow {
  when: string;
  who: string | null;
  text: string;
  kind: "done" | "stage" | "note";
  jobNumber?: number | undefined;
}

export async function fetchJobActivity(jobId: string): Promise<ActivityRow[]> {
  const [checklist, stages, activities] = await Promise.all([
    supabase
      .from("job_checklist_items")
      .select(
        "label,completed_at, who:profiles!job_checklist_items_completed_by_fkey(full_name)",
      )
      .eq("job_id", jobId)
      .not("completed_at", "is", null),
    supabase
      .from("job_stage_events")
      .select(
        "stage,entered_at, who:profiles!job_stage_events_entered_by_fkey(full_name)",
      )
      .eq("job_id", jobId),
    supabase
      .from("activities")
      .select(
        "type,body,occurred_at, who:profiles!activities_created_by_fkey(full_name)",
      )
      .eq("job_id", jobId),
  ]);

  const rows: ActivityRow[] = [];
  type WhoRow = { who?: { full_name: string | null } | null };

  for (const r of unwrap<(WhoRow & { label: string; completed_at: string })[]>(
    checklist,
  )) {
    rows.push({
      when: r.completed_at,
      who: r.who?.full_name ?? null,
      text: `Completed "${r.label}"`,
      kind: "done",
    });
  }
  for (const r of unwrap<(WhoRow & { stage: string; entered_at: string })[]>(
    stages,
  )) {
    rows.push({
      when: r.entered_at,
      who: r.who?.full_name ?? null,
      text: `Status → ${r.stage}`,
      kind: "stage",
    });
  }
  for (const r of unwrap<
    (WhoRow & { type: string; body: string; occurred_at: string })[]
  >(activities)) {
    rows.push({
      when: r.occurred_at,
      who: r.who?.full_name ?? null,
      text: `${r.type === "note" ? "Note" : r.type}: ${r.body}`,
      kind: "note",
    });
  }

  return rows.sort(
    (a, b) => new Date(b.when).getTime() - new Date(a.when).getTime(),
  );
}

export async function fetchContactActivity(
  contactId: string,
  jobs: readonly Pick<Job, "id" | "job_number">[],
): Promise<ActivityRow[]> {
  const ids = jobs.map((j) => j.id);
  const numberFor = new Map(jobs.map((j) => [j.id, j.job_number]));

  const [activities, stages] = await Promise.all([
    supabase
      .from("activities")
      .select(
        "type,body,occurred_at,job_id, who:profiles!activities_created_by_fkey(full_name)",
      )
      .eq("contact_id", contactId),
    ids.length
      ? supabase
          .from("job_stage_events")
          .select(
            "stage,entered_at,job_id, who:profiles!job_stage_events_entered_by_fkey(full_name)",
          )
          .in("job_id", ids)
      : Promise.resolve({ data: [], error: null }),
  ]);

  const rows: ActivityRow[] = [];
  type WhoRow = { who?: { full_name: string | null } | null; job_id: string };

  for (const r of unwrap<
    (WhoRow & { type: string; body: string; occurred_at: string })[]
  >(activities)) {
    rows.push({
      when: r.occurred_at,
      who: r.who?.full_name ?? null,
      text: `${r.type === "note" ? "Note" : r.type}: ${r.body}`,
      kind: "note",
      jobNumber: numberFor.get(r.job_id),
    });
  }
  for (const r of unwrap<(WhoRow & { stage: string; entered_at: string })[]>(
    stages,
  )) {
    rows.push({
      when: r.entered_at,
      who: r.who?.full_name ?? null,
      text: `Status → ${r.stage}`,
      kind: "stage",
      jobNumber: numberFor.get(r.job_id),
    });
  }

  return rows.sort(
    (a, b) => new Date(b.when).getTime() - new Date(a.when).getTime(),
  );
}

/** One note or one reply, with who wrote it and who it tags. */
export interface NoteRow {
  id: string;
  type: string;
  direction: string | null;
  body: string;
  occurred_at: string;
  /** The note this one answers. Null on a note that starts a thread. */
  parent_id: string | null;
  /** `profiles.id` of everybody tagged in the body. */
  mentions: string[] | null;
  created_by: string | null;
  author?: { full_name: string | null } | null;
  /** What was photographed, in the order it was picked. */
  photos?: NotePhoto[] | null;
}

/**
 * A photograph hung off a note.
 *
 * Jonathon, 2026-09-01: "we want to be able to attach photos to our notes."
 * Rotten decking under a tear-off, the neighbour's fence the crew has to work
 * around, the shingle that came in the wrong colour — half of what gets said
 * about a roof is only sayable with a picture, and until now that picture went
 * to somebody's personal phone and the job never had it.
 *
 * Deliberately not a `job_photos` row: those are the proposal, offered to the
 * customer. See the migration for the whole argument.
 */
export interface NotePhoto {
  id: string;
  activity_id: string;
  path: string;
  filename: string | null;
  mime_type: string | null;
  sort_order: number;
  created_at: string;
}

export async function fetchNotes(jobId: string): Promise<NoteRow[]> {
  return unwrap<NoteRow[]>(
    await supabase
      .from("activities")
      .select(
        "*, author:profiles!activities_created_by_fkey(full_name), " +
          "photos:activity_photos(*)",
      )
      .eq("job_id", jobId)
      .order("occurred_at", { ascending: false }),
  );
}

/**
 * Store one photograph against a note.
 *
 * The file goes to storage first and the row second, the shape every upload in
 * this file uses: a row pointing at an object that failed to upload is worse
 * than no row at all.
 */
export async function uploadNotePhoto(
  activityId: string,
  jobId: string,
  file: File,
  sortOrder: number,
  profileId: string | null,
): Promise<NotePhoto> {
  const path = await uploadJobFile("note-photos", jobId, file);
  return insertRow<NotePhoto>("activity_photos", {
    activity_id: activityId,
    path,
    filename: file.name,
    mime_type: file.type || null,
    size_bytes: file.size,
    sort_order: sortOrder,
    uploaded_by: profileId,
  });
}

/** Links good for an hour, one request for the lot. */
export async function signNotePhotos(
  paths: readonly string[],
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("note-photos")
    .createSignedUrls(paths as string[], 60 * 60);
  if (error) throw new Error(error.message);
  const pairs: [string, string][] = [];
  for (const row of data ?? []) {
    // One that has been deleted since is skipped rather than left as a broken
    // image. Supabase reports the failure per row, not for the whole request.
    if (row.path && row.signedUrl) pairs.push([row.path, row.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

/** Update one column on one row. The app has no save buttons. */
export async function updateRow(
  table: string,
  id: string,
  patch: Record<string, unknown>,
): Promise<void> {
  const { error } = await supabase.from(table).update(patch).eq("id", id);
  if (error) throw new Error(error.message);

  // Finishing the work fixes the invoice date. A QuickBooks invoice is dated
  // the day the work was completed (ADR 0078), but it was written the day the
  // job sold, when that date did not exist yet. Marking a work order complete
  // is the moment it becomes knowable, so QuickBooks is told now.
  //
  // Writing one of the columns that queues a cost sends it for the same
  // reason: approving a crew payout is the moment the debt is real (ADR 0088),
  // and a cost that waits for somebody to remember a button is a cost the
  // books never hear about — nineteen of them, by the time Jonathon asked on
  // 2026-09-20 where his crew's bill had got to. See ADR 0173.
  //
  // Deliberately not awaited into the caller's failure: QuickBooks having a
  // bad afternoon must never make approving a payout look like it failed. The
  // reason lands on the row and shows in Tools → Integrations, the same way a
  // failed push does.
  if (tellsQuickbooks(table, patch)) {
    void sendQuickbooksCosts().catch(() => {});
  }

  // The neighbours. A finished roof and a recorded contract each queue a letter
  // for Kelsey (ADR 0149); this is what sends it. A customer who e-signs is
  // already covered — `presented-document` pushes the moment they sign, because
  // nobody is in the office at that point — so what this catches is the roof
  // being ticked off, a contract recorded by hand from paper, and anything an
  // earlier push could not send.
  //
  // Same `void` and same reason as above: a letter to Kelsey must never make
  // ticking off a work order look like it failed. The reason lands on the row.
  const neighbourWorthy =
    (table === "work_orders" &&
      (("completed_at" in patch && patch.completed_at) ||
        ("status" in patch &&
          (patch.status === "completed" || patch.status === "paid")))) ||
    (table === "jobs" &&
      "signed_contract_at" in patch &&
      patch.signed_contract_at);
  if (neighbourWorthy) {
    void supabase.functions
      .invoke("neighbour-letters", { body: { action: "push" } })
      .catch(() => {});
  }
}

/**
 * The columns whose being written means QuickBooks has something to hear.
 *
 * Each one is the moment a trigger queues a cost — `payout_approved_at` and
 * `payout_paid_manually_at` for a crew payout, arrival for a material order,
 * the amount for a typed expense — plus `completed_at`, which re-dates the
 * invoice rather than queueing anything. Writing a null is not one of these:
 * un-approving takes a cost back out of the queue, and there is nothing to
 * send.
 *
 * `payout_paid_at` is deliberately absent. Ticking an approved payout as paid
 * queues nothing — approving it already did — and a whole run ticked off at
 * once would otherwise be one push per crew.
 */
const TELLS_QUICKBOOKS: Record<string, readonly string[]> = {
  work_orders: [
    "completed_at",
    "paid_at",
    "payout_approved_at",
    "payout_paid_manually_at",
  ],
  material_orders: [
    "picked_up_at",
    "delivery_date",
    "tax_rate",
    "tax_amount",
    "reconcile_amt",
  ],
  material_order_items: ["qty", "unit_price"],
  job_expenses: ["amount"],
};

function tellsQuickbooks(
  table: string,
  patch: Record<string, unknown>,
): boolean {
  if (table === "material_order_items") return true;
  if (
    table === "material_orders" &&
    ("tax_rate" in patch || "tax_amount" in patch || "reconcile_amt" in patch)
  )
    return true;
  if (table === "job_expenses" && "amount" in patch) return true;
  return (TELLS_QUICKBOOKS[table] ?? []).some(
    (column) => column in patch && Boolean(patch[column]),
  );
}

export async function insertRow<T>(
  table: string,
  row: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase
    .from(table)
    .insert(row)
    .select()
    .single();
  if (error) throw new Error(error.message);
  contractMayHaveChanged(table, data as Record<string, unknown>);
  // A typed expense is a cost the moment it is written down — there is no
  // later event to wait for — so it goes the same way an approved payout does.
  if (tellsQuickbooks(table, row)) {
    void sendQuickbooksCosts().catch(() => {});
  }
  return data as T;
}

export async function deleteRow(table: string, id: string): Promise<void> {
  // Read before deleting: afterwards there is no row to say which estimate it
  // belonged to, and a deleted line changes the contract total as surely as an
  // edited one.
  const doomed = CONTRACT_TABLES.has(table)
    ? unwrap<Record<string, unknown>[]>(
        await supabase.from(table).select("*").eq("id", id),
      )[0]
    : null;
  if (table === "change_orders" && doomed?.signed_at && doomed.document_id) {
    const { data, error: stopError } = await supabase.functions.invoke(
      "mercury",
      {
        body: { action: "void-change-order", documentId: doomed.document_id },
      },
    );
    if (stopError || data?.error || data?.stopped !== true) {
      throw new Error(
        String(
          data?.error ??
            "Stop the change order payment link before withdrawing its signed work.",
        ),
      );
    }
  }
  const { error } = await supabase.from(table).delete().eq("id", id);
  if (error) throw new Error(error.message);
  if (doomed) contractMayHaveChanged(table, doomed);
  if (table === "material_order_items") {
    void sendQuickbooksCosts().catch(() => {});
  }
}

/** Every roof layout measured on a job, oldest first. */
export async function fetchRoofLayouts(jobId: string): Promise<RoofLayout[]> {
  return unwrap<RoofLayout[]>(
    await supabase
      .from("job_measurements")
      .select("*")
      .eq("job_id", jobId)
      .order("captured_at"),
  );
}

/**
 * Every wall measurement on a job, oldest first — the siding's numbers, kept
 * apart from the roof's (packages/core/src/siding.ts).
 */
export async function fetchWallMeasurements(
  jobId: string,
): Promise<WallMeasurement[]> {
  return unwrap<WallMeasurement[]>(
    await supabase
      .from("job_wall_measurements")
      .select("*")
      .eq("job_id", jobId)
      .order("captured_at"),
  );
}

/**
 * Store a measurement report against its job.
 *
 * The bucket is private; the path is only ever resolved through a signed URL.
 * Prefixing with the job id keeps one job's reports together and makes the
 * object name unique without inventing one.
 */
export async function uploadRoofReport(
  jobId: string,
  file: File,
): Promise<string> {
  return uploadJobFile("roof-reports", jobId, file);
}

/**
 * A measurement report's bytes, base64, for hanging off an email.
 *
 * Downloaded rather than linked: a supplier's counter opens an attachment and
 * does not sign in to anything, and a signed URL good for an hour is no use in
 * an inbox read tomorrow.
 */
export async function roofReportBase64(path: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from("roof-reports")
    .download(path);
  if (error) throw new Error(error.message);
  if (!data) throw new Error("That measurement report could not be read.");
  const bytes = new Uint8Array(await data.arrayBuffer());
  // In chunks: `String.fromCharCode(...bytes)` on a multi-megabyte PDF blows
  // the argument limit and throws a RangeError nobody could read.
  let binary = "";
  const CHUNK = 0x8000;
  for (let at = 0; at < bytes.length; at += CHUNK) {
    binary += String.fromCharCode(...bytes.subarray(at, at + CHUNK));
  }
  return btoa(binary);
}

/** The signed contract, in its own private bucket. */
export async function uploadSignedContract(
  jobId: string,
  file: File,
): Promise<string> {
  return uploadJobFile("signed-contracts", jobId, file);
}

export async function uploadJobFile(
  bucket: string,
  jobId: string,
  file: File,
): Promise<string> {
  const path = `${jobId}/${Date.now()}-${file.name.replace(/[^\w.-]+/g, "_")}`;
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    contentType: file.type || "application/octet-stream",
  });
  if (error) throw new Error(error.message);
  return path;
}

/**
 * Every document this job has ever sent the customer.
 *
 * One row per presented document, with where it got to and — if it was signed —
 * who signed it and when. Voided and expired ones are included on purpose: the
 * question this screen answers is "what did we send them?", and the answer has
 * to include the one that was withdrawn.
 */
export interface JobDocument {
  id: string;
  title: string | null;
  status: string;
  token: string;
  revision: number;
  sent_at: string | null;
  /** When the customer's link stops opening. Null until it is sent. */
  expires_at: string | null;
  created_at: string;
  accepted_at: string | null;
  signer_name: string | null;
  /** Whether the customer has their copy of the signed agreement (ADR 0122). */
  signed_copy_sent_at: string | null;
  signed_copy_error: string | null;
}

export async function fetchJobDocuments(jobId: string): Promise<JobDocument[]> {
  const rows = unwrap<
    {
      id: string;
      title: string | null;
      status: string;
      token: string;
      revision: number | null;
      sent_at: string | null;
      expires_at: string | null;
      created_at: string;
      signed_copy_sent_at: string | null;
      signed_copy_error: string | null;
      acceptance?:
        | { accepted_at: string; signer_name: string }[]
        | { accepted_at: string; signer_name: string }
        | null;
    }[]
  >(
    await supabase
      .from("proposals")
      .select(
        `id,title,status,token,revision,sent_at,expires_at,created_at,
         signed_copy_sent_at,signed_copy_error,
         acceptance:proposal_acceptances(accepted_at,signer_name)`,
      )
      .eq("job_id", jobId)
      .order("created_at", { ascending: false }),
  );

  return rows.map((row) => {
    // PostgREST returns an embedded one-to-many as an array and a one-to-one as
    // an object, and which of the two it decides on depends on the constraints
    // it can see. Handled either way rather than guessed at.
    const acceptance = Array.isArray(row.acceptance)
      ? row.acceptance[0]
      : row.acceptance;
    return {
      id: row.id,
      title: row.title,
      status: row.status,
      token: row.token,
      revision: row.revision ?? 1,
      sent_at: row.sent_at,
      expires_at: row.expires_at,
      created_at: row.created_at,
      accepted_at: acceptance?.accepted_at ?? null,
      signer_name: acceptance?.signer_name ?? null,
      signed_copy_sent_at: row.signed_copy_sent_at,
      signed_copy_error: row.signed_copy_error,
    };
  });
}

/**
 * One document, read back out of the freezer.
 *
 * The office asking "what did they sign?" cannot use the customer's link: the
 * `presented-document` endpoint refuses an accepted one on purpose, and on a
 * live one it would stamp a view onto the audit trail that no customer made.
 * So the snapshot is read straight off the row, under the signed-in user's own
 * row level security, and rendered by the same component the customer saw.
 *
 * The acceptance comes with it, because the choice the customer made is the
 * half of the record the snapshot cannot hold — it was made minutes after the
 * document was frozen.
 */
export interface ProposalRecord {
  id: string;
  title: string | null;
  status: string;
  revision: number;
  token: string;
  sentAt: string | null;
  companySignedAt: string | null;
  document: PresentedDocument;
  signature: DocumentSignature | null;
  /** The initial boxes the customer ticked, keyed as the document keyed them. */
  initials: Record<string, unknown>;
}

export async function fetchProposalRecord(
  proposalId: string,
): Promise<ProposalRecord> {
  const rows = unwrap<
    {
      id: string;
      title: string | null;
      status: string;
      revision: number | null;
      token: string;
      sent_at: string | null;
      company_signed_at: string | null;
      snapshot: PresentedDocument | null;
      acceptance?: AcceptanceRow[] | AcceptanceRow | null;
    }[]
  >(
    await supabase
      .from("proposals")
      .select(
        `id,title,status,revision,token,sent_at,company_signed_at,snapshot,
         acceptance:proposal_acceptances(
           signer_name,signer_email,signer_phone,signed_date,accepted_at,
           estimate_id,extra_estimate_ids,upgrade_groups,subtotal,sales_tax,
           total,ip,user_agent,customer_note,initials)`,
      )
      .eq("id", proposalId)
      .limit(1),
  );

  const row = rows[0];
  if (!row) throw new Error("That document could not be found.");
  if (!row.snapshot) {
    // A packet, or a draft that was never sent — there is nothing frozen to
    // show, and re-rendering the estimate as it stands today would be a
    // different document wearing this one's name.
    throw new Error(
      "This document was never sent, so there is no frozen copy to read.",
    );
  }

  const acceptance = Array.isArray(row.acceptance)
    ? row.acceptance[0]
    : row.acceptance;

  return {
    id: row.id,
    title: row.title,
    status: row.status,
    revision: row.revision ?? 1,
    token: row.token,
    sentAt: row.sent_at,
    companySignedAt: row.company_signed_at,
    document: row.snapshot,
    signature: acceptance
      ? {
          signerName: acceptance.signer_name,
          signerEmail: acceptance.signer_email ?? null,
          signerPhone: acceptance.signer_phone ?? null,
          signedDate: acceptance.signed_date ?? null,
          acceptedAt: acceptance.accepted_at,
          estimateId: acceptance.estimate_id,
          // Everything they bought, the main estimate first, so the record
          // shows the whole contract rather than its first line (ADR 0123).
          estimateIds: [
            acceptance.estimate_id,
            ...(acceptance.extra_estimate_ids ?? []),
          ],
          upgradeKeys: acceptance.upgrade_groups ?? [],
          subtotal: Number(acceptance.subtotal ?? 0),
          salesTax: Number(acceptance.sales_tax ?? 0),
          total: Number(acceptance.total ?? 0),
          ip: acceptance.ip ?? null,
          userAgent: acceptance.user_agent ?? null,
          customerNote: acceptance.customer_note ?? null,
        }
      : null,
    initials:
      acceptance?.initials && typeof acceptance.initials === "object"
        ? acceptance.initials
        : {},
  };
}

interface AcceptanceRow {
  signer_name: string;
  signer_email: string | null;
  signer_phone: string | null;
  signed_date: string | null;
  accepted_at: string;
  estimate_id: string;
  /** The others, where the customer bought more than one (ADR 0123). */
  extra_estimate_ids: string[] | null;
  upgrade_groups: string[] | null;
  subtotal: number | string | null;
  sales_tax: number | string | null;
  total: number | string | null;
  ip: string | null;
  user_agent: string | null;
  customer_note: string | null;
  initials: Record<string, unknown> | null;
}

/**
 * The documents one estimate has been offered on.
 *
 * Only what bears on clearing it away: whether a customer can still open the
 * link, and whether it was ever signed. `signed` is asked of the acceptance
 * rather than of the status, because voiding a signed proposal moves the status
 * and leaves the acceptance standing — and the acceptance is the row whose
 * ON DELETE RESTRICT actually refuses the delete.
 */
export interface EstimateDocumentRow {
  id: string;
  title: string | null;
  status: string;
  sent_at: string | null;
  signed: boolean;
}

export async function fetchEstimateDocumentsOffered(
  estimateId: string,
): Promise<EstimateDocumentRow[]> {
  const rows = unwrap<
    {
      proposal: {
        id: string;
        title: string | null;
        status: string;
        sent_at: string | null;
        acceptance?: { accepted_at: string }[] | { accepted_at: string } | null;
      } | null;
    }[]
  >(
    await supabase
      .from("proposal_options")
      .select(
        `proposal:proposals(id,title,status,sent_at,
           acceptance:proposal_acceptances(accepted_at))`,
      )
      .eq("estimate_id", estimateId),
  );

  return rows
    .map((row) => row.proposal)
    .filter((proposal): proposal is NonNullable<typeof proposal> =>
      Boolean(proposal),
    )
    .map((proposal) => {
      const acceptance = Array.isArray(proposal.acceptance)
        ? proposal.acceptance[0]
        : proposal.acceptance;
      return {
        id: proposal.id,
        title: proposal.title,
        status: proposal.status,
        sent_at: proposal.sent_at,
        signed: Boolean(acceptance),
      };
    });
}

/**
 * Withdraw a document that has already gone out.
 *
 * The whole of the unwind: the trigger behind it (`on_proposal_voided`) stands
 * the estimate back down from approved and asks the job which estimate is
 * active now, so nothing else here has to. The document itself is untouched —
 * a voided proposal is still the answer to "what did you send me on Tuesday?"
 * and is why a presented document is frozen (ADR 0032).
 */
export async function voidProposal(id: string): Promise<void> {
  await updateRow("proposals", id, { status: "void" });
  // The trigger has put the deposit request back to fresh (ADR 0123); the
  // invoice it was holding at Mercury is cancelled from here, because a
  // trigger cannot call an API. Not awaited and never fatal: the void has
  // landed, and a Mercury refusal lands on the row for Tools -> Integrations.
  void supabase.functions
    .invoke("mercury", { body: { action: "withdraw", proposalId: id } })
    .catch(() => {});
}

/**
 * Photographs of the customer's own roof, for the proposal.
 *
 * `path` is an object in the private `job-photos` bucket, never a URL. The
 * office sees them through a signed URL made here; the customer sees them
 * through one minted by the presented-document Edge Function when they open
 * their copy. See the 20260813150000 migration for why it is the path that is
 * stored and not a link.
 */
export interface JobPhoto {
  id: string;
  job_id: string;
  path: string;
  filename: string | null;
  caption: string | null;
  sort_order: number;
  created_at: string;
}

export async function fetchJobPhotos(jobId: string): Promise<JobPhoto[]> {
  return unwrap<JobPhoto[]>(
    await supabase
      .from("job_photos")
      .select("*")
      .eq("job_id", jobId)
      .order("sort_order")
      .order("created_at"),
  );
}

/** Store one photograph against a job and record it in order. */
export async function uploadJobPhoto(
  jobId: string,
  file: File,
  sortOrder: number,
  profileId: string | null,
): Promise<JobPhoto> {
  const path = await uploadJobFile("job-photos", jobId, file);
  return insertRow<JobPhoto>("job_photos", {
    job_id: jobId,
    path,
    filename: file.name,
    sort_order: sortOrder,
    uploaded_by: profileId,
  });
}

/**
 * Links the office can look at, good for an hour.
 *
 * One request for the lot rather than one each: a job with a dozen photographs
 * would otherwise open a dozen round trips every time the modal is opened.
 */
export async function signJobPhotos(
  paths: readonly string[],
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("job-photos")
    .createSignedUrls(paths as string[], 60 * 60);
  if (error) throw new Error(error.message);
  const pairs: [string, string][] = [];
  for (const row of data ?? []) {
    // A photograph deleted since is skipped rather than left as a broken
    // image. Supabase reports one per row, not for the whole request.
    if (row.path && row.signedUrl) pairs.push([row.path, row.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

/**
 * The documents the company sends over and over.
 *
 * The licence, the certificate of insurance, the W-9, warranty brochures. Not
 * attached to a job — picked from here when one is needed. `path` is an object
 * in the private `company-docs` bucket, never a URL, for the same reason as a
 * job photo: what gets frozen into a presented document has to outlive any
 * signature.
 */
export interface CompanyDocument {
  id: string;
  name: string;
  category: string | null;
  path: string;
  filename: string | null;
  mime_type: string | null;
  size_bytes: number | null;
  expires_at: string | null;
  active: boolean;
  sort_order: number;
  created_at: string;
}

export async function fetchCompanyDocuments(
  includeRetired = false,
): Promise<CompanyDocument[]> {
  let request = supabase.from("company_documents").select("*");
  if (!includeRetired) request = request.eq("active", true);
  return unwrap<CompanyDocument[]>(
    await request.order("category").order("sort_order").order("name"),
  );
}

/**
 * Store one document in the library.
 *
 * The file goes to storage first and the row second: a row pointing at an
 * object that failed to upload is worse than no row, and it is the shape the
 * measurement and photo uploads already use.
 */
export async function uploadCompanyDocument(
  file: File,
  fields: { name: string; category?: string | null; expiresAt?: string | null },
  profileId: string | null,
): Promise<CompanyDocument> {
  const safe = file.name.replace(/[^\w.-]+/g, "_");
  const path = `${Date.now()}-${safe}`;
  const { error } = await supabase.storage
    .from("company-docs")
    .upload(path, file, {
      contentType: file.type || "application/octet-stream",
    });
  if (error) throw new Error(error.message);

  return insertRow<CompanyDocument>("company_documents", {
    name: fields.name,
    category: fields.category ?? null,
    path,
    filename: file.name,
    mime_type: file.type || null,
    size_bytes: file.size,
    expires_at: fields.expiresAt ?? null,
    uploaded_by: profileId,
  });
}

/** Links good for an hour, one request for the lot. */
export async function signCompanyDocuments(
  paths: readonly string[],
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("company-docs")
    .createSignedUrls(paths as string[], 60 * 60);
  if (error) throw new Error(error.message);
  const pairs: [string, string][] = [];
  for (const row of data ?? []) {
    if (row.path && row.signedUrl) pairs.push([row.path, row.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

/**
 * Deleting a document means deleting the bytes too.
 *
 * The row goes first: if storage refuses, the library still shows the document
 * and the person can try again, which is recoverable. The other order leaves an
 * orphaned object nobody can see. Only three people are allowed here at all —
 * both the table policy and the bucket policy check the same marker, so a
 * refusal arrives as an error rather than a silent no-op.
 */
export async function deleteCompanyDocument(
  document: CompanyDocument,
): Promise<void> {
  await deleteRow("company_documents", document.id);
  const { error } = await supabase.storage
    .from("company-docs")
    .remove([document.path]);
  if (error) throw new Error(error.message);
}

/** A company document picked to go out with one estimate. */
export interface EstimateDocument {
  id: string;
  estimate_id: string;
  company_document_id: string;
  sort_order: number;
  document: CompanyDocument | null;
}

export async function fetchEstimateDocuments(
  estimateId: string,
): Promise<EstimateDocument[]> {
  const rows = unwrap<
    (Omit<EstimateDocument, "document"> & {
      document: CompanyDocument | CompanyDocument[] | null;
    })[]
  >(
    await supabase
      .from("estimate_documents")
      .select("*, document:company_documents(*)")
      .eq("estimate_id", estimateId)
      .order("sort_order"),
  );

  // PostgREST returns an embedded row as an object or an array depending on the
  // constraints it can see. Handled either way rather than guessed at.
  return rows.map((row) => ({
    ...row,
    document: Array.isArray(row.document)
      ? (row.document[0] ?? null)
      : row.document,
  }));
}

export async function attachDocumentToEstimate(
  estimateId: string,
  companyDocumentId: string,
  sortOrder: number,
): Promise<void> {
  await insertRow<{ id: string }>("estimate_documents", {
    estimate_id: estimateId,
    company_document_id: companyDocumentId,
    sort_order: sortOrder,
  });
}

/* ------------------------------------------------------------------ QuickBooks
 *
 * Selling a job queues an invoice — the `job_sold` trigger does that, so it
 * happens whether or not the browser is still open (migration 20260816110000).
 * What the trigger cannot do is work out the contract amount: that is
 * `computeLadder`, it lives in `packages/core`, and re-implementing it in SQL
 * would be a second ladder free to drift from the one the golden tests pin
 * (CLAUDE.md rule 1). So the app fills the figure in, here, using exactly the
 * numbers the estimate card was showing a moment earlier.
 *
 * A row left without an amount is not lost. It sits in the queue, the push
 * skips it, and Tools → Integrations lists it as needing a look.
 */

/**
 * Every e-signature on a job, newest first.
 *
 * All of them, not the latest: a job can be signed more than once. Katie
 * Harding-Smith on job 1015 signed the deck on one document and the siding on
 * another, and reading only the newest made her contract the $4,000 siding.
 * `invoiceAmount` in packages/core is what decides which of these count —
 * an option re-signed on a later document has two rows here and owes one
 * amount, and sorting that out is a rule, so it lives there and not in a query.
 */
export async function signedContracts(
  jobId: string,
): Promise<SignedContract[]> {
  const rows = unwrap<
    {
      id: string;
      total: NumericFromDb;
      estimate_id: string | null;
      extra_estimate_ids: string[] | null;
      accepted_at: string;
    }[]
  >(
    await supabase
      .from("proposal_acceptances")
      .select(
        "id, total, estimate_id, extra_estimate_ids, accepted_at, " +
          "proposals!inner(job_id)",
      )
      .eq("proposals.job_id", jobId)
      .order("accepted_at", { ascending: false }),
  );
  return rows.map((row) => ({
    id: row.id,
    total: row.total == null ? cents(0) : toCents(row.total),
    estimateIds: row.estimate_id
      ? [row.estimate_id, ...(row.extra_estimate_ids ?? [])]
      : [],
    acceptedAt: row.accepted_at,
  }));
}

/**
 * `signedContracts` for a list of jobs, in one read.
 *
 * The production board needs what every job on it was signed for — the deposit
 * is half of that and must not move when a change order is approved (ADR 0205)
 * — and a query per card is a query per card. Same rows, same order, same rule
 * in `invoiceAmount` deciding which of them count.
 */
export async function fetchSignedContracts(
  jobIds: readonly string[],
): Promise<Map<string, SignedContract[]>> {
  const out = new Map<string, SignedContract[]>();
  if (jobIds.length === 0) return out;
  const rows = unwrap<
    {
      id: string;
      total: NumericFromDb;
      estimate_id: string | null;
      extra_estimate_ids: string[] | null;
      accepted_at: string;
      proposals: { job_id: string } | { job_id: string }[] | null;
    }[]
  >(
    await supabase
      .from("proposal_acceptances")
      .select(
        "id, total, estimate_id, extra_estimate_ids, accepted_at, " +
          "proposals!inner(job_id)",
      )
      .in("proposals.job_id", jobIds as string[])
      .order("accepted_at", { ascending: false }),
  );
  for (const row of rows) {
    // PostgREST hands an embedded parent back as an object or as a
    // single-element array depending on the constraints it can see.
    const parent = Array.isArray(row.proposals)
      ? row.proposals[0]
      : row.proposals;
    const jobId = parent?.job_id;
    if (!jobId) continue;
    const list = out.get(jobId) ?? [];
    list.push({
      id: row.id,
      total: row.total == null ? cents(0) : toCents(row.total),
      estimateIds: row.estimate_id
        ? [row.estimate_id, ...(row.extra_estimate_ids ?? [])]
        : [],
      acceptedAt: row.accepted_at,
    });
    out.set(jobId, list);
  }
  return out;
}

/**
 * The upgrades the customer bought on one estimate, for a work order's Copy
 * From Estimate.
 *
 * A signature covers the estimate it was made on and every extra estimate it
 * bought alongside, the same as `signedContracts` reads it, so both are asked
 * for. Which of those still stand, and what they bought, is
 * `boughtUpgrades` in packages/core.
 */
export async function fetchBoughtUpgrades(
  estimateId: string,
): Promise<string[]> {
  const rows = unwrap<
    {
      estimate_id: string | null;
      extra_estimate_ids: string[] | null;
      upgrade_groups: string[] | null;
      // PostgREST can hand the embed back as one row or as a list of one.
      proposals: { status: string | null } | { status: string | null }[] | null;
    }[]
  >(
    await supabase
      .from("proposal_acceptances")
      .select(
        "estimate_id, extra_estimate_ids, upgrade_groups, proposals!inner(status)",
      )
      .or(`estimate_id.eq.${estimateId},extra_estimate_ids.cs.{${estimateId}}`),
  );
  return boughtUpgrades(
    rows.map((row) => {
      const proposal = Array.isArray(row.proposals)
        ? row.proposals[0]
        : row.proposals;
      return { ...row, voided: proposal?.status === "void" };
    }),
    estimateId,
  );
}

/**
 * A signed job's deposit base, including active options not covered by a
 * signature. The compact parts view avoids loading every line of the ladder.
 */
export async function fetchSignedContractBases(
  jobIds: readonly string[],
): Promise<Map<string, Cents>> {
  if (jobIds.length === 0) return new Map();
  const [jobs, parts, signatures] = await Promise.all([
    supabase
      .from("jobs")
      .select("id,job_type")
      .in("id", jobIds as string[]),
    supabase
      .from("job_contract_parts")
      .select(
        "job_id,estimate_id,line_total,line_discount,change_orders,office_discount",
      )
      .in("job_id", jobIds as string[]),
    fetchSignedContracts(jobIds),
  ]);
  return signedJobContractValues(
    unwrap<{ id: string; job_type: string | null }[]>(jobs),
    unwrap<JobContractParts[]>(parts),
    signatures,
  );
}

/**
 * What the customer signed, across every document they signed. In cents.
 *
 * For a caller that has no ladder to fall back on — matching a payment against
 * a job with no QuickBooks invoice yet. Null when nothing was e-signed.
 */
export async function signedContractTotal(
  jobId: string,
): Promise<Cents | null> {
  const signed = await signedContracts(jobId);
  if (signed.length === 0) return null;
  return invoiceAmount({ signed });
}

/**
 * Work out what a newly sold job should be invoiced for, and write it onto the
 * queued row.
 *
 * Called straight after `send_job()`. It is deliberately forgiving: a failure
 * here must not make Sends look like it failed, because the handoff itself has
 * already happened and the invoice can be filled in later from the Integrations
 * screen. The caller ignores the result for that reason.
 */
export async function recordInvoiceAmount(job: Job): Promise<void> {
  try {
    const [signed, inputs] = await Promise.all([
      signedContracts(job.id),
      fetchLadderInputs([job.id]),
    ]);

    const amount = invoiceAmount({
      signed,
      options: soldOptions(job.job_type, inputs.get(job.id)),
    });
    if (amount <= 0) {
      await noteInvoiceProblem(
        job.id,
        "No contract amount could be worked out for this job. Check that it " +
          "has an active estimate with priced lines.",
      );
      return;
    }

    await supabase
      .from("quickbooks_invoices")
      .update({ amount_cents: amount, last_error: null })
      .eq("job_id", job.id)
      .is("amount_cents", null);

    // ...and send it, rather than waiting for somebody to press a button.
    // Jonathon, 2026-08-19: "I don't want to have to click on send now on the
    // integration screen every time." (docs/adr/0042)
    await supabase.functions.invoke("quickbooks", { body: { action: "push" } });
  } catch (error) {
    // Whatever went wrong is written where somebody will see it.
    //
    // The first version of this ended `.catch(() => {})`, on the reasoning that
    // a QuickBooks hiccup must not make a handoff that worked look like it
    // failed. That half was right. Throwing the reason away was not: when
    // `fetchLadderInputs` started querying a table that had been renamed, every
    // sold job queued an invoice with no amount and nothing anywhere said why.
    // The handoff still must not fail — so this still does not rethrow — but
    // the reason now lands on the row and shows in Tools → Integrations.
    await noteInvoiceProblem(
      job.id,
      String((error as Error)?.message ?? error),
    ).catch(() => {});
  }
}

/** Put a reason on the queued row, where the Integrations screen will show it. */
async function noteInvoiceProblem(
  jobId: string,
  message: string,
): Promise<void> {
  await supabase
    .from("quickbooks_invoices")
    .update({ last_error: message.slice(0, 1000) })
    .eq("job_id", jobId)
    .is("amount_cents", null);
}

/* --------------------------------------------------------------------- Mercury
 *
 * Signing queues a payment request — the `jobs_signed_ask_for_deposit` trigger
 * does that, so it happens whether or not the browser is still open (migration
 * 20260826130000). What the trigger cannot do is work out how much to ask for:
 * that is the payment schedule over the ladder, both of which live in
 * `packages/core`, and re-implementing either in SQL would be a second copy
 * free to drift from the one the golden tests pin (CLAUDE.md rule 1).
 *
 * So the app fills the figure in, here, and then asks the Edge Function to
 * send. The same shape as `recordInvoiceAmount` above, and forgiving in the
 * same way: a failure must not make signing look like it failed, because the
 * signature has already happened. The reason lands on the row instead.
 */

/**
 * What the customer owes at signing, and the contract it comes out of.
 *
 * The same figure `presented-document` writes as part of signing: core
 * `signingFirstPayment` over what was bought, each option's typed first
 * payment counting for itself and the rest pooled (ADR 0124). Until
 * 2026-09-10 this took slot 1 of whichever active estimate came first and
 * applied it to the whole contract, so on a roof with $2,000 typed beside
 * untyped gutters and siding it asked for $2,000 where signing asked $2,850.
 */
export async function recordDepositAmount(job: Job): Promise<void> {
  try {
    const [acceptance, inputs] = await Promise.all([
      latestAcceptance(job.id),
      fetchLadderInputs([job.id]),
    ]);

    // Each active estimate and what its ladder says it is worth, for a job
    // sold on paper.
    const options = soldOptions(job.job_type, inputs.get(job.id));
    const active = options.map((option) => ({
      estimateId: option.estimateId,
      total: option.ladderTotal,
    }));
    /*
     * The contract THIS signature bought, which is not the job's contract when
     * the job has been signed more than once.
     *
     * `invoiceAmount` adds every signature up, because that is what the
     * customer owes in all. A deposit is asked against the document just
     * signed: the split below shares this figure out among the options that
     * document named, so handing it the whole job would bill an earlier
     * contract's money a second time. Katie Harding-Smith signing the siding
     * on job 1015 owes a deposit on $4,000, not on the deck she already signed
     * and already paid a deposit on.
     */
    const contract =
      acceptance?.total != null && acceptance.total > 0
        ? acceptance.total
        : invoiceAmount({ options });
    if (contract <= 0) {
      await noteDepositProblem(
        job.id,
        "No contract amount could be worked out for this job, so there is " +
          "nothing to ask a deposit against.",
      );
      return;
    }

    /*
     * What was bought. An e-signature names it — the lead estimate, then the
     * rest — and each of the rest is worth its frozen package total, the lead
     * the remainder, exactly as signing splits it (`paymentPlan` in the Edge
     * Function; supabase/test/first-payment.test.ts holds the two together).
     * A contract signed on paper has no acceptance, and then it is every
     * active estimate at its own ladder.
     */
    const bought =
      acceptance && acceptance.estimateIds.length > 0
        ? acceptance.estimateIds.map((estimateId) => ({
            estimateId,
            total: acceptance.packageTotals.get(estimateId) ?? cents(0),
          }))
        : active;
    /*
     * What the customer read. A document frozen from 2026-09-11 carries the
     * first payment typed on each package (`typedFirstPayment`), and that
     * beats the live schedule, as it does at signing — a first payment edited
     * after presenting is not billed. Paper has no document, and neither does
     * an acceptance that names no estimate; those read the schedule.
     */
    const frozen =
      acceptance && acceptance.estimateIds.length > 0
        ? acceptance.firstPayments
        : new Map<string, unknown>();
    const firstSlots = await fetchFirstPayments(
      bought.map((option) => option.estimateId),
    );
    const first = signingFirstPaymentTyped(
      signedContractOptions(
        contract,
        bought.map((option) => ({
          ...option,
          payments: firstSlots.filter(
            (row) => row.estimate_id === option.estimateId,
          ),
        })),
      ).map((option) => ({
        total: option.total,
        typedFirst: typedFirstPaymentAtSigning(
          frozen.get(option.estimateId),
          option.payments,
        ),
      })),
    );

    if (!first.dueNow) {
      // The ordinary answer on a job under $1,000, and on one where the rep
      // set the first payment to zero on purpose. Not an error — but the row
      // must not sit in the queue forever pretending to be one, so it is
      // cancelled with the reason on it.
      await supabase
        .from("mercury_payment_requests")
        .update({
          state: "cancelled",
          contract_cents: contract,
          last_error:
            contract < PAYMENT_SPLIT_THRESHOLD
              ? "Under $1,000, so the whole contract is due on completion " +
                "and there is no deposit to ask for."
              : "The first payment on what was bought comes to zero, so " +
                "there is no deposit to ask for.",
        })
        .eq("job_id", job.id)
        .eq("slot", 1)
        .eq("state", "pending");
      return;
    }

    await supabase
      .from("mercury_payment_requests")
      .update({
        amount_cents: first.amount,
        contract_cents: contract,
        last_error: null,
      })
      .eq("job_id", job.id)
      .eq("slot", 1)
      .is("amount_cents", null);

    /*
     * It is NOT sent from here any more.
     *
     * Until 2026-09-06 this line raised a Mercury invoice and Mercury emailed
     * the customer a bank transfer request. Jonathon asked for that to stop:
     * the customer chooses on the page after signing, and nothing is billed
     * until they do, because a card carries 3% and one invoice cannot name two
     * totals.
     *
     * What is left is a safety net. `presented-document` fills this row in as
     * part of signing; this runs when a rep opens a job whose row somehow has
     * no amount — a contract entered by hand, or a signature during an outage.
     * The page and the letters do the rest.
     */
  } catch (error) {
    await noteDepositProblem(
      job.id,
      String((error as Error)?.message ?? error),
    ).catch(() => {});
  }
}

/** Put a reason on the queued request, where the screens will show it. */
async function noteDepositProblem(
  jobId: string,
  message: string,
): Promise<void> {
  await supabase
    .from("mercury_payment_requests")
    .update({ last_error: message.slice(0, 1000) })
    .eq("job_id", jobId)
    .is("amount_cents", null);
}

/** The four payment slots as the rep typed them. */
async function fetchScheduledPayments(
  jobId: string,
): Promise<
  { slot: number; amount: NumericFromDb; trigger_text: string | null }[]
> {
  return unwrap<
    { slot: number; amount: NumericFromDb; trigger_text: string | null }[]
  >(
    await supabase
      .from("estimate_payments")
      .select("slot, amount, trigger_text, estimates!inner(job_id, is_active)")
      .eq("estimates.job_id", jobId)
      .eq("estimates.is_active", true)
      .order("slot"),
  );
}

/** Slot 1 of each of these estimates' schedules, as the rep typed it. */
async function fetchFirstPayments(
  estimateIds: readonly string[],
): Promise<{ estimate_id: string; slot: number; amount: NumericFromDb }[]> {
  if (estimateIds.length === 0) return [];
  return unwrap<{ estimate_id: string; slot: number; amount: NumericFromDb }[]>(
    await supabase
      .from("estimate_payments")
      .select("estimate_id, slot, amount")
      .in("estimate_id", [...estimateIds])
      .eq("slot", 1),
  );
}

/**
 * The latest e-signature on a job: its total, what it bought, and what each
 * option was worth on the document it signed. Null for a job signed on paper.
 *
 * The same acceptance `signedContractTotal` reads, with what the deposit also
 * needs: `estimate_id` then `extra_estimate_ids` (ADR 0120), the frozen
 * package totals the Edge Function splits the contract by, and the first
 * payment each package froze, which signing bills by.
 */
async function latestAcceptance(jobId: string): Promise<{
  total: Cents | null;
  estimateIds: string[];
  packageTotals: Map<string, Cents>;
  /**
   * `typedFirstPayment` off the signed document, exactly as frozen, for the
   * packages that carry the key. Read through `typedFirstPaymentAtSigning`.
   */
  firstPayments: Map<string, unknown>;
} | null> {
  type Proposal = { snapshot: unknown };
  const rows = unwrap<
    {
      total: NumericFromDb;
      estimate_id: string | null;
      extra_estimate_ids: string[] | null;
      proposals: Proposal | Proposal[] | null;
    }[]
  >(
    await supabase
      .from("proposal_acceptances")
      .select(
        "total, estimate_id, extra_estimate_ids, proposals!inner(job_id, snapshot)",
      )
      .eq("proposals.job_id", jobId)
      .order("accepted_at", { ascending: false })
      .limit(1),
  );
  const row = rows[0];
  if (!row) return null;

  const proposal = Array.isArray(row.proposals)
    ? row.proposals[0]
    : row.proposals;
  const snapshot = proposal?.snapshot as PresentedDocument | null | undefined;
  const packages = Array.isArray(snapshot?.sections)
    ? documentPackages(snapshot)
    : [];
  return {
    total: row.total == null ? null : toCents(row.total),
    estimateIds: row.estimate_id
      ? [row.estimate_id, ...(row.extra_estimate_ids ?? [])]
      : [],
    packageTotals: new Map(
      packages.map((pkg) => [
        pkg.estimateId,
        // Cents already, as frozen. Rounded as `paymentPlan` rounds them.
        cents(Number.isFinite(Number(pkg.total)) ? Math.round(pkg.total) : 0),
      ]),
    ),
    // Only where the key is there: absent is a document frozen before the
    // figure was, and that one reads the live schedule, as signing does.
    firstPayments: new Map<string, unknown>(
      packages
        .filter((pkg) => "typedFirstPayment" in pkg)
        .map((pkg) => [pkg.estimateId, pkg.typedFirstPayment]),
    ),
  };
}

/** A queued payment request, as the screens list it. */
export interface MercuryPaymentRow {
  id: string;
  job_id: string;
  /** Null on a link somebody asked for by hand — it is on no schedule. */
  slot: number | null;
  /** True for one of those (ADR 0141). */
  is_manual?: boolean | null;
  /** What the rep said it is for. The line the customer reads. */
  purpose?: string | null;
  amount_cents: NumericFromDb;
  contract_cents: NumericFromDb;
  state: string;
  attempts: number;
  last_error: string | null;
  payment_url: string | null;
  sent_at: string | null;
  paid_at: string | null;
  created_at: string;
  /** What the customer picked on the page after signing. Null until they do. */
  customer_method: string | null;
  /** The 3% on a card payment, in cents. */
  surcharge_cents: NumericFromDb;
  /** When we last wrote to them, and how many nudges that is. */
  last_letter_at: string | null;
  reminder_count: number | null;
  last_letter_error: string | null;
  /** When Mercury first said the invoice was Processing: paid, clearing. */
  payment_started_at: string | null;
  job?: { job_number: number | null; address: string | null } | null;
}

/** Every payment request that is not settled, newest first. */
export async function fetchMercuryQueue(): Promise<MercuryPaymentRow[]> {
  return unwrap<MercuryPaymentRow[]>(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,job_id,slot,is_manual,purpose,amount_cents,contract_cents,state," +
          "attempts,last_error,payment_url,sent_at,paid_at,created_at," +
          "customer_method,surcharge_cents,last_letter_at,reminder_count," +
          "last_letter_error,payment_started_at, job:jobs(job_number,address)",
      )
      .neq("state", "cancelled")
      .order("created_at", { ascending: false })
      .limit(100),
  );
}

/** The payment requests on one job, for the job screen. */
export async function fetchMercuryForJob(
  jobId: string,
): Promise<MercuryPaymentRow[]> {
  return unwrap<MercuryPaymentRow[]>(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,job_id,slot,amount_cents,contract_cents,state,attempts,last_error," +
          "payment_url,sent_at,paid_at,created_at",
      )
      .eq("job_id", jobId)
      .order("slot"),
  );
}

/** A queued invoice, as the Integrations screen lists it. */
export interface QuickbooksInvoiceRow {
  id: string;
  job_id: string;
  amount_cents: NumericFromDb;
  state: string;
  attempts: number;
  last_error: string | null;
  quickbooks_doc_number: string | null;
  sold_at: string;
  job?: { job_number: number | null; address: string | null } | null;
}

/** Everything not yet safely in QuickBooks, newest first. */
export async function fetchQuickbooksQueue(): Promise<QuickbooksInvoiceRow[]> {
  return unwrap<QuickbooksInvoiceRow[]>(
    await supabase
      .from("quickbooks_invoices")
      .select(
        "id,job_id,amount_cents,state,attempts,last_error," +
          "quickbooks_doc_number,sold_at, job:jobs(job_number,address)",
      )
      .neq("state", "sent")
      .order("sold_at", { ascending: false })
      .limit(100),
  );
}

/**
 * Work the contract amount out again for a job already invoiced.
 *
 * Job 1013, 2026-08-31: sold, invoiced, estimate found to be $100 short, fixed
 * — and QuickBooks went on saying the old figure because the amount was
 * captured once at the sale and nothing looked again. This is what looks again
 * (ADR 0081).
 *
 * Only for a job that has been sold and whose invoice has actually gone. An
 * unsent invoice already picks up the current figure when it goes, and a job
 * nobody has sold has no invoice to correct.
 *
 * Writes the new figure and leaves the sending to the push, which decides
 * whether QuickBooks may be changed — a part-paid invoice, or one somebody
 * edited there, is left alone with a reason on the row.
 */
export async function refreshInvoiceAmountForEstimate(
  estimateId: string,
): Promise<void> {
  const rows = unwrap<{ job_id: string }[]>(
    await supabase.from("estimates").select("job_id").eq("id", estimateId),
  );
  const jobId = rows[0]?.job_id;
  if (!jobId) return;

  const invoices = unwrap<{ id: string; state: string }[]>(
    await supabase
      .from("quickbooks_invoices")
      .select("id,state")
      .eq("job_id", jobId),
  );
  if (invoices[0]?.state !== "sent") return;

  const jobs = unwrap<{ id: string; job_type: string | null }[]>(
    await supabase.from("jobs").select("id,job_type").eq("id", jobId),
  );
  const job = jobs[0];
  if (!job) return;

  const [signed, inputs] = await Promise.all([
    signedContracts(jobId),
    fetchLadderInputs([jobId]),
  ]);
  const amount = invoiceAmount({
    signed,
    options: soldOptions(job.job_type, inputs.get(jobId)),
  });
  if (amount <= 0) return;

  await supabase
    .from("quickbooks_invoices")
    .update({ amount_cents: amount })
    .eq("job_id", jobId);

  await supabase.functions.invoke("quickbooks", { body: { action: "push" } });
}

/** The tables whose rows decide what a contract is worth. */
const CONTRACT_TABLES = new Set([
  "estimate_items",
  "estimates",
  "change_orders",
]);

/**
 * Ask for a recount after something that changes a contract total.
 *
 * Deliberately never awaited into the caller's failure: correcting QuickBooks
 * must not make editing an estimate look like it failed. Anything that goes
 * wrong lands on the invoice row and shows in Tools → Integrations.
 */
function contractMayHaveChanged(
  table: string,
  row: Record<string, unknown>,
): void {
  if (!CONTRACT_TABLES.has(table)) return;
  const estimateId =
    table === "estimates"
      ? String(row.id ?? "")
      : String(row.estimate_id ?? "");
  if (!estimateId) return;
  void refreshInvoiceAmountForEstimate(estimateId).catch(() => {});
}

/** A queued cost, for the Integrations screen. */
export interface QuickbooksBillRow {
  id: string;
  job_id: string;
  source_kind: "crew_payout" | "material_order" | "job_expense";
  source_id: string;
  amount_cents: number | null;
  txn_date: string | null;
  state: QuickbooksBillState;
  attempts: number;
  last_error: string | null;
  incurred_at: string;
  job?: { job_number: number | null; address: string | null } | null;
}

/**
 * Every cost not yet safely in QuickBooks, newest first.
 *
 * Not a cost left out on purpose (ADR 0276): one recorded before costs
 * could reach QuickBooks is never going to QuickBooks, needs nobody to do anything, and is not a
 * failure to list here.
 */
export async function fetchQuickbooksBills(): Promise<QuickbooksBillRow[]> {
  return unwrap<QuickbooksBillRow[]>(
    await supabase
      .from("quickbooks_bills")
      .select(
        "id,job_id,source_kind,source_id,amount_cents,txn_date,state,attempts," +
          "last_error,incurred_at, job:jobs(job_number,address)",
      )
      .neq("state", "sent")
      .neq("state", "left_out")
      .order("incurred_at", { ascending: false })
      .limit(100),
  );
}

/**
 * Work out what each queued cost is worth, and send them.
 *
 * The triggers queue a cost with no amount on it, for the same reason the
 * `job_sold` trigger queues an invoice with none: the arithmetic is
 * `packages/core`'s — `payoutBillAmount`, `materialBillAmount`,
 * `expenseBillAmount` — and a second copy of it in PL/pgSQL would be free to
 * drift from the one the tests pin (CLAUDE.md rules 1 and 5).
 *
 * The date is the day the cost was incurred, resolved in Central Time, for the
 * same reason an invoice carries the day the work was done (ADR 0078).
 *
 * Cheap to call and safe to call often: it only ever looks at rows still
 * missing an amount.
 */
export async function recordBillAmounts(): Promise<void> {
  const pending = unwrap<
    {
      id: string;
      source_kind: QuickbooksBillRow["source_kind"];
      source_id: string;
      incurred_at: string;
    }[]
  >(
    await supabase
      .from("quickbooks_bills")
      .select("id,source_kind,source_id,incurred_at")
      .is("amount_cents", null)
      .in("state", ["pending", "failed"])
      .limit(200),
  );
  if (pending.length === 0) return;

  const idsOf = (kind: QuickbooksBillRow["source_kind"]) =>
    pending.filter((r) => r.source_kind === kind).map((r) => r.source_id);

  const [payouts, materials, expenses] = await Promise.all([
    idsOf("crew_payout").length === 0
      ? Promise.resolve([])
      : unwrap<Record<string, unknown>[]>(
          await supabase
            .from("work_orders")
            .select(
              "id,payout_amount,tax_rate,reconcile_amt,items:work_order_items(qty,unit_price)",
            )
            .in("id", idsOf("crew_payout")),
        ),
    idsOf("material_order").length === 0
      ? Promise.resolve([])
      : unwrap<Record<string, unknown>[]>(
          await supabase
            .from("material_orders")
            .select(
              "id,tax_rate,tax_amount,reconcile_amt,items:material_order_items(qty,unit_price)",
            )
            .in("id", idsOf("material_order")),
        ),
    idsOf("job_expense").length === 0
      ? Promise.resolve([])
      : unwrap<Record<string, unknown>[]>(
          await supabase
            .from("job_expenses")
            .select("id,amount")
            .in("id", idsOf("job_expense")),
        ),
  ]);

  const byId = (rows: Record<string, unknown>[]) =>
    new Map(rows.map((r) => [String(r.id), r]));
  const payoutById = byId(payouts);
  const materialById = byId(materials);
  const expenseById = byId(expenses);

  await Promise.all(
    pending.map(async (row) => {
      let amount: Cents | null = null;
      if (row.source_kind === "crew_payout") {
        const source = payoutById.get(row.source_id);
        if (source) amount = payoutBillAmount(source as never);
      } else if (row.source_kind === "material_order") {
        const source = materialById.get(row.source_id);
        if (source) amount = materialBillAmount(source as never);
      } else {
        const source = expenseById.get(row.source_id);
        if (source) amount = expenseBillAmount(source as never);
      }
      if (amount == null) return;
      await supabase
        .from("quickbooks_bills")
        .update({
          amount_cents: amount,
          txn_date: invoiceTxnDate({ sendsAt: row.incurred_at }),
          last_error: null,
        })
        .eq("id", row.id);
    }),
  );
}

/**
 * Price whatever costs are waiting and push them.
 *
 * One call, because the two always belong together: an amount worked out and
 * not sent is a cost that never reaches the accounts, and a push with nothing
 * priced does nothing.
 *
 * This is what `tellsQuickbooks` fires, so it runs on an ordinary approval and
 * must stay cheap: `recordBillAmounts` only looks at rows still missing an
 * amount, and the push only at rows that have one. It prices and sends
 * everything that is waiting rather than the one row that triggered it —
 * a cost stuck behind a failure is a cost the books are missing, whoever
 * queued it.
 */
export async function sendQuickbooksCosts(): Promise<void> {
  await recordBillAmounts();
  await supabase.functions.invoke("quickbooks", { body: { action: "push" } });
}

/** A receipt from QuickBooks, and where it landed. */
export interface QuickbooksReceiptRow {
  id: string;
  quickbooks_purchase_id: string;
  vendor_name: string | null;
  txn_date: string | null;
  amount_cents: number | null;
  description: string | null;
  account_name: string | null;
  state: "new" | "assigned" | "ignored";
  job_id: string | null;
  expense_id: string | null;
  assigned_by: string | null;
  note: string | null;
  job?: { job_number: number | null; address: string | null } | null;
}

/**
 * Receipts that still need a job.
 *
 * Only the ones nobody has placed. A receipt tagged to a project in QuickBooks
 * lands on its job by itself and never appears here — this list is the
 * exception, and the shorter it is the better the flow is working (ADR 0089).
 */
export async function fetchReceiptsNeedingAJob(): Promise<
  QuickbooksReceiptRow[]
> {
  return unwrap<QuickbooksReceiptRow[]>(
    await supabase
      .from("quickbooks_receipts")
      .select(
        "id,quickbooks_purchase_id,vendor_name,txn_date,amount_cents," +
          "description,account_name,state,job_id,expense_id,assigned_by,note," +
          " job:jobs(job_number,address)",
      )
      .eq("state", "new")
      .order("txn_date", { ascending: false })
      .limit(100),
  );
}

/** Sold jobs, for putting a receipt on one. Newest first, as people think. */
export async function fetchJobsForReceipt(): Promise<
  {
    id: string;
    job_number: number | null;
    address: string | null;
    city: string | null;
    customer: string | null;
  }[]
> {
  const rows = unwrap<
    {
      id: string;
      job_number: number | null;
      address: string | null;
      city: string | null;
      contact?: { full_name: string | null } | null;
    }[]
  >(
    await supabase
      .from("jobs")
      .select(
        "id,job_number,address,city, contact:contacts!jobs_contact_id_fkey(full_name)",
      )
      .not("sends_at", "is", null)
      .eq("is_test", false)
      .order("sends_at", { ascending: false })
      .limit(300),
  );
  return rows.map((r) => ({
    id: r.id,
    job_number: r.job_number,
    address: r.address,
    city: r.city,
    customer: r.contact?.full_name ?? null,
  }));
}

/**
 * Put a receipt on a job by hand.
 *
 * The exception path. Writes the expense, links the receipt to it, and tells
 * QuickBooks which job it was so the two sets of books agree — that last step
 * is what makes QuickBooks' own Project Profitability report right, and it is
 * why this is one function rather than three calls at the call site.
 *
 * `quickbooks_purchase_id` on the expense is what stops it being pushed back to
 * QuickBooks as a bill and counted twice.
 */
export async function assignReceiptToJob(
  receipt: QuickbooksReceiptRow,
  jobId: string,
): Promise<void> {
  const expense = await insertRow<{ id: string }>("job_expenses", {
    job_id: jobId,
    name: receiptRecordName(receipt),
    amount: (receipt.amount_cents ?? 0) / 100,
    occurred_at: receipt.txn_date,
    note: "From a receipt in QuickBooks.",
    quickbooks_purchase_id: receipt.quickbooks_purchase_id,
  });

  const { data: user } = await supabase.auth.getUser();
  await updateRow("quickbooks_receipts", receipt.id, {
    state: "assigned",
    job_id: jobId,
    expense_id: expense.id,
    assigned_at: new Date().toISOString(),
    assigned_by: user?.user?.id ?? null,
  });

  // Tell QuickBooks. If this fails the expense is still on the job — the books
  // being one tag short is worth saying out loud, but it is not worth undoing
  // work somebody just did.
  await supabase.functions.invoke("quickbooks", {
    body: {
      action: "tag-receipt",
      purchaseId: receipt.quickbooks_purchase_id,
      jobId,
    },
  });
}

/** Set a receipt aside: it was never a job cost. */
export async function ignoreReceipt(id: string, note: string): Promise<void> {
  await updateRow("quickbooks_receipts", id, {
    state: "ignored",
    note: note.trim() || null,
  });
}

/** A temporary link to the receipt image, which lives in QuickBooks. */
export async function receiptImageUrl(
  purchaseId: string,
): Promise<{ url: string | null; fileName: string | null }> {
  const { data, error } = await supabase.functions.invoke("quickbooks", {
    body: { action: "receipt-link", purchaseId },
  });
  const payload = (data ?? {}) as {
    url?: string;
    fileName?: string;
    error?: string;
  };
  if (payload.error) throw new Error(payload.error);
  if (error) throw new Error("Could not fetch the receipt from QuickBooks.");
  return { url: payload.url ?? null, fileName: payload.fileName ?? null };
}

/** Whether QuickBooks is connected, and to which company. Never a token. */
export async function fetchQuickbooksStatus(): Promise<{
  connected: boolean;
  company_name: string | null;
  realm_id: string | null;
  connected_at: string | null;
} | null> {
  const rows = unwrap<
    {
      connected: boolean;
      company_name: string | null;
      realm_id: string | null;
      connected_at: string | null;
    }[]
  >(await supabase.rpc("quickbooks_status"));
  return rows[0] ?? null;
}

/**
 * Every call the phone numbers have taken.
 *
 * One request, newest first. Small by nature — a roofing company's phone rings
 * tens of times a week, not thousands — so the report filters and adds up in
 * the browser rather than asking the database for a group-by it would then have
 * to keep in step with `jobContractValues`.
 *
 * Fetched a page at a time, because `.limit()` cannot lift the ceiling it was
 * written to clear. The 1,000-row page is Supabase's `db-max-rows`, applied by
 * the server to every request; asking for 5,000 returned the newest 1,000 with
 * no error and no sign anything was missing, which is exactly the silent
 * truncation the limit was meant to prevent. `.range()` walks past it.
 */
const CALL_PAGE = 1000;

/** Enough pages for years of calls. A runaway loop is worse than a short read,
 * so the ceiling is stated rather than implied. */
const CALL_LIMIT = 20000;

/**
 * Pass `brief` for what the phone bubble's badge reads (ADR 0321): only calls
 * since Recents was last looked at, one page at most, with no transcript,
 * summary or names. The badge is on every screen and looks again every few
 * minutes, so it must not pull the whole history to count a few missed calls.
 */
export async function fetchCalls(brief?: {
  since: string | null;
}): Promise<CallRow[]> {
  if (brief) {
    let query = supabase.from("calls").select(
      `call_sid,direction,status,answered,lead_source,customer_number,
         started_at,duration_seconds,recording_path,recording_seconds,voicemail_at,
         contact_id,job_id`,
    );
    if (brief.since) query = query.gt("started_at", brief.since);
    return unwrap<CallRow[]>(
      await query.order("started_at", { ascending: false }).limit(CALL_PAGE),
    );
  }

  const all: CallRow[] = [];

  for (;;) {
    const page = unwrap<CallRow[]>(
      await supabase
        .from("calls")
        .select(
          `call_sid,direction,status,answered,lead_source,customer_number,
           started_at,duration_seconds,recording_path,recording_seconds,voicemail_at,
           contact_id,job_id,placed_by,summary,transcript_text,transcribed_at,
           transcription_error,
           contact:contacts!calls_contact_id_fkey(full_name),
           job:jobs!calls_job_id_fkey(job_number),
           placer:profiles!calls_placed_by_fkey(full_name)`,
        )
        .order("started_at", { ascending: false })
        .range(all.length, all.length + CALL_PAGE - 1),
    );

    all.push(...page);
    if (page.length === 0 || all.length >= CALL_LIMIT) break;
  }

  return all;
}

/** One call, as the report reads it. */
export interface CallRow {
  call_sid: string;
  direction: string;
  status: string;
  answered: boolean;
  lead_source: string | null;
  customer_number: string | null;
  started_at: string;
  duration_seconds: number | null;
  recording_path: string | null;
  recording_seconds: number | null;
  voicemail_at: string | null;
  contact_id: string | null;
  job_id: string | null;
  contact?: { full_name: string | null } | null;
  job?: { job_number: number } | null;
  /** Who pressed Call in Roof Board (ADR 0310). Null on an inbound call. */
  placed_by?: string | null;
  placer?: { full_name: string | null } | null;
  /** What was said, once the recording has been read. */
  transcript_text?: string | null;
  summary?: string | null;
  transcribed_at?: string | null;
  transcription_error?: string | null;
}

/**
 * A link to one voicemail, good for an hour.
 *
 * The recording lives in a private bucket because it is a customer speaking:
 * their name, their address, and often their claim. Twilio's own URL is not
 * used — it needs the account credentials, which a browser cannot hold.
 */
export async function signVoicemail(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("voicemails")
    .createSignedUrl(path, 60 * 60);
  if (error) throw new Error(error.message);
  return data?.signedUrl ?? null;
}

// ------------------------------------------- calling from Roof Board (0310) ---

/**
 * The lead sources, with whether a job from each is called through Roof
 * Board. Every source, active or not: a job closed under a retired source
 * still has a Call button, and the rule for it is on the retired row.
 */
export async function fetchCallingSources(): Promise<
  { name: string; calls_through_roof_board: boolean | null }[]
> {
  return unwrap(
    await supabase
      .from("lead_sources")
      .select("name,calls_through_roof_board")
      .order("sort_order"),
  );
}

/**
 * Ring my phone from the Roof Board line, then connect the customer.
 *
 * The number, the job and the contact go up; `twilio-call` decides whether
 * this person may place a call at all, places it, and opens the call row.
 * The sentence in a refusal is the server's and is shown as it is.
 */
export async function placeCall(payload: {
  to: string;
  jobId?: string | null;
  contactId?: string | null;
  /** The line to call from, one of ours (ADR 0321); absent means the rep's own. */
  from?: string;
}): Promise<{ callSid: string; status: string }> {
  const { data, error } = await supabase.functions.invoke("twilio-call", {
    body: payload,
  });
  if (!error) return data as { callSid: string; status: string };

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const said = await context.json().catch(() => null);
    if (said && typeof said.error === "string") throw new Error(said.error);
  }
  throw new Error(error.message);
}

/** Ask for a call's recording to be read again after a failed reading. */
export async function transcribeCall(callSid: string): Promise<void> {
  const { error } = await supabase.functions.invoke("transcribe-call", {
    body: { callSid },
  });
  if (!error) return;
  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const said = await context.json().catch(() => null);
    if (said && typeof said.error === "string") throw new Error(said.error);
  }
  throw new Error(error.message);
}

/** One text as the Phone screen's Messages list needs it. */
export interface PhoneTextRow {
  id: string;
  direction: "inbound" | "outbound";
  body: string | null;
  sent_at: string;
  contact_id: string | null;
  /** Ten digits, the customer's. */
  phone: string | null;
  job_id: string | null;
  media: number;
  /** Which line it came through: ours, or a rep's own synced phone. */
  line: "roof-board" | "personal";
}

/**
 * Every text either way, both lines, newest first, for the Messages list.
 *
 * The Roof Board line's messages and the texts synced from reps' own phones,
 * because a customer who only ever texted Jonathon's cell is still a
 * conversation. Capped, newest first: the list is a phone's Messages screen,
 * not an archive, and the thread itself loads in full on the job.
 *
 * Pass `brief` for the phone bubble's badge (ADR 0321), which only counts
 * conversations waiting on an answer: who, which way and when, with no
 * bodies or attachments. Those rows carry a null body, no job and no media.
 */
export async function fetchPhoneTexts(brief?: {
  brief: true;
}): Promise<PhoneTextRow[]> {
  const [ours, personal] = await Promise.all([
    supabase
      .from("messages")
      .select(
        brief
          ? "message_sid,direction,sent_at,contact_id,customer_number"
          : "message_sid,direction,body,sent_at,contact_id,customer_number,job_id,media",
      )
      .order("sent_at", { ascending: false })
      .limit(2000),
    supabase
      .from("personal_texts")
      .select(
        brief
          ? "id,direction,sent_at,contact_id,counterpart_number"
          : "id,direction,body,sent_at,contact_id,counterpart_number",
      )
      .is("deleted_at", null)
      .order("sent_at", { ascending: false })
      .limit(2000),
  ]);
  const roofBoard = unwrap<
    {
      message_sid: string;
      direction: string;
      /** Absent on a brief read, as are the job and the media. */
      body?: string | null;
      sent_at: string;
      contact_id: string | null;
      customer_number: string | null;
      job_id?: string | null;
      media?: unknown[] | null;
    }[]
  >(ours).map<PhoneTextRow>((m) => ({
    id: m.message_sid,
    direction: m.direction === "outbound" ? "outbound" : "inbound",
    body: m.body ?? null,
    sent_at: m.sent_at,
    contact_id: m.contact_id,
    phone: m.customer_number,
    job_id: m.job_id ?? null,
    media: Array.isArray(m.media) ? m.media.length : 0,
    line: "roof-board",
  }));
  const synced = unwrap<
    {
      id: string;
      direction: string;
      body?: string | null;
      sent_at: string;
      contact_id: string | null;
      counterpart_number: string | null;
    }[]
  >(personal).map<PhoneTextRow>((t) => ({
    id: `personal:${t.id}`,
    direction: t.direction === "outbound" ? "outbound" : "inbound",
    body: t.body ?? null,
    sent_at: t.sent_at,
    contact_id: t.contact_id,
    phone: t.counterpart_number
      ? t.counterpart_number.replace(/\D/g, "").slice(-10) || null
      : null,
    job_id: null,
    media: 0,
    line: "personal",
  }));
  return [...roofBoard, ...synced];
}

/** One text message, as the conversation on a job reads it. */
export interface MessageRow {
  message_sid: string;
  direction: string;
  /** The number it left from or arrived on, E.164 (ADR 0310). */
  from_number?: string | null;
  status: string;
  error_code: number | null;
  error_message: string | null;
  lead_source: string | null;
  customer_number: string | null;
  body: string | null;
  media: { path: string; contentType: string }[];
  sent_at: string;
  contact_id: string | null;
  job_id: string | null;
  sender?: { full_name: string | null } | null;
}

/**
 * The conversation with one person.
 *
 * By contact rather than by job, because that is what it is: a customer with a
 * roof and a gutter job did not have two conversations, and a thread that
 * dropped half of what they said would send somebody hunting through Twilio.
 * The reply is still filed against the job it was typed on — see `sendText`.
 */
export async function fetchContactMessages(
  contactId: string,
): Promise<MessageRow[]> {
  return unwrap<MessageRow[]>(
    await supabase
      .from("messages")
      .select(
        `message_sid,direction,from_number,status,error_code,error_message,lead_source,
         customer_number,body,media,sent_at,contact_id,job_id,
         sender:profiles!messages_sent_by_fkey(full_name)`,
      )
      .eq("contact_id", contactId)
      .order("sent_at", { ascending: true })
      .limit(500),
  );
}

/** One text synced from a rep's own phone. */
export interface PersonalTextRow {
  id: string;
  direction: string;
  body: string;
  counterpart_number: string;
  sent_at: string;
  /** Whose phone it came through. Null on anything stored before 2026-09-20. */
  source_profile_id: string | null;
}

/**
 * The personal texts on one customer, oldest first.
 *
 * `deleted_at is null` twice over: here, and in the table's own policy. The
 * policy is the guardrail — a screen written next year that forgets this filter
 * gets nothing rather than gets a text somebody deleted — and this is what
 * makes the intent readable where the query is.
 */
export async function fetchContactPersonalTexts(
  contactId: string,
): Promise<PersonalTextRow[]> {
  return unwrap<PersonalTextRow[]>(
    await supabase
      .from("personal_texts")
      .select("id,direction,body,counterpart_number,sent_at,source_profile_id")
      .eq("contact_id", contactId)
      .is("deleted_at", null)
      .order("sent_at", { ascending: true })
      .limit(500),
  );
}

/**
 * Hide one synced text.
 *
 * Through the database function rather than an UPDATE, because "only the person
 * whose phone this is" is a rule the database holds — and because the row stays
 * behind afterwards, keeping its iMessage id, which is what stops the backfill
 * script bringing it back.
 */
export async function deletePersonalText(id: string): Promise<void> {
  const { error } = await supabase.rpc("delete_personal_text", { p_id: id });
  if (error) throw new Error(error.message);
}

/** Your own Mac: whether its batches are stored, and the key it presents. */
export interface PersonalTextSource {
  sync_enabled: boolean;
  owner_key: string;
}

/**
 * Your own sync, or null if your phone is not one that syncs.
 *
 * Your row and nobody else's — that is the table's policy rather than this
 * query's filter, because the row holds the key a Mac authenticates with and an
 * admin who could read it could post texts in somebody else's name.
 */
export async function fetchPersonalTextSource(): Promise<PersonalTextSource | null> {
  const { data, error } = await supabase
    .from("personal_text_sources")
    .select("sync_enabled,owner_key")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as PersonalTextSource | null) ?? null;
}

/**
 * Stop, or resume, syncing texts with one person.
 *
 * Switching it on also hides what is already stored for them: somebody who has
 * just said these conversations do not belong in the CRM does not mean "from
 * tomorrow". Through the database function, because that rule and the
 * "only the person whose phone it is" rule both live there.
 */
export async function setContactPersonalTexts(
  contactId: string,
  excluded: boolean,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_contact_personal_texts", {
    p_contact: contactId,
    p_excluded: excluded,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/** Turn the sync on or off, and return where it actually landed. */
export async function setPersonalTextSync(enabled: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_personal_text_sync", {
    p_enabled: enabled,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/** Whether Roof Board will hand the Mac the list of leads to file in Contacts. */
export async function fetchPhoneContactSync(): Promise<boolean> {
  const { data, error } = await supabase
    .from("phone_contact_settings")
    .select("sync_enabled")
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as { sync_enabled: boolean } | null)?.sync_enabled ?? false;
}

/** Turn the export of contacts on or off, and return where it actually landed. */
export async function setPhoneContactSync(enabled: boolean): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_phone_contact_sync", {
    p_enabled: enabled,
  });
  if (error) throw new Error(error.message);
  return data as boolean;
}

/** May this number be texted at all, and if not, what does a rep need to be
 * told? Asked in the database, where the opt-out list lives, so the screen and
 * the send cannot disagree. */
export async function mayTextNumber(
  number: string,
  contactId: string | null,
): Promise<{ ok: boolean; reason?: string }> {
  const { data, error } = await supabase.rpc("may_send_text", {
    p_number: number,
    p_contact: contactId,
  });
  if (error) throw new Error(error.message);
  return data as { ok: boolean; reason?: string };
}

/** Pictures a customer texted in, good for an hour. Private, like a voicemail:
 * it is their house and often their claim. */
export async function signMessageMedia(
  paths: readonly string[],
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const { data, error } = await supabase.storage
    .from("message-media")
    .createSignedUrls(paths as string[], 60 * 60);
  if (error) throw new Error(error.message);
  const pairs: [string, string][] = [];
  for (const row of data ?? []) {
    if (row.path && row.signedUrl) pairs.push([row.path, row.signedUrl]);
  }
  return Object.fromEntries(pairs);
}

/** What `twilio-send` says back. */
export interface SentText {
  messageSid: string;
  status: string;
  warning?: string;
}

/**
 * Send a text from the Roof Board line.
 *
 * The row is written by the Edge Function once Twilio has accepted the message,
 * never here: a message this app filed itself would claim a text was sent that
 * never left, and somebody would stop chasing a customer on the strength of it.
 *
 * `functions.invoke` reports a non-2xx as a generic error and puts the response
 * on `context`. The body is where the sentence is — "they replied STOP", "that
 * contact is blacklisted" — and a rep shown "non-2xx status code" learns
 * nothing.
 */
export async function sendText(payload: {
  to: string;
  body: string;
  jobId?: string | null;
  contactId?: string | null;
  /** Pictures already in the bucket under `outbound/` (ADR 0310). */
  mediaPaths?: string[];
  /** The line to send from, one of ours; absent means the rep's own. */
  from?: string;
}): Promise<SentText> {
  const { data, error } = await supabase.functions.invoke("twilio-send", {
    body: payload,
  });
  if (!error) return data as SentText;

  const context = (error as { context?: unknown }).context;
  if (context instanceof Response) {
    const said = await context.json().catch(() => null);
    if (said && typeof said.error === "string") throw new Error(said.error);
  }
  throw new Error(error.message);
}

/** A number that has been in touch and belongs to nobody. */
export interface UnknownNumber {
  phone: string;
  calls: number;
  texts: number;
  voicemails: number;
  first_at: string;
  last_at: string;
  lead_source: string | null;
  last_text: string | null;
}

/**
 * Everyone who has called or texted and is not in Roof Board.
 *
 * One row per number, newest first, because the decision being made is about
 * the person: somebody who rang twice and then texted is one decision.
 */
export async function fetchUnknownNumbers(): Promise<UnknownNumber[]> {
  return unwrap<UnknownNumber[]>(
    await supabase
      .from("unknown_numbers")
      .select("*")
      .order("last_at", { ascending: false })
      .limit(500),
  );
}

/**
 * Turn one of them into a customer with a job.
 *
 * Everything they have already said moves onto the new job — the calls, the
 * texts and a timeline entry for each — so the job's history starts where the
 * conversation did rather than where the button was pressed.
 */
export async function leadFromUnknownNumber(
  phone: string,
  name: string | null,
  repId: string | null,
): Promise<{ jobId: string; jobNumber: number; calls: number; texts: number }> {
  const { data, error } = await supabase.rpc("lead_from_unknown_number", {
    p_phone: phone,
    p_name: name,
    p_rep: repId,
  });
  if (error) throw new Error(error.message);
  return data as {
    jobId: string;
    jobNumber: number;
    calls: number;
    texts: number;
  };
}

/**
 * One bill on a job, as the database keeps it (ADR 0068).
 *
 * `snapshot` is the whole document — the PDF is drawn from it, never from the
 * estimate as it stands today, because the estimate carries on being edited
 * after the bill has gone out.
 */
export interface InvoiceRow {
  id: string;
  job_id: string;
  estimate_id: string | null;
  number: string;
  sequence: number;
  status: "ready" | "sent" | "void";
  invoice_date: string;
  due_date: string;
  snapshot: InvoiceSnapshot;
  /** Generated by the database off the snapshot. Integer cents (rule 3). */
  total_cents: Cents;
  sent_at: string | null;
  sent_to: string | null;
  created_at: string;
}

/** Every invoice on a job, newest first. */
export async function fetchInvoices(jobId: string): Promise<InvoiceRow[]> {
  return unwrap<InvoiceRow[]>(
    await supabase
      .from("invoices")
      .select("*")
      .eq("job_id", jobId)
      .order("created_at", { ascending: false }),
  );
}

/**
 * What a bill needs off each estimate it covers.
 *
 * Three reads for however many estimates, rather than `fetchEstimateChildren`
 * once per estimate: that one pulls work orders, material orders, expenses and
 * returns as well, and a bill wants none of them. Billing all four options on
 * a job would otherwise be twenty-eight queries for the six columns it uses.
 *
 * The adjustments come too, because the general contractor fee is billed on at
 * cost and the figure the estimate carries — corrected or struck off by hand —
 * is the figure the customer pays. `buildInvoice` recomputing the rule would
 * bill an amount nobody agreed to.
 */
export interface InvoiceSource {
  items: EstimateItem[];
  changeOrders: ChangeOrder[];
  adjustments: AutoExpenseAdjustments;
}

export async function fetchInvoiceSources(
  estimateIds: readonly string[],
): Promise<Map<string, InvoiceSource>> {
  const out = new Map<string, InvoiceSource>();
  if (estimateIds.length === 0) return out;
  const ids = estimateIds as string[];

  const [items, changeOrders, adjustments] = await Promise.all([
    supabase
      .from("estimate_items")
      .select("*")
      .in("estimate_id", ids)
      .order("sort_order"),
    supabase
      .from("change_orders")
      .select("*")
      .in("estimate_id", ids)
      .order("sort_order"),
    supabase
      .from("auto_expense_payments")
      .select("estimate_id,expense_key,amount_override,removed")
      .in("estimate_id", ids),
  ]);

  for (const id of ids) {
    out.set(id, {
      items: [],
      changeOrders: [],
      adjustments: { overrides: {}, removals: {} },
    });
  }

  for (const row of unwrap<EstimateItem[]>(items)) {
    out.get(row.estimate_id)?.items.push(row);
  }
  for (const row of unwrap<ChangeOrder[]>(changeOrders)) {
    // Nullable on the table — a change order whose estimate was deleted keeps
    // the job. One of those belongs to no bill here.
    if (row.estimate_id) out.get(row.estimate_id)?.changeOrders.push(row);
  }
  for (const row of unwrap<
    {
      estimate_id: string;
      expense_key: string;
      amount_override: unknown;
      removed: boolean | null;
    }[]
  >(adjustments)) {
    const source = out.get(row.estimate_id);
    if (!source) continue;
    if (row.amount_override != null) {
      source.adjustments.overrides[row.expense_key] = row.amount_override;
    }
    if (row.removed) source.adjustments.removals[row.expense_key] = true;
  }

  return out;
}

/**
 * Make one.
 *
 * The number is not passed and must not be: the database works it out, under a
 * lock, so that two people billing the same job at the same moment get two
 * numbers rather than one error. The row comes back with the number already on
 * its snapshot, and that returned snapshot is what gets rendered.
 */
export async function createInvoice(input: {
  jobId: string;
  estimateId: string | null;
  estimateIds: readonly string[];
  invoiceDate: string;
  dueDate: string;
  snapshot: InvoiceSnapshot;
}): Promise<InvoiceRow> {
  const { data, error } = await supabase.rpc("prepare_invoice_snapshot", {
    p_job_id: input.jobId,
    p_estimate_id: input.estimateId,
    p_estimate_ids: input.estimateIds,
    p_invoice_date: input.invoiceDate,
    p_due_date: input.dueDate,
    p_snapshot: input.snapshot,
  });
  if (error) throw new Error(error.message);
  const result = data as {
    created: boolean;
    number: string;
    invoice: InvoiceRow;
  };
  if (!result.created) {
    throw new Error(
      `Unsent invoice ${result.number} already exists for this estimate.`,
    );
  }
  return result.invoice;
}

/** Record that it went, and where to — which is not always who it names. */
export async function markInvoiceSent(
  id: string,
  sentTo: string,
): Promise<void> {
  await updateRow("invoices", id, { status: "sent", sent_to: sentTo });
}

/** Withdraw one. The document is kept; what was sent still happened. */
export async function voidInvoice(id: string): Promise<void> {
  await updateRow("invoices", id, { status: "void" });
}

/* ---------------------------------------------------------------------------
 * Marketing partners — the advertiser paid a share of the profit.
 * ------------------------------------------------------------------------ */

/** The arrangements. Active ones first, because that is what the screen opens on. */
export async function fetchMarketingPartners(): Promise<MarketingPartner[]> {
  return unwrap<MarketingPartner[]>(
    await supabase
      .from("marketing_partners")
      .select(
        "id,name,lead_source,fee_pct,repeat_fee_pct,basis,attribution," +
          "starts_on,ended_on,tail_months,agreement_note,active,notes",
      )
      .order("active", { ascending: false })
      .order("name"),
  );
}

/**
 * Every payment made to every partner.
 *
 * Not filtered by partner: there is one advertiser today and a handful of rows
 * a year, so one read serves the whole screen and switching partners costs
 * nothing. `marketingReport` picks out the ones it is asked about.
 */
export async function fetchMarketingPayments(): Promise<MarketingPayment[]> {
  return unwrap<MarketingPayment[]>(
    await supabase
      .from("marketing_payments")
      .select("id,partner_id,job_id,amount,paid_on,note,created_by,created_at")
      .order("paid_on", { ascending: false })
      .limit(5000),
  );
}

/**
 * Record money sent to a partner against one job.
 *
 * `created_by` is stamped from the signed-in user rather than passed in. The
 * advertiser has a login of their own, so who entered a payment is the only
 * thing separating a record of money sent from a claim that it was — see the
 * migration.
 */
export async function recordMarketingPayment(input: {
  partnerId: string;
  jobId: string;
  amount: number;
  paidOn: string;
  note?: string | null;
}): Promise<MarketingPayment> {
  const { data } = await supabase.auth.getUser();
  return insertRow<MarketingPayment>("marketing_payments", {
    partner_id: input.partnerId,
    job_id: input.jobId,
    amount: input.amount,
    paid_on: input.paidOn,
    note: input.note?.trim() || null,
    created_by: data.user?.id ?? null,
  });
}

/** Undo one. A payment entered against the wrong job is a typo, not history. */
export async function deleteMarketingPayment(id: string): Promise<void> {
  await deleteRow("marketing_payments", id);
}

/**
 * The two facts about a customer the marketing report needs, and no others.
 *
 * When we first knew them decides whether a sale is a new connection or new
 * business from an existing one; the No Contact tag decides whether it counts
 * at all. Deliberately a narrow select over every contact rather than a wider
 * embed on the jobs list — the jobs query is read by every screen in the app
 * and does not need to carry two columns for this one.
 */
export async function fetchMarketingContacts(): Promise<MarketingContact[]> {
  return unwrap<MarketingContact[]>(
    await supabase
      .from("contacts")
      .select("id,created_at,marketing_no_contact,marketing_no_contact_at")
      .limit(20000),
  );
}

/**
 * Hold a customer out of the marketing arrangement, or put them back in.
 *
 * The date it happened is stamped by a database trigger and never sent from
 * here: the agreement requires the tag before the lead has been worked, so
 * when it went on is the evidence, and evidence typed by the person it
 * benefits is not evidence.
 */
export async function setMarketingNoContact(
  contactId: string,
  excluded: boolean,
  note?: string | null,
): Promise<void> {
  await updateRow("contacts", contactId, {
    marketing_no_contact: excluded,
    marketing_no_contact_note: excluded ? (note?.trim() ?? null) : null,
  });
}

/* ==========================================================================
 * The job's documents
 * ======================================================================== */

/**
 * Every file registered against a job, newest first.
 *
 * The register, not the bytes — `job_files` says which bucket and which path,
 * and `signJobFile` turns one into a link when somebody asks to read it. See
 * the migration for why nothing is copied into a Docs bucket.
 */
export async function fetchJobFiles(jobId: string): Promise<JobFileEntry[]> {
  return unwrap<JobFileEntry[]>(
    await supabase
      .from("job_files")
      .select(
        "id,kind,title,bucket,path,mime_type,size_bytes,emailed_to,emailed_at,created_at",
      )
      .eq("job_id", jobId)
      .order("created_at", { ascending: false }),
  );
}

/** A link to one, good for an hour. Signed on demand: the buckets are private. */
export async function signJobFile(entry: {
  bucket: string;
  path: string;
}): Promise<string> {
  const { data, error } = await supabase.storage
    .from(entry.bucket)
    .createSignedUrl(entry.path, 60 * 60);
  if (error) throw new Error(error.message);
  return data.signedUrl;
}

/**
 * Put a file on the Docs card.
 *
 * The upload and the register entry are two writes and the order matters: the
 * bytes first, so a row can never point at a path that does not exist. The
 * other way round leaves a document in the list that breaks when tapped.
 */
export async function uploadJobDocument(
  jobId: string,
  file: File,
  profileId: string | null,
): Promise<void> {
  const path = await uploadJobFile("job-files", jobId, file);
  await insertRow("job_files", {
    job_id: jobId,
    kind: "upload",
    title: file.name,
    bucket: "job-files",
    path,
    mime_type: file.type || null,
    size_bytes: file.size,
    uploaded_by: profileId,
  });
}

/**
 * Keep the supplier's receipt that was read against a material order.
 *
 * Two places at once, which is the whole point: the file lands on the job's
 * Docs card like every other document, and carries `material_order_id` so the
 * order it was read against can list it beside the reconciliation.
 *
 * Not `source_table`/`source_id` — that pair is unique, one row per source, and
 * an order delivered in two loads has two receipts. See the migration.
 */
export async function uploadOrderDocument(entry: {
  jobId: string;
  materialOrderId: string;
  orderNumber: number;
  file: File;
  profileId: string | null;
}): Promise<void> {
  const path = await uploadJobFile("job-files", entry.jobId, entry.file);
  await insertRow("job_files", {
    job_id: entry.jobId,
    material_order_id: entry.materialOrderId,
    kind: "supplier_receipt",
    // The MO number in the title, because Docs lists a job's documents in one
    // column and "invoice-4471.pdf" says nothing about which order it settles.
    title: `MO-${entry.orderNumber} — ${entry.file.name}`,
    bucket: "job-files",
    path,
    mime_type: entry.file.type || null,
    size_bytes: entry.file.size,
    uploaded_by: entry.profileId,
  });
}

/** The receipts filed against one material order, newest first. */
export async function fetchOrderDocuments(
  materialOrderId: string,
): Promise<JobFileEntry[]> {
  return unwrap<JobFileEntry[]>(
    await supabase
      .from("job_files")
      .select(
        "id,kind,title,bucket,path,mime_type,size_bytes,emailed_to,emailed_at,created_at",
      )
      .eq("material_order_id", materialOrderId)
      .order("created_at", { ascending: false }),
  );
}

/**
 * Keep a receipt uploaded on an other expense.
 *
 * The material order's arrangement, for an expense: one `job_files` row that
 * the Docs card lists by job and the expense row lists by `job_expense_id`.
 * Many per expense — a cost paid twice is receipted twice. See the migration.
 */
export async function uploadExpenseReceipt(entry: {
  jobId: string;
  expenseId: string;
  expenseName: string | null;
  file: File;
  profileId: string | null;
}): Promise<void> {
  const path = await uploadJobFile("job-files", entry.jobId, entry.file);
  await insertRow("job_files", {
    job_id: entry.jobId,
    job_expense_id: entry.expenseId,
    kind: "expense_receipt",
    // The expense's name in the title, because Docs lists every document on
    // the job in one column and "IMG_2231.jpg" says nothing about what it paid.
    title: `${entry.expenseName?.trim() || "Expense"} — ${entry.file.name}`,
    bucket: "job-files",
    path,
    mime_type: entry.file.type || null,
    size_bytes: entry.file.size,
    uploaded_by: entry.profileId,
  });
}

/** The receipts filed against these expenses, newest first, one read for all. */
export async function fetchExpenseReceipts(
  expenseIds: readonly string[],
): Promise<(JobFileEntry & { job_expense_id: string })[]> {
  if (expenseIds.length === 0) return [];
  return unwrap<(JobFileEntry & { job_expense_id: string })[]>(
    await supabase
      .from("job_files")
      .select(
        "id,kind,title,bucket,path,mime_type,size_bytes,emailed_to,emailed_at,created_at,job_expense_id",
      )
      .in("job_expense_id", expenseIds as string[])
      .order("created_at", { ascending: false }),
  );
}

/**
 * Register a document some other screen produced.
 *
 * `source_table` and `source_id` carry a unique index, so a measurement report
 * uploaded twice replaces its entry instead of listing the job's roof twice.
 * Everything without a source — an invoice PDF built in the browser at the
 * moment it is emailed — simply inserts.
 */
export async function registerJobFile(entry: {
  jobId: string;
  kind: string;
  title: string;
  bucket: string;
  path: string;
  mimeType?: string | null;
  sizeBytes?: number | null;
  sourceTable?: string | null;
  sourceId?: string | null;
  emailedTo?: string | null;
  emailedAt?: string | null;
  uploadedBy?: string | null;
}): Promise<void> {
  const row = {
    job_id: entry.jobId,
    kind: entry.kind,
    title: entry.title,
    bucket: entry.bucket,
    path: entry.path,
    mime_type: entry.mimeType ?? null,
    size_bytes: entry.sizeBytes ?? null,
    source_table: entry.sourceTable ?? null,
    source_id: entry.sourceId ?? null,
    emailed_to: entry.emailedTo ?? null,
    emailed_at: entry.emailedAt ?? null,
    uploaded_by: entry.uploadedBy ?? null,
  };
  const { error } = await supabase
    .from("job_files")
    .upsert(row, { onConflict: "source_table,source_id" });
  if (error) throw new Error(error.message);
}

/** Take an upload back off the card. Only an upload — see `canDeleteDocument`. */
export async function deleteJobDocument(entry: {
  id: string;
  bucket: string;
  path: string;
}): Promise<void> {
  await supabase.storage.from(entry.bucket).remove([entry.path]);
  await deleteRow("job_files", entry.id);
}

/**
 * File away a PDF that was built in the browser and emailed.
 *
 * An invoice and a storm report are generated at the moment they are sent and,
 * until now, existed nowhere afterwards: the customer had a copy and the job
 * had none. This stores the same bytes that were attached — not a re-render,
 * which could differ — and registers them.
 */
export async function fileEmailedPdf(entry: {
  jobId: string;
  kind: string;
  title: string;
  filename: string;
  base64: string;
  emailedTo: string;
  profileId: string | null;
}): Promise<void> {
  const binary = atob(entry.base64);
  const bytes = new Uint8Array(binary.length);
  for (let at = 0; at < binary.length; at += 1)
    bytes[at] = binary.charCodeAt(at);
  const file = new File([bytes], entry.filename, { type: "application/pdf" });
  const path = await uploadJobFile("job-files", entry.jobId, file);
  await registerJobFile({
    jobId: entry.jobId,
    kind: entry.kind,
    title: entry.title,
    bucket: "job-files",
    path,
    mimeType: "application/pdf",
    sizeBytes: file.size,
    emailedTo: entry.emailedTo,
    emailedAt: new Date().toISOString(),
    uploadedBy: entry.profileId,
  });
}

/** A job's in-house payment plan, and what has been recorded toward it (ADR 0286). */
export interface JobPlan {
  terms: PlanTerms;
  /** Every check or cash payment recorded toward the plan, banked or not. */
  payments: { amountCents: number; state: string; receivedOn: string | null }[];
}

/**
 * The plan on a job's active estimate, or null when it has none.
 *
 * Per job, because the plan is the customer's: one active estimate carries it,
 * the way one carries the schedule (`fetchScheduledPayments`).
 */
export async function fetchJobPlans(
  jobIds: readonly string[],
): Promise<Map<string, JobPlan>> {
  const out = new Map<string, JobPlan>();
  if (jobIds.length === 0) return out;
  const estimates = await supabase
    .from("estimates")
    .select("job_id,plan_monthly_cents,plan_first_due_on")
    .in("job_id", [...jobIds])
    .eq("is_active", true)
    .not("plan_monthly_cents", "is", null);
  for (const row of unwrap<
    {
      job_id: string;
      plan_monthly_cents: number;
      plan_first_due_on: string;
    }[]
  >(estimates)) {
    if (out.has(row.job_id)) continue;
    out.set(row.job_id, {
      terms: {
        monthlyCents: cents(Number(row.plan_monthly_cents)),
        firstDueOn: String(row.plan_first_due_on).slice(0, 10),
      },
      payments: [],
    });
  }
  // Only the jobs that have a plan: most have none, and the to-do list asks
  // this of every job a person owns.
  if (out.size === 0) return out;
  for (const [jobId, payments] of await fetchPlanPayments([...out.keys()]))
    out.get(jobId)?.payments.push(...payments);
  return out;
}

/**
 * Every check or cash payment recorded toward a payment plan on these jobs,
 * whether or not the job's active estimate still has a plan.
 *
 * Money received is money received: removing the plan, or making another
 * estimate the active one, must not put five $100 checks back on what the
 * customer owes. The plan decides only whether a month is late.
 */
export async function fetchPlanPayments(
  jobIds: readonly string[],
): Promise<Map<string, JobPlan["payments"]>> {
  const out = new Map<string, JobPlan["payments"]>();
  if (jobIds.length === 0) return out;
  const payments = await supabase
    .from("customer_payments")
    .select("job_id,amount_cents,state,received_on,toward_plan")
    .in("job_id", [...jobIds])
    .eq("toward_plan", true);
  for (const row of unwrap<
    {
      job_id: string;
      amount_cents: number;
      state: string;
      received_on: string | null;
      toward_plan: boolean | null;
    }[]
  >(payments)) {
    // Asked of each row as well as of the query, as the letter does: a
    // payment for a scheduled slot is counted by its slot, never twice.
    if (row.toward_plan !== true) continue;
    const list = out.get(row.job_id) ?? [];
    list.push({
      amountCents: Number(row.amount_cents),
      state: String(row.state),
      receivedOn: row.received_on ?? null,
    });
    out.set(row.job_id, list);
  }
  return out;
}

/**
 * What a plan adds to `jobBalance`: the money banked toward it, the money
 * recorded and not yet banked (on its way, like a card payment at Stripe).
 * The payments count whether or not a plan is still set up
 * (`fetchPlanPayments`). There is no plan fee (ADR 0286).
 */
export function planBalanceInput(
  plan: JobPlan | null | undefined,
  payments: JobPlan["payments"] = plan?.payments ?? [],
): {
  planPaidCents: Cents;
  planOnItsWayCents: Cents;
} {
  const sum = (state: (s: string) => boolean) =>
    planSentCents(payments.filter((p) => state(p.state)));
  return {
    planPaidCents: sum((s) => s === "paid"),
    planOnItsWayCents: sum((s) => s === "submitted"),
  };
}

/**
 * The balance on one job, from the same arithmetic the board uses.
 *
 * Jonathon, 2026-09-08: "there needs to be a balance section in the overview
 * and estimate so we can quickly see how much money is left. that remaining
 * balance needs to correspond throughout roofboard."
 *
 * Six small reads rather than a view, because every one of them is already
 * needed elsewhere on the job screen and none of them is expensive. What
 * matters is that the arithmetic is `jobBalance` in packages/core and not
 * repeated here (CLAUDE.md rule 5) — the board, both cards and the balance
 * letter are then incapable of disagreeing.
 *
 * The contract is `invoiceAmount`: what the customer signed where they have
 * signed, and what the active estimates come to where they have not. That is
 * the same figure the invoice carries and the same one `raiseBalanceRequest`
 * bills against.
 */
export async function fetchJobBalance(job: Job): Promise<Balance> {
  return jobBalance(await fetchJobBalanceInput(job));
}

/**
 * What `jobBalance` is worked out from, for a caller that has to add to it.
 *
 * The receipt is the one: it states the balance *after* the payment it is for,
 * which is this plus that payment's slots — still `jobBalance`, still one
 * answer (ADR 0125).
 */
export async function fetchJobBalanceInput(job: Job): Promise<BalanceInput> {
  const [signed, inputs, payments, requests, deposits, handed, plans, toward] =
    await Promise.all([
      signedContracts(job.id),
      fetchLadderInputs([job.id]),
      fetchScheduledPayments(job.id),
      // Money that is actually in the account, from Mercury's two answers
      // about it — an invoice it raised and collected...
      supabase
        .from("mercury_payment_requests")
        // The amount as well as the slot: what a request BILLED is not always
        // its row on the schedule. A balance request bills whatever is
        // genuinely outstanding (`raiseBalanceRequest`), so `jobBalance` has
        // to be told rather than left to infer it from slot 4.
        //
        // Every request, not only the paid ones, since 2026-09-14: what is on
        // its way is read off the same rows, and a second round trip for it
        // would be a second chance for the two to disagree. `is_manual` comes
        // with them and is filtered below: a manual link bills no slot
        // (ADR 0141), but its money is as much on its way as any other.
        .select(
          "id,slot,state,is_manual,amount_cents,payment_started_at," +
            "mercury_deposit_id",
        )
        .eq("job_id", job.id),
      // ...and a settled credit read off the account and confirmed against
      // this job by a person. One card payment can be both; they are recorded
      // per slot, and a slot is paid once.
      supabase
        .from("mercury_deposits")
        // `description` so a check deposit can be told from a card payout, and
        // the amounts because a credit that ticked no slot is money the
        // schedule does not account for (ADR 0141).
        .select("id,slots,description,amount_cents,charge_cents")
        .eq("job_id", job.id)
        .eq("state", "confirmed"),
      // ...and money handed over in person: cash, and checks that have
      // cleared. Jonathon, 2026-09-10: "cash should be considered paid" —
      // the one kind of paid that never passes through Mercury (ADR 0125).
      supabase
        // `mercury_deposit_id` as well: a recorded payment pointing at a credit
        // is what stops that credit being counted a second time.
        .from("customer_payments")
        // ...and `mercury_payment_request_id`: a link a person said was in
        // the bank is counted, and is no longer on its way (ADR 0312).
        .select(
          "state,slots,amount_cents,toward_plan,mercury_deposit_id," +
            "mercury_payment_request_id",
        )
        .eq("job_id", job.id)
        .eq("state", "paid"),
      // ...and an in-house payment plan, and the checks toward one, which
      // count even once the plan is gone (ADR 0286).
      fetchJobPlans([job.id]),
      fetchPlanPayments([job.id]),
    ]);

  const options = soldOptions(job.job_type, inputs.get(job.id));
  const contract = invoiceAmount({ signed, options });
  // What they put their name to, which is the contract less anything signed
  // since. The deposit is half of THAT, so approving a change order adds its
  // whole amount to the Final instead of half of it to each (ADR 0205).
  const signedContract = contractAsSigned({ signed, options });

  const allRequests = unwrap<
    {
      id: string;
      slot: number | null;
      state: string;
      is_manual: boolean | null;
      amount_cents: number | null;
      payment_started_at: string | null;
      mercury_deposit_id: string | null;
    }[]
  >(requests);
  const handedRows = unwrap<
    {
      state: string;
      slots: number[] | null;
      amount_cents: number | null;
      toward_plan: boolean | null;
      mercury_deposit_id: string | null;
      mercury_payment_request_id: string | null;
    }[]
  >(handed);
  // A manual link bills no slot (ADR 0141) and is skipped where the slots are
  // read. Its money still travels, so `alreadyBeingCollected` reads every row.
  const paidRequests = allRequests.filter((row) => row.state === "paid");

  const billedBySlot: Record<number, Cents> = {};
  for (const row of paidRequests) {
    // What the invoice billed, which is all this row is read for now. Whether
    // it has been PAID is the credit's business, not the invoice's — see
    // `slotsInTheBank` and ADR 0139. A manual link bills no slot at all.
    if (row.is_manual || row.slot == null) continue;
    const slot = Number(row.slot);
    // Already cents in this column. A row with no amount falls back to the
    // schedule, which is what `jobBalance` does with a missing entry.
    const billed = Math.round(Number(row.amount_cents ?? 0));
    if (billed > 0) billedBySlot[slot] = cents(billed);
  }

  const confirmedDeposits = unwrap<
    {
      id: string;
      slots: number[] | null;
      description: string | null;
      amount_cents: number | null;
      charge_cents: number | null;
    }[]
  >(deposits);

  /*
   * Money on this job that settled no scheduled payment.
   *
   * Read off the credits, not off the requests: a link raised by hand whose
   * amount happens to match a scheduled payment ticks that slot when its money
   * lands, and counting the request as well made the same money count twice —
   * a job with $6,630 still owing read as settled (Josh, 2026-09-13). A
   * confirmed credit that ticked no slot is money the slots do not account
   * for, once, whatever raised it.
   *
   * At what the link BILLED, where a link billed it, because the 3% card
   * surcharge is not part of the contract and must not settle any of it
   * (Josh, 2026-09-14). And never a credit that is money already recorded on
   * the job — a check or cash arriving at the bank is the same money the
   * `customer_payments` row already counted (ADR 0125).
   */
  const billedByDeposit = new Map<string, number>();
  for (const row of allRequests) {
    if (!row.mercury_deposit_id) continue;
    const billed = Math.round(Number(row.amount_cents ?? 0));
    if (billed > 0) billedByDeposit.set(row.mercury_deposit_id, billed);
  }
  const handedOverDeposits = new Set(
    handedRows
      .map((row) => row.mercury_deposit_id)
      .filter((id): id is string => Boolean(id)),
  );
  const confirmedIds = new Set(confirmedDeposits.map((row) => row.id));
  const offSchedule = cents(
    offScheduleMoney(
      confirmedDeposits.map((row) => ({
        slots: row.slots,
        amountCents: row.amount_cents,
        chargeCents: row.charge_cents,
        billedCents: billedByDeposit.get(row.id) ?? null,
        handedOver:
          handedOverDeposits.has(row.id) ||
          isCheckDeposit({ description: row.description }),
      })),
    ) +
      // A link off the schedule a person said was in the bank, until its
      // credit is confirmed and counted above instead (ADR 0312).
      linkMoneySaidInTheBank(
        handedRows.map((row) => ({
          state: row.state,
          amountCents: row.amount_cents,
          slots: row.slots ?? [],
          towardPlan: row.toward_plan === true,
          mercuryRequestId: row.mercury_payment_request_id,
          depositId: row.mercury_deposit_id,
        })),
        allRequests.map((row) => ({
          id: row.id,
          mercuryDepositId: row.mercury_deposit_id,
        })),
        confirmedIds,
      ),
  );
  const saidInTheBank = new Set(
    handedRows
      .map((row) => row.mercury_payment_request_id)
      .filter((id): id is string => Boolean(id)),
  );
  const paidSlots = slotsInTheBank({
    confirmedDeposits: confirmedDeposits.map((row) => row.slots ?? []),
    handedOver: paidSlotsFromCustomerPayments(handedRows),
    // Passed, and deliberately not counted. This is the number Roof Board
    // used to call paid, and handing it to the rule rather than leaving it
    // out of the call is what makes the rule's test a test of what the app
    // does (ADR 0139).
    requestsMercuryCallsPaid: paidRequests
      .map((row) => Number(row.slot))
      .filter((slot) => Number.isFinite(slot)),
  });

  /*
   * Money the customer has sent that the bank has not seen.
   *
   * The same rule the balance letter has used since ADR 0139, now read here
   * so the job screen and the production board say what the letter says. Josh,
   * 2026-09-14: the letter asked Cat Edwards for $6,630 and told her $6,630
   * was on its way, while both screens read $13,260 owed.
   *
   * `landed` is the deposit being CONFIRMED, not merely claimed against — the
   * claim is written before the confirmation, and reading it as arrival is how
   * a payment fell between the two tests and was billed twice.
   */
  const onItsWay = cents(
    alreadyBeingCollected(
      allRequests.map((row) => ({
        state: String(row.state),
        amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
        paymentStartedAt: row.payment_started_at ?? null,
        slot: row.slot ?? null,
        landed: Boolean(
          row.mercury_deposit_id && confirmedIds.has(row.mercury_deposit_id),
        ),
        saidInTheBank: saidInTheBank.has(row.id),
      })),
      paidSlots,
    ),
  );

  const plan = planBalanceInput(plans.get(job.id), toward.get(job.id) ?? []);

  const typed = new Map<number, Cents>();
  for (const row of payments) {
    if (row.amount == null || row.amount === "") continue;
    const amount = toCents(row.amount);
    if (amount > 0 || Number(row.slot) === 1)
      typed.set(Number(row.slot), amount);
  }
  const middleCents: Record<number, Cents> = {};
  for (const slot of [2, 3]) {
    const amount = typed.get(slot);
    if (amount != null && amount > 0) middleCents[slot] = amount;
  }

  return {
    contract,
    signedContractCents: signedContract,
    firstCents: typed.get(1) ?? null,
    middleCents,
    paidSlots,
    billedBySlot,
    // A plan check recorded and not yet banked is on its way, like a card
    // payment still at Stripe.
    onItsWay: cents(onItsWay + plan.planOnItsWayCents),
    offScheduleCents: offSchedule,
    planPaidCents: plan.planPaidCents,
  };
}

/**
 * What is left to pay on a finished job, and the row that asks for it.
 *
 * Jonathon, 2026-09-06: marking the last work order complete should prompt the
 * rep with an email they review before it goes. This is the half that has to
 * happen before the email can be written — there has to be a request for the
 * balance before there is a link to put in a letter.
 *
 * Nothing is emailed here and nothing is billed. The row sits with a method of
 * null, exactly like the one signing leaves behind, and Mercury is only
 * involved once the customer picks a way to pay.
 *
 * Returns null when there is nothing to ask for: a job with no contract, one
 * whose balance is zero because the schedule collected everything up front, or
 * one where the request already went. Null is the ordinary answer on a repair
 * paid in full at signing, not an error.
 */
export async function raiseBalanceRequest(job: Job): Promise<{
  requestId: string;
  amountCents: number;
  contractCents: number;
  /** Everything the customer still owes, which can be more than is billed. */
  outstandingCents: number;
  /** Already sent, not yet in the bank. Named in the letter, not asked for. */
  onItsWayCents?: number;
  token: string | null;
} | null> {
  // A customer on an in-house payment plan pays the Final month by month
  // (ADR 0286). Finishing the work does not make it due in one go, so there
  // is nothing to ask for and no letter to offer.
  if ((await fetchJobPlans([job.id])).has(job.id)) return null;

  const existing = unwrap<
    {
      id: string;
      state: string;
      slot: number;
      amount_cents: number | null;
      contract_cents: number | null;
      last_letter_at: string | null;
      /** When Mercury first said the customer's payment was processing. */
      payment_started_at: string | null;
      /** The bank line this payment's money arrived as, if it has. */
      mercury_deposit_id: string | null;
      /** A link raised by hand, which is on no schedule (ADR 0141). */
      is_manual: boolean | null;
    }[]
  >(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,state,slot,amount_cents,contract_cents,last_letter_at," +
          "payment_started_at,mercury_deposit_id,is_manual",
      )
      .eq("job_id", job.id),
  );
  const final = existing.find((row) => Number(row.slot) === FINAL_SLOT);

  /*
   * Raised before, and the rep said Don't send. Offered again, the same row
   * and the same figure, so marking the work order complete a second time is
   * how a rep who changed their mind gets the letter back. Nothing else sends
   * it — `worthReminding` never chases a balance nobody has sent (2026-09-10,
   * job 1021); only a sent one is reminded (ADR 0260).
   */
  if (final && final.state === "pending" && final.last_letter_at == null) {
    const balance = await fetchJobBalance(job);
    if (balance.outstanding <= 0) return null;
    // A cancel refused last time is tried again. See below.
    await cancelReplacedLinks(job.id);
    return {
      requestId: final.id,
      amountCents: Math.round(Number(final.amount_cents ?? 0)),
      contractCents: Math.round(Number(final.contract_cents ?? 0)),
      outstandingCents: balance.outstanding,
      onItsWayCents: await onItsWayFor(existing, balance),
      token: await presentedToken(job.id),
    };
  }
  // Already asked. Not an error, and not a second ask — a customer billed
  // twice for the same balance is the worst outcome this whole flow has.
  if (final) return null;

  const [signed, inputs, payments, balance] = await Promise.all([
    signedContracts(job.id),
    fetchLadderInputs([job.id]),
    fetchScheduledPayments(job.id),
    fetchJobBalance(job),
  ]);
  const options = soldOptions(job.job_type, inputs.get(job.id));
  const contract = invoiceAmount({ signed, options });
  if (contract <= 0) return null;

  // The deposit is half of what was signed, not half of what a change order
  // has since made the job worth (ADR 0205).
  const schedule = paymentSchedule(
    contract,
    payments,
    contractAsSigned({ signed, options }),
  );
  if (schedule.error) return null;

  /*
   * What this request bills, which is not always the Final row on the
   * schedule.
   *
   * It was `schedule.final` until 2026-09-08 — the contract less whatever the
   * earlier slots ASKED for, whether or not any of it arrived. On a job whose
   * deposit never came through that billed half a roof and called it the
   * balance, and the letter said so to the customer. Jonathon: "we don't send
   * an email to a customer that says they only x when they actually owe y."
   *
   * So it starts from what is genuinely outstanding (`jobBalance`, the same
   * figure the board and both balance cards show) and then steps back from
   * money the customer is already being asked for, or has already started
   * paying — `alreadyBeingCollected` in packages/core, which the reminder
   * letters use too so the two can never disagree.
   *
   * A request in 'sent' that nobody has started is not subtracted: a link in
   * an inbox is not money on its way (review round 4 of #233), and Jonathon's
   * rule bills an unstarted payment in full. A request in 'paid' is one they
   * have paid, and from ADR 0139 the balance no longer
   * counts that as money in hand — so without this a customer who paid the
   * deposit by card on Friday would be invoiced for the whole contract when
   * the work finished on Monday. Josh found exactly that on 2026-09-13;
   * Jonathon: subtract it "only if that first payment has been initiated".
   * Anything nobody is asking for — pending, failed, cancelled — belongs in
   * this ask, because nothing else will collect it.
   */
  const alreadyAsked = await onItsWayFor(existing, balance);
  const owed = Math.max(0, balance.outstanding - alreadyAsked);
  if (owed <= 0) return null;

  const active = unwrap<{ id: string }[]>(
    await supabase
      .from("estimates")
      .select("id")
      .eq("job_id", job.id)
      .eq("is_active", true)
      .order("created_at")
      .limit(1),
  );

  const created = await insertRow<{ id: string }>("mercury_payment_requests", {
    job_id: job.id,
    slot: FINAL_SLOT,
    amount_cents: owed,
    contract_cents: contract,
    estimate_id: active[0]?.id ?? null,
    letter_kind: "final",
    signed_at: new Date().toISOString(),
  });

  /*
   * The earlier link this request bills again has to stop working.
   *
   * A deposit link that was sent and never started is billed in full above,
   * and until 2026-09-19 it stayed live beside this one, so the customer
   * could pay it twice. Jonathon, 2026-09-19: "raising the final request
   * should cancel the unpaid earlier link. Make sure somebody is told if
   * Mercury refuses the cancel." (ADR 0139.)
   */
  await cancelReplacedLinks(job.id);

  return {
    requestId: created.id,
    amountCents: owed,
    contractCents: contract,
    // Money already sent that the bank has not seen, so the letter can say so
    // rather than asking for it (ADR 0139, Jonathon 2026-09-13).
    onItsWayCents: alreadyAsked,
    // The whole debt, which the letter says out loud when this link cannot
    // charge all of it.
    outstandingCents: balance.outstanding,
    token: await presentedToken(job.id),
  };
}

/**
 * Cancel, at Mercury, the earlier links a final request bills again.
 *
 * The rule and the telling are in the mercury function (`replaced`), because
 * only it holds the Mercury token. Never fatal: raising the request has
 * happened, and a cancel that did not run leaves the link live on its row,
 * where `linksStillLiveBesideTheFinal` puts it on the job's balance card
 * whether Mercury refused or was never asked.
 */
async function cancelReplacedLinks(jobId: string): Promise<void> {
  try {
    await supabase.functions.invoke("mercury", {
      body: { action: "replaced", jobId },
    });
  } catch {
    // See above: the balance card says so.
  }
}

/** An earlier payment link that still takes money beside the final one. */
export interface LiveLink {
  id: string;
  slot: number;
  amountCents: number | null;
  invoiceId: string | null;
  /** Why Mercury would not cancel it, when it was asked. */
  error: string | null;
}

/**
 * Payment links still live beside a raised final request, for the balance
 * card. Empty on nearly every job. The rule is packages/core's.
 */
export async function fetchLinksStillLive(jobId: string): Promise<LiveLink[]> {
  const rows = unwrap<
    {
      id: string;
      slot: number | null;
      state: string;
      amount_cents: number | null;
      mercury_invoice_id: string | null;
      payment_started_at: string | null;
      last_error: string | null;
    }[]
  >(
    await supabase
      .from("mercury_payment_requests")
      .select(
        "id,slot,state,amount_cents,mercury_invoice_id,payment_started_at,last_error",
      )
      .eq("job_id", jobId),
  );
  return linksStillLiveBesideTheFinal(rows).map((row) => ({
    id: row.id,
    slot: Number(row.slot),
    amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
    invoiceId: row.mercury_invoice_id,
    error: row.last_error,
  }));
}

/**
 * Money the customer has already sent that the bank has not seen.
 *
 * The same figure twice over: it is what the final request must not ask for
 * again, and it is what the letter says is on its way rather than owed
 * (ADR 0139, Jonathon 2026-09-13). The rule is `alreadyBeingCollected` in
 * packages/core — a payment that has been started, whose slot the balance has
 * not already counted and whose money the bank has not been seen to hold.
 *
 * That last test reads the **deposit's own state**, not merely the link on the
 * request, and the difference is a bug Josh found on 2026-09-14: "a payment
 * claimed but never confirmed gets billed again on the final invoice."
 *
 * `mercury_deposit_id` is written by the claim — the conditional update that
 * decides which of two callers may account for a bank line — and the claim
 * comes *before* the confirmation. A confirmation that never finishes leaves a
 * request pointing at a deposit that was never confirmed, and that payment
 * then falls between the two tests: the balance does not count it, because
 * nothing was confirmed; and this did not subtract it, because a link was
 * there. So the final invoice billed the customer for their deposit a second
 * time. A claim is a lock. Only a confirmation is an accounting fact.
 */
async function onItsWayFor(
  requests: readonly {
    id: string;
    state: string;
    slot: number | null;
    amount_cents: number | null;
    payment_started_at: string | null;
    mercury_deposit_id: string | null;
    is_manual: boolean | null;
  }[],
  balance: Balance,
): Promise<number> {
  const [confirmed, saidInTheBank] = await Promise.all([
    confirmedDepositIds(
      requests
        .map((row) => row.mercury_deposit_id)
        .filter((id): id is string => Boolean(id)),
    ),
    linksSaidInTheBank(requests.map((row) => row.id)),
  ]);
  return alreadyBeingCollected(
    requests.map((row) => ({
      state: String(row.state),
      amountCents: row.amount_cents == null ? null : Number(row.amount_cents),
      paymentStartedAt: row.payment_started_at ?? null,
      slot: row.slot ?? null,
      // Money the bank has seen is already off `balance.outstanding`.
      // Confirmed, never merely claimed: the claim is written before the
      // confirmation, and reading it as arrival billed a customer twice.
      landed: Boolean(
        row.mercury_deposit_id && confirmed.has(row.mercury_deposit_id),
      ),
      // A link raised by hand counts only once it is paid: an unpaid change
      // order is money nobody has (ADR 0141).
      isManual: row.is_manual ?? false,
      // A link a person said was in the bank is counted by the balance
      // through its record, so it is not on its way as well (ADR 0312).
      saidInTheBank: saidInTheBank.has(row.id),
    })),
    // ...and so is any slot the balance has already counted, whichever source
    // saw the money.
    balance.lines.filter((line) => line.paid).map((line) => line.slot),
  );
}

/** Which of these links a person has recorded as in the bank (ADR 0312). */
async function linksSaidInTheBank(
  requestIds: readonly string[],
): Promise<Set<string>> {
  const ids = requestIds.filter(Boolean);
  if (ids.length === 0) return new Set();
  const rows = unwrap<{ mercury_payment_request_id: string | null }[]>(
    await supabase
      .from("customer_payments")
      .select("mercury_payment_request_id")
      .in("mercury_payment_request_id", [...new Set(ids)])
      .eq("state", "paid"),
  );
  return new Set(
    rows
      .map((row) => row.mercury_payment_request_id)
      .filter((id): id is string => Boolean(id)),
  );
}

/** Which of these bank lines have actually been confirmed against a job. */
async function confirmedDepositIds(
  ids: readonly string[],
): Promise<Set<string>> {
  if (ids.length === 0) return new Set();
  const rows = unwrap<{ id: string }[]>(
    await supabase
      .from("mercury_deposits")
      .select("id")
      .in("id", [...new Set(ids)])
      .eq("state", "confirmed"),
  );
  return new Set(rows.map((row) => row.id));
}

/**
 * The page the customer already knows. The same link they signed on shows the
 * balance once a slot 4 row exists — see `paymentRequestFor` in the
 * presented-document function.
 */
async function presentedToken(jobId: string): Promise<string | null> {
  const proposals = unwrap<{ token: string }[]>(
    await supabase
      .from("proposals")
      .select("token")
      .eq("job_id", jobId)
      .eq("kind", "presented")
      .order("created_at", { ascending: false })
      .limit(1),
  );
  return proposals[0]?.token ?? null;
}

/** Stamp a balance request once its letter has actually gone. */
export async function markBalanceLetterSent(requestId: string): Promise<void> {
  const now = new Date().toISOString();
  await supabase
    .from("mercury_payment_requests")
    .update({
      first_email_at: now,
      // Reminders count from here, so the customer is not nudged the same day.
      last_letter_at: now,
      last_letter_error: null,
    })
    .eq("id", requestId);
}

/** Whether every work order on a job is finished. */
export async function allWorkOrdersComplete(jobId: string): Promise<boolean> {
  const rows = unwrap<{ completed_at: string | null; status: string | null }[]>(
    await supabase
      .from("work_orders")
      .select("completed_at,status")
      .eq("job_id", jobId),
  );
  // No work orders is not "all complete" — the same rule the database trigger
  // uses (`sync_job_work_steps`, migration 20260804120000), because a job with
  // nothing scheduled has not been done.
  if (rows.length === 0) return false;
  return rows.every(
    (row) =>
      row.completed_at != null ||
      row.status === "completed" ||
      row.status === "paid",
  );
}

/** The job, with what a balance request and its letter both need. */
export async function fetchJobForBalance(jobId: string): Promise<Job | null> {
  const { data, error } = await supabase
    .from("jobs")
    .select(
      "id,job_number,job_type,address,city,state,zip,contact_id, " +
        "contact:contacts!jobs_contact_id_fkey(id,full_name,email,cell,phone)",
    )
    .eq("id", jobId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Job | null) ?? null;
}

// ------------------------------------------------------------ the list ---

/**
 * Everything on one person's list, done and open alike.
 *
 * `tasks` holds two different things and this fetches both: the tasks somebody
 * typed, and the rows that record what a person did about a computed one. Which
 * is which is `source_key` — `derivedTasks` in packages/core sorts them out.
 *
 * Bounded rather than filtered to open rows, because the panel shows what was
 * ticked today as well as what is still owed, and a person who has been here a
 * year should not fetch a year of ticks to draw this morning's list.
 */
export async function fetchMyTasks(profileId: string): Promise<TaskRow[]> {
  return unwrap<TaskRow[]>(
    await supabase
      .from("tasks")
      .select(
        "id,title,detail,job_id,assigned_to,assigned_by,due_on,source_key,snoozed_until,done_at,done_by,created_at",
      )
      .eq("assigned_to", profileId)
      .or(
        `done_at.is.null,done_at.gte.${new Date(Date.now() - 7 * 86_400_000).toISOString()}`,
      )
      .order("due_on", { nullsFirst: false })
      .limit(500),
  );
}

export interface TextReviewChoice {
  taskId: string;
  contactId: string | null;
  contactName: string | null;
  body: string | null;
  sentAt: string | null;
  jobs: { id: string; job_number: number; stage: string }[];
}

/** Admin review only: offer jobs on the text's actual contact. */
export async function fetchTextReviewChoices(
  taskIds: readonly string[],
): Promise<TextReviewChoice[]> {
  if (taskIds.length === 0) return [];
  const reviews = unwrap<
    {
      task_id: string;
      contact_id: string | null;
      source: "twilio" | "personal";
      source_id: string;
    }[]
  >(
    await supabase
      .from("text_reading_reviews")
      .select("task_id,contact_id,source,source_id")
      .in("task_id", taskIds)
      .is("resolved_at", null),
  );
  const contacts = [
    ...new Set(
      reviews.flatMap((review) =>
        review.contact_id ? [review.contact_id] : [],
      ),
    ),
  ];
  const twilioIds = reviews
    .filter((review) => review.source === "twilio")
    .map((review) => review.source_id);
  const personalIds = reviews
    .filter((review) => review.source === "personal")
    .map((review) => review.source_id);
  const [jobs, names, twilio, personal] = await Promise.all([
    contacts.length
      ? supabase
          .from("jobs")
          .select("id,contact_id,job_number,stage")
          .in("contact_id", contacts)
          .eq("is_test", false)
      : Promise.resolve({ data: [], error: null }),
    contacts.length
      ? supabase.from("contacts").select("id,full_name").in("id", contacts)
      : Promise.resolve({ data: [], error: null }),
    twilioIds.length
      ? supabase
          .from("messages")
          .select("message_sid,body,sent_at")
          .in("message_sid", twilioIds)
      : Promise.resolve({ data: [], error: null }),
    personalIds.length
      ? supabase
          .from("personal_texts")
          .select("id,body,sent_at")
          .in("id", personalIds)
      : Promise.resolve({ data: [], error: null }),
  ]);
  const jobRows = unwrap<
    {
      id: string;
      contact_id: string;
      job_number: number;
      stage: string;
    }[]
  >(jobs);
  const contactRows = unwrap<{ id: string; full_name: string | null }[]>(names);
  const twilioRows =
    unwrap<{ message_sid: string; body: string | null; sent_at: string }[]>(
      twilio,
    );
  const personalRows =
    unwrap<{ id: string; body: string; sent_at: string }[]>(personal);
  return reviews.map((review) => {
    const message =
      review.source === "twilio"
        ? twilioRows.find((row) => row.message_sid === review.source_id)
        : personalRows.find((row) => row.id === review.source_id);
    return {
      taskId: review.task_id,
      contactId: review.contact_id,
      contactName:
        contactRows.find((row) => row.id === review.contact_id)?.full_name ??
        null,
      body: message?.body ?? null,
      sentAt: message?.sent_at ?? null,
      jobs: jobRows.filter((job) => job.contact_id === review.contact_id),
    };
  });
}

export async function assignReviewedText(
  taskId: string,
  jobId: string,
): Promise<void> {
  const { error } = await supabase.rpc("assign_reviewed_text_to_job", {
    p_task_id: taskId,
    p_job_id: jobId,
  });
  if (error) throw new Error(error.message);
}

/**
 * The handful of extra rows the computed list needs.
 *
 * Everything else it reads — jobs, who owns them, the follow-up clock — is
 * already in memory from `fetchJobs`. These five are narrow selects against
 * indexed columns, run together rather than one after another: the panel is
 * drawn at sign-in and a chain of round trips would be felt.
 *
 * Each is filtered down to the rows that could possibly become a task, so what
 * comes back is tens of rows and not thousands.
 */
export async function fetchTaskSignals(
  profileId: string,
  /**
   * The jobs this person owns. Every signal below is about one of them.
   *
   * Not a nicety. `work_orders` was fetched whole — no filter, no order — and
   * Supabase answers with at most a thousand rows and no error, so past a
   * thousand orders the answer is an arbitrary thousand of them. The other
   * signals lose tasks when that happens, which is bad; this one *invents*
   * them. `no_crew_booked` reads "this job has no live work order" from the
   * absence of a row, so a job whose orders fell off the end reads as unbooked
   * and the rep is told to go and book work that is already scheduled. A list
   * that tells people to redo finished work is a list they stop believing.
   *
   * Scoping to the person's own jobs fixes the truncation and the waste
   * together: `derivedTasks` throws away every signal that does not belong to
   * one of these jobs anyway, so this asks for what it was going to keep.
   */
  jobIds: readonly string[],
): Promise<
  {
    proposals: TaskProposal[];
    mentions: TaskMention[];
    materialOrders: TaskMaterialOrder[];
    workOrders: TaskWorkOrder[];
    materialSteps: TaskMaterialStep[];
    invoices: TaskInvoice[];
    receipts: TaskReceipt[];
    unsignedWork: UnsignedWork[];
    termsChecks: PaymentTermsCheckRow[];
    latePlans: TaskLatePlan[];
  } & ReplySignals
> {
  // Nobody's jobs, nothing to say about them — and `.in()` on an empty list is
  // a request that can only answer "none", so it is not worth making.
  // Supplier mail is not among these: an email nobody could place is
  // everybody's, jobs or no jobs, so the list asks for it on its own
  // (`fetchSupplierMailSignals`, ADR 0311).
  if (jobIds.length === 0) {
    return {
      proposals: [],
      mentions: [],
      materialOrders: [],
      workOrders: [],
      materialSteps: [],
      invoices: [],
      readings: [],
      outbound: [],
      calls: [],
      payments: [],
      receipts: [],
      unsignedWork: [],
      termsChecks: [],
      latePlans: [],
    };
  }

  const [
    proposals,
    mentions,
    materialOrders,
    workOrders,
    materialSteps,
    invoices,
    replies,
    receipts,
    unsigned,
    termsChecks,
    latePlans,
  ] = await Promise.all([
    supabase
      .from("proposals")
      .select("id,job_id,status,sent_at,viewed_at")
      .in("job_id", jobIds)
      .in("status", ["sent", "viewed"]),
    // The GIN index on `mentions` was added for exactly this question and
    // until now nothing has ever asked it.
    supabase
      .from("activities")
      .select(
        "id,job_id,body,occurred_at,mentions,author:profiles!activities_created_by_fkey(full_name)",
      )
      .contains("mentions", [profileId])
      .order("occurred_at", { ascending: false })
      .limit(50),
    // Every live order, placed or not: whether a job has any order at all is
    // half the question, and a placed order is recognised by its status as
    // well as by `ordered_at`, which is often never stamped.
    supabase
      .from("material_orders")
      .select("id,job_id,order_number,ordered_at,hold_order,status,is_live")
      .in("job_id", jobIds)
      .eq("is_live", true),
    supabase
      .from("work_orders")
      .select("id,job_id,scheduled_at,is_live,start_at")
      .in("job_id", jobIds),
    // Done or N/A settles the material question for a job; the database
    // writes N/A itself when the material is ours or the crew's (ADR 0102).
    supabase
      .from("job_checklist_items")
      .select("job_id,status")
      .in("job_id", jobIds)
      .eq("label", "Material"),
    supabase
      .from("invoices")
      .select("id,job_id,status")
      .in("job_id", jobIds)
      .eq("status", "ready"),
    fetchReplySignals(jobIds),
    // Receipts drawn up and waiting to be read before they go to the
    // customer (ADR 0140). Nobody else is going to notice them.
    supabase
      .from("customer_payments")
      .select("id,job_id,amount_cents,method,receipt_waiting_since")
      .in("job_id", jobIds)
      .not("receipt_waiting_since", "is", null)
      .is("receipt_sent_at", null)
      // A send that failed keeps its reason and comes off the list; the job
      // shows it with Resend beside it (ADR 0140).
      .is("receipt_error", null),
    fetchUnsignedWork(jobIds),
    // What Jev found about payment terms (ADR 0226). Only rows that could
    // warn; `derivedTasks` decides the rest.
    supabase
      .from("payment_terms_checks")
      .select(TERMS_CHECK_COLUMNS)
      .in("job_id", jobIds)
      .gte("probability", TERMS_WARN_AT),
    // Payment plans with a monthly check late (ADR 0286).
    fetchLatePlans(jobIds),
  ]);

  return {
    proposals: unwrap<TaskProposal[]>(proposals),
    mentions: unwrap<TaskMention[]>(mentions),
    materialOrders: unwrap<TaskMaterialOrder[]>(materialOrders),
    workOrders: unwrap<TaskWorkOrder[]>(workOrders),
    materialSteps: unwrap<TaskMaterialStep[]>(materialSteps),
    invoices: unwrap<TaskInvoice[]>(invoices),
    receipts: unwrap<TaskReceipt[]>(receipts),
    unsignedWork: unsigned,
    termsChecks: unwrap<PaymentTermsCheckRow[]>(termsChecks),
    latePlans,
    ...replies,
  };
}

/**
 * The jobs among these whose in-house payment plan has a monthly payment
 * late (ADR 0286).
 *
 * One balance per plan job, because a plan pays off the Final and only the
 * balance knows what that is. Plans are few, and a job with none costs
 * nothing past the first query.
 */
export async function fetchLatePlans(
  jobIds: readonly string[],
  now: Date = new Date(),
): Promise<TaskLatePlan[]> {
  // It runs beside every other to-do signal, so a failure here costs only the
  // late-plan items, never the whole list: none at all when the plans cannot
  // be read, and none for a job whose balance cannot.
  const plans = await fetchJobPlans(jobIds).catch(
    () => new Map<string, JobPlan>(),
  );
  const offset = now.getTimezoneOffset() * 60_000;
  const today = new Date(now.getTime() - offset).toISOString().slice(0, 10);
  const late = await Promise.all(
    [...plans].map(async ([jobId, plan]): Promise<TaskLatePlan | null> => {
      try {
        const job = await fetchJobForBalance(jobId);
        if (!job) return null;
        const balance = jobBalance(await fetchJobBalanceInput(job));
        const standing = jobPlanStanding(
          balance,
          plan.terms,
          planSentCents(plan.payments),
          today,
        );
        if (!standing?.late || !standing.overdueSince) return null;
        return {
          job_id: jobId,
          behind_cents: standing.behind,
          overdue_since: standing.overdueSince,
        };
      } catch {
        return null;
      }
    }),
  );
  return late.filter((row): row is TaskLatePlan => row !== null);
}

interface ReplySignals {
  readings: TaskReading[];
  outbound: TaskOutbound[];
  calls: TaskCall[];
  payments: TaskPayment[];
}

/**
 * Customers' replies on these jobs, with what Jev read them as, and what has
 * happened since that might answer them (ADR 0158): email we sent, calls
 * logged, money recorded.
 *
 * The replies take two steps, because a message reaches its job through its
 * thread: the threads on these jobs first, then every message on them inside
 * the reading window, inbound and outbound together. The calls and payments
 * are only asked for when there is a reading for them to answer.
 * `derivedTasks` decides which reply is still owed; this only fetches.
 */
/**
 * Every payment recorded since `since`, on these jobs or on any job: what
 * "they say they paid" waits for, on My list and on the AI Team page alike
 * (issue #724). Cash and checks, a Mercury request (started is the customer
 * paying, paid is the money landing; either says "they paid", and started
 * comes first) and a QuickBooks payment.
 */
async function fetchPaymentsSince(
  since: string,
  jobIds: readonly string[] | null,
): Promise<TaskPayment[]> {
  let cash = supabase
    .from("customer_payments")
    .select("job_id,created_at")
    .gte("created_at", since);
  let requests = supabase
    .from("mercury_payment_requests")
    .select("job_id,payment_started_at,paid_at")
    .or(`payment_started_at.gte.${since},paid_at.gte.${since}`);
  let books = supabase
    .from("quickbooks_payments")
    .select("job_id,created_at")
    .gte("created_at", since);
  if (jobIds) {
    cash = cash.in("job_id", [...jobIds]);
    requests = requests.in("job_id", [...jobIds]);
    books = books.in("job_id", [...jobIds]);
  }
  const [cashAndChecks, mercury, quickbooks] = await Promise.all([
    cash.range(0, 999),
    requests.range(0, 999),
    books.range(0, 999),
  ]);
  return [
    ...unwrap<{ job_id: string; created_at: string }[]>(cashAndChecks).map(
      (row) => ({ job_id: row.job_id, at: row.created_at }),
    ),
    ...unwrap<
      {
        job_id: string;
        payment_started_at: string | null;
        paid_at: string | null;
      }[]
    >(mercury).flatMap((row) => {
      const at = row.payment_started_at ?? row.paid_at;
      return at ? [{ job_id: row.job_id, at }] : [];
    }),
    ...unwrap<{ job_id: string; created_at: string }[]>(quickbooks).map(
      (row) => ({ job_id: row.job_id, at: row.created_at }),
    ),
  ];
}

/** Dismissed on the AI Team page: handled, with a reason (ADR 0259). */
function dismissedOnAiTeam(
  handled: { reason: string | null } | { reason: string | null }[] | null,
): boolean {
  const row = Array.isArray(handled) ? handled[0] : handled;
  return Boolean(row?.reason);
}

async function fetchReplySignals(
  jobIds: readonly string[],
): Promise<ReplySignals> {
  const none: ReplySignals = {
    readings: [],
    outbound: [],
    calls: [],
    payments: [],
  };
  const threads = unwrap<{ id: string; job_id: string | null }[]>(
    await supabase
      .from("email_threads")
      .select("id,job_id")
      .in("job_id", jobIds),
  );
  const jobOf = new Map(threads.map((thread) => [thread.id, thread.job_id]));

  // Readings already accepted into the queue remain visible until settled.
  // The import cutoff belongs to the reader, not this task view.
  type Handled = { reason: string | null };
  type Reading = {
    meaning: TaskReading["meaning"];
    probability: number;
    sender_kind: TaskReading["sender_kind"];
    handled: Handled | Handled[] | null;
  };
  type ReplyMessage = {
    id: string;
    thread_id: string;
    direction: "inbound" | "outbound";
    sent_at: string;
    from_name: string | null;
    from_address: string | null;
    to_addresses: string[] | null;
    cc_addresses: string[] | null;
    snippet: string | null;
    rfc822_message_id: string | null;
    // One-to-one, so PostgREST answers with an object; an array is
    // accepted too, in case it ever reads the relation the other way.
    reading: Reading | Reading[] | null;
  };
  const messages: ReplyMessage[] = [];
  // PostgREST caps a response at 1,000 rows. An old unanswered reply must not
  // fall off this list just because a job has a long conversation history.
  for (let page = 0; threads.length > 0; page += 1) {
    const part = unwrap<ReplyMessage[]>(
      await supabase
        .from("email_messages")
        .select(
          "id,thread_id,direction,sent_at,from_name,from_address,to_addresses,cc_addresses,snippet,rfc822_message_id,reading:message_readings(meaning,probability,sender_kind,handled:message_handled(reason))",
        )
        .in("thread_id", [...jobOf.keys()])
        .order("sent_at", { ascending: true })
        .order("id", { ascending: true })
        .range(page * 500, page * 500 + 499),
    );
    messages.push(...part);
    if (part.length < 500) break;
  }

  const readings: TaskReading[] = [];
  const outbound: TaskOutbound[] = [];
  for (const message of messages) {
    if (message.direction === "outbound") {
      outbound.push({
        thread_id: message.thread_id,
        job_id: jobOf.get(message.thread_id) ?? null,
        to_addresses: [
          ...(message.to_addresses ?? []),
          ...(message.cc_addresses ?? []),
        ],
        sent_at: message.sent_at,
      });
      continue;
    }
    const reading = Array.isArray(message.reading)
      ? message.reading[0]
      : message.reading;
    if (!reading) continue;
    readings.push({
      message_id: message.id,
      thread_id: message.thread_id,
      job_id: jobOf.get(message.thread_id) ?? null,
      meaning: reading.meaning,
      probability: Number(reading.probability),
      sender_kind: reading.sender_kind,
      sent_at: message.sent_at,
      from_name: message.from_name,
      from_address: message.from_address,
      snippet: message.snippet,
      rfc822_message_id: message.rfc822_message_id,
      ...(dismissedOnAiTeam(reading.handled) ? { dismissed: true } : {}),
    });
  }

  type TextMessage = {
    body: string | null;
    sent_at: string;
    contact_id: string | null;
    customer_number?: string | null;
    counterpart_number?: string | null;
  };
  type TextReading = {
    message_sid: string | null;
    personal_text_id: string | null;
    meaning: TaskReading["meaning"];
    probability: number;
    sender_kind: TaskReading["sender_kind"];
    matched_job_id: string;
    conversation_id: string;
    sms: TextMessage | null;
    personal: TextMessage | null;
    handled: Handled | Handled[] | null;
  };
  const textRows: TextReading[] = [];
  for (let page = 0; ; page += 1) {
    const part = unwrap<TextReading[]>(
      await supabase
        .from("message_readings")
        .select(
          "id,message_sid,personal_text_id,meaning,probability,sender_kind,matched_job_id,conversation_id,sms:messages!message_readings_message_sid_fkey(body,sent_at,contact_id,customer_number),personal:personal_texts!message_readings_personal_text_id_fkey(body,sent_at,contact_id,counterpart_number),handled:message_handled(reason)",
        )
        .in("matched_job_id", jobIds)
        .order("id", { ascending: true })
        .range(page * 500, page * 500 + 499),
    );
    textRows.push(...part);
    if (part.length < 500) break;
  }
  const textContacts = new Set<string>();
  for (const row of textRows) {
    const source = row.message_sid ? "twilio" : "personal";
    const message = row.message_sid ? row.sms : row.personal;
    if (!message) continue;
    if (message.contact_id) textContacts.add(message.contact_id);
    readings.push({
      message_id: `${source}:${row.message_sid ?? row.personal_text_id}`,
      thread_id: row.conversation_id,
      job_id: row.matched_job_id,
      meaning: row.meaning,
      probability: Number(row.probability),
      sender_kind: row.sender_kind,
      sent_at: message.sent_at,
      from_address: message.customer_number ?? message.counterpart_number,
      snippet: message.body,
      ...(dismissedOnAiTeam(row.handled) ? { dismissed: true } : {}),
    });
  }
  if (textContacts.size) {
    const ids = [...textContacts];
    for (let page = 0; ; page += 1) {
      const part = unwrap<
        {
          contact_id: string;
          customer_number: string | null;
          tracking_number: string | null;
          from_number: string;
          to_number: string;
          sent_at: string;
        }[]
      >(
        await supabase
          .from("messages")
          .select(
            "message_sid,contact_id,customer_number,tracking_number,from_number,to_number,sent_at",
          )
          .eq("direction", "outbound")
          .in("contact_id", ids)
          .order("sent_at", { ascending: true })
          .order("message_sid", { ascending: true })
          .range(page * 500, page * 500 + 499),
      );
      for (const message of part) {
        outbound.push({
          thread_id: `twilio:${message.tracking_number ?? message.from_number}:${message.customer_number ?? message.to_number}`,
          sent_at: message.sent_at,
        });
      }
      if (part.length < 500) break;
    }
    for (let page = 0; ; page += 1) {
      const part = unwrap<
        {
          contact_id: string;
          source_profile_id: string | null;
          sent_at: string;
        }[]
      >(
        await supabase
          .from("personal_texts")
          .select("id,contact_id,source_profile_id,sent_at")
          .eq("direction", "outbound")
          .in("contact_id", ids)
          .order("sent_at", { ascending: true })
          .order("id", { ascending: true })
          .range(page * 500, page * 500 + 499),
      );
      for (const message of part) {
        outbound.push({
          thread_id: `personal:${message.source_profile_id ?? "unknown"}:${message.contact_id}`,
          sent_at: message.sent_at,
        });
      }
      if (part.length < 500) break;
    }
  }
  if (readings.length === 0) return none;

  const since = readings.reduce(
    (oldest, reading) => (reading.sent_at < oldest ? reading.sent_at : oldest),
    readings[0]!.sent_at,
  );

  const readJobs = [
    ...new Set(readings.flatMap((r) => (r.job_id ? [r.job_id] : []))),
  ];
  const [calls, payments] = await Promise.all([
    // A call either way counts; staff talking to each other does not.
    supabase
      .from("activities")
      .select("job_id,occurred_at")
      .in("job_id", readJobs)
      .eq("type", "call")
      .or("direction.is.null,direction.neq.internal")
      .gte("occurred_at", since),
    fetchPaymentsSince(since, readJobs),
  ]);

  return {
    readings,
    outbound,
    calls: unwrap<TaskCall[]>(calls),
    payments,
  };
}

/**
 * Which of these computed items this person has ticked off, by key. For a
 * screen that shows one outside the list, such as the job page's warning
 * about work nobody signed, whose "Dismiss" is the same answer (ADR 0324).
 */
export async function fetchDismissedTaskKeys(
  profileId: string,
  keys: readonly string[],
): Promise<Set<string>> {
  if (keys.length === 0) return new Set();
  const rows = unwrap<{ source_key: string }[]>(
    await supabase
      .from("tasks")
      .select("source_key")
      .eq("assigned_to", profileId)
      .in("source_key", keys)
      .not("done_at", "is", null),
  );
  return new Set(rows.map((row) => row.source_key));
}

/** Write down what somebody did about a computed item, so it stays done. */
export async function answerDerivedTask(entry: {
  task: Pick<DerivedTask, "key" | "title" | "detail" | "jobId" | "dueOn">;
  profileId: string;
  /** A date to come back to it, or null to tick it off for good. */
  snoozeUntil?: string | null;
}): Promise<void> {
  const { error } = await supabase.from("tasks").upsert(
    {
      title: entry.task.title,
      detail: entry.task.detail,
      job_id: entry.task.jobId,
      assigned_to: entry.profileId,
      due_on: entry.task.dueOn,
      source_key: entry.task.key,
      snoozed_until: entry.snoozeUntil ?? null,
      done_at: entry.snoozeUntil ? null : new Date().toISOString(),
      done_by: entry.snoozeUntil ? null : entry.profileId,
    },
    { onConflict: "assigned_to,source_key" },
  );
  if (error) throw new Error(error.message);
}

/* ----------------------------------------------------- neighbour letters ---
 *
 * Kelsey's mailing list: what is ready to print, what went, and what needs
 * somebody to look at it (ADR 0149). The packets themselves are built by the
 * `neighbour-letters` Edge Function; nothing here builds anything.
 */

export interface NeighbourMailing {
  id: string;
  job_id: string;
  kind: "install_notice" | "work_complete";
  status: string;
  queued_at: string;
  notified_at: string | null;
  notified_to: string | null;
  mailed_at: string | null;
  mailed_by: string | null;
  recipient_count: number;
  shortfall_note: string | null;
  error: string | null;
  flag: string | null;
  attempts: number;
  next_attempt_at: string | null;
  letter_path: string | null;
  labels_path: string | null;
  cover_path: string | null;
  jobs: {
    job_number: number | null;
    address: string | null;
    city: string | null;
  } | null;
}

/**
 * Every mailing worth showing, newest first.
 *
 * Everything, not only what is waiting: the question "did the neighbours
 * hear?" is asked about jobs that finished weeks ago, and a list that shows
 * only the unfinished ones cannot answer it. Bounded rather than paged —
 * twenty letters a month means this stays small for years.
 */
export async function fetchNeighbourMailings(): Promise<NeighbourMailing[]> {
  return unwrap<NeighbourMailing[]>(
    await supabase
      .from("neighbour_letters")
      .select(
        "id, job_id, kind, status, queued_at, notified_at, notified_to, " +
          "mailed_at, mailed_by, recipient_count, shortfall_note, error, " +
          "flag, attempts, next_attempt_at, letter_path, labels_path, " +
          "cover_path, jobs(job_number, address, city)",
      )
      .order("queued_at", { ascending: false })
      .limit(500),
  );
}

/** The households one mailing went to, in the order the labels print. */
export async function fetchNeighbourRecipients(letterId: string): Promise<
  Array<{
    id: string;
    line1: string;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    distance_miles: number | null;
    incomplete: boolean;
  }>
> {
  return unwrap(
    await supabase
      .from("neighbour_letter_recipients")
      .select("id, line1, city, state, postal_code, distance_miles, incomplete")
      .eq("letter_id", letterId)
      .order("distance_miles", { ascending: true }),
  );
}

/**
 * Ticking one off, or un-ticking it.
 *
 * `mailed_at` and `mailed_by` are stamped by the database, not sent from here
 * — the record of who put twenty letters in the post should say who actually
 * pressed the button (see the migration).
 */
export async function setNeighbourMailingMailed(
  letterId: string,
  mailed: boolean,
): Promise<void> {
  const { error } = await supabase
    .from("neighbour_letters")
    .update({ status: mailed ? "mailed" : "ready" })
    .eq("id", letterId);
  if (error) throw new Error(error.message);
}

/**
 * Putting a blocked mailing back in the queue.
 *
 * The attempts are cleared, because a person pressing this has usually just
 * fixed the thing that was wrong — a missing link in Tools, an address never
 * pinned on the map — and a row that comes back with one attempt left would
 * block again on the next hiccup.
 */
export async function retryNeighbourMailing(letterId: string): Promise<void> {
  const { error } = await supabase
    .from("neighbour_letters")
    .update({
      status: "preparing",
      attempts: 0,
      error: null,
      flag: null,
      flagged_at: null,
      claimed_at: null,
      next_attempt_at: new Date().toISOString(),
    })
    .eq("id", letterId);
  if (error) throw new Error(error.message);
  await supabase.functions
    .invoke("neighbour-letters", { body: { action: "push" } })
    .catch(() => {});
}

/** Stopping one for good, when the letters should not go at all. */
export async function cancelNeighbourMailing(letterId: string): Promise<void> {
  const { error } = await supabase
    .from("neighbour_letters")
    .update({ status: "canceled" })
    .eq("id", letterId);
  if (error) throw new Error(error.message);
}

/** A link to one of a packet's three PDFs, good for an hour. */
export async function signPacketFile(path: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from("job-files")
    .createSignedUrl(path, 60 * 60);
  if (error) throw new Error(error.message);
  return data?.signedUrl ?? null;
}

/** How many address lookups this calendar month has already paid for. */
export async function fetchNeighbourLookupCount(): Promise<number> {
  const since = new Date();
  since.setUTCDate(1);
  since.setUTCHours(0, 0, 0, 0);
  const { count, error } = await supabase
    .from("neighbour_address_lookups")
    .select("id", { count: "exact", head: true })
    .gte("requested_at", since.toISOString());
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** What one address lookup would find for a job, without sending anything. */
export async function pilotNeighbourLookup(jobId: string): Promise<{
  found?: number;
  requests?: number;
  addresses?: Array<{
    line1: string;
    city: string | null;
    state: string | null;
    postalCode: string | null;
    distanceMiles: number;
    propertyType: string | null;
    incomplete: boolean;
  }>;
  error?: string;
}> {
  const { data, error } = await supabase.functions.invoke("neighbour-letters", {
    body: { action: "pilot", jobId },
  });
  if (error) {
    // A non-2xx from an Edge Function arrives as a FunctionsHttpError whose
    // body holds the sentence worth showing; the error itself says only that
    // it failed.
    const body = await (error as { context?: Response }).context
      ?.json?.()
      .catch(() => null);
    return { error: body?.error ?? error.message };
  }
  return data ?? {};
}

/**
 * Everything the check against the quote needs besides the order itself
 * (ADR 0156): the estimate's recipe, its template and lines, the catalogue and
 * prices it was generated from, and its change orders.
 *
 * The same column lists the estimate builder and "From Measurements" read, so
 * the check regenerates exactly what they wrote. Null when the order is not on
 * an estimate at all.
 */
export interface QuoteCheckContext {
  estimate: {
    id: string;
    roof_template_id: string | null;
    generation_answers: TemplateAnswers | null;
    generation_supplier_id: string | null;
    /** What it was generated from, frozen (`GenerationSnapshot`). */
    generation_snapshot: unknown;
  };
  template: RoofTemplate | undefined;
  templateItems: RoofTemplateItem[];
  catalogue: QuoteCheckProduct[];
  prices: ProductPrice[];
  suppliers: PricedSupplier[];
  changeOrders: {
    product_id: string | null;
    description: string | null;
    qty: unknown;
  }[];
  /** The estimate's lines, for what it sells (ADR 0154). */
  estimateItems: EstimateLineSold[];
  /** The estimate's other material orders, checked with this one. */
  otherOrders: QuoteSiblingOrder[];
  /** Template names by id, for saying which two templates differ. */
  templateNames: Record<string, string>;
}

export async function fetchQuoteCheckContext(order: {
  id: string;
  estimate_id: string | null;
  roof_template_id?: string | null;
}): Promise<QuoteCheckContext | null> {
  if (!order.estimate_id) return null;
  // Not `unwrap`: it turns a missing row into an empty array, and an empty
  // array is truthy.
  const { data, error } = await supabase
    .from("estimates")
    .select(
      "id,roof_template_id,generation_answers,generation_supplier_id,generation_snapshot",
    )
    .eq("id", order.estimate_id)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const estimate = data as QuoteCheckContext["estimate"];

  const templateIds = [
    estimate.roof_template_id,
    order.roof_template_id ?? null,
  ].filter((id): id is string => Boolean(id));

  const [
    templates,
    items,
    products,
    prices,
    suppliers,
    changeOrders,
    estimateItems,
    otherOrders,
  ] = await Promise.all([
    templateIds.length > 0
      ? supabase.from("roof_templates").select("*").in("id", templateIds)
      : Promise.resolve({ data: [], error: null }),
    estimate.roof_template_id
      ? supabase
          .from("roof_template_items")
          .select(
            "id,slot,product_id,qty,default_qty,supplier_id,coverage,section," +
              "applies_when,girth_source,count_source,sort_order",
          )
          .eq("template_id", estimate.roof_template_id)
          .order("sort_order")
      : Promise.resolve({ data: [], error: null }),
    supabase
      .from("products")
      .select(
        "id,name,description,category,kind,unit,default_cost,coverage,waste,measurement,finish,galvalume_twin_id,change_order_preset",
      )
      .eq("active", true),
    supabase.from("product_prices").select("price_list_id,product_id,cost"),
    supabase.from("suppliers").select("id,price_list_id"),
    supabase
      .from("change_orders")
      .select("product_id,description,qty")
      .eq("estimate_id", estimate.id),
    supabase
      .from("estimate_items")
      .select("product_id,qty,unit,sort_order,section")
      .eq("estimate_id", estimate.id),
    // The estimate's other orders: a quote split across deliveries is
    // checked as one (ADR 0156). Include change-order purchases because their
    // quantities are also included in the expected baseline.
    supabase
      .from("material_orders")
      .select(
        "id,supplier_id,roof_template_id,items:material_order_items(id,product_id,name,qty,unit_price)",
      )
      .eq("estimate_id", estimate.id)
      .neq("id", order.id),
  ]);

  const templateRows = unwrap<RoofTemplate[]>(templates);
  return {
    estimate,
    template: templateRows.find((t) => t.id === estimate.roof_template_id),
    templateItems: unwrap(items),
    catalogue: unwrap(products),
    prices: unwrap(prices),
    suppliers: unwrap(suppliers),
    changeOrders: unwrap(changeOrders),
    estimateItems: unwrap(estimateItems),
    otherOrders: unwrap(otherOrders),
    templateNames: Object.fromEntries(templateRows.map((t) => [t.id, t.name])),
  };
}

/** What Jev proposed about this order's typed lines (ADR 0153). */
export async function fetchQuoteMatchSuggestions(
  materialOrderId: string,
): Promise<(QuoteMatchSuggestion & { id: string })[]> {
  const rows = unwrap<
    (Omit<QuoteMatchSuggestion, "probability"> & {
      id: string;
      probability: unknown;
    })[]
  >(
    await supabase
      .from("quote_match_suggestions")
      .select(
        "id,order_item_id,order_item_name,product_id,probability,accepted",
      )
      .eq("material_order_id", materialOrderId),
  );
  // numeric arrives as a string from PostgREST.
  return rows.map((row) => ({ ...row, probability: Number(row.probability) }));
}

/**
 * Ask Jev about typed lines, through the Edge Function that holds the key.
 * Throws the function's own sentence, which is written to be shown.
 */
export async function requestQuoteMatch(body: {
  orderId: string;
  itemIds: string[];
  productIds: string[];
}): Promise<void> {
  const { data, error } = await supabase.functions.invoke("quote-match", {
    body,
  });
  if (error) {
    let message = error.message;
    try {
      const context = (error as { context?: Response }).context;
      const parsed = context ? await context.json() : null;
      if (parsed?.error) message = parsed.error;
    } catch {
      // The generic message is all there is.
    }
    throw new Error(message);
  }
  if ((data as { error?: string } | null)?.error) {
    throw new Error((data as { error: string }).error);
  }
}

/**
 * Everything `computeLadder` reads for one estimate, the way EstimateCard
 * assembles it, so the quote check can say what an order's differences do to
 * the margin with the same numbers the estimate shows. Null when the estimate
 * is gone.
 */
export async function fetchEstimateLadderInput(
  estimateId: string,
  jobId: string,
): Promise<LadderInput | null> {
  const { data, error } = await supabase
    .from("estimates")
    .select("*")
    .eq("id", estimateId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) return null;
  const estimate = data as Estimate;

  const [children, layouts, adjustments, job] = await Promise.all([
    fetchEstimateChildren(estimateId),
    fetchRoofLayouts(jobId),
    fetchAutoExpenseAdjustments(estimateId),
    fetchJob(jobId),
  ]);
  return {
    estimate,
    items: children.items,
    changeOrders: children.changeOrders,
    materialOrders: children.materialOrders,
    workOrders: children.workOrders,
    expenses: children.expenses,
    returns: children.returns,
    jobType: job?.job_type ?? null,
    place: job,
    layouts,
    autoExpenseOverrides: adjustments.overrides,
    autoExpenseRemovals: adjustments.removals,
    workOrderCrews: children.workOrders.map((order) => order.crew ?? null),
  };
}

/* ---- the Messages inbox (ADR 0222, ADR 0227) -------------------------- */

/** How much of a message the list shows before the row is opened. */
const SORTED_TEXT_MAX = 400;

/** The job an email belongs to, through its thread. */
const threadJob = (email: { thread: { job_id: string | null } | null }) =>
  email.thread?.job_id ?? null;

/** Who an email we sent went to, To and Cc together. */
const sentTo = (email: {
  to_addresses: string[] | null;
  cc_addresses: string[] | null;
}) => [...(email.to_addresses ?? []), ...(email.cc_addresses ?? [])];

/** A customer text's number: the company line's, or a synced phone's. */
const textNumber = (text: {
  customer_number?: string | null;
  counterpart_number?: string | null;
}) => text.customer_number ?? text.counterpart_number ?? null;

/**
 * Every customer message read in the last fortnight, email and text, with
 * its seven answers when it has them; every text and email we sent in the
 * same fortnight; and the messages somebody marked handled. Only fetches:
 * who is waiting, and in what order, is core's `buildInbox`.
 */
export async function fetchInbox(days: number = REPLY_READ_DAYS): Promise<{
  messages: InboxMessage[];
  handled: Set<string>;
}> {
  const cutoff = new Date(Date.now() - days * 86_400_000).toISOString();

  type EmailRow = {
    id: string;
    thread_id: string;
    subject: string | null;
    from_name: string | null;
    from_address: string | null;
    body_text: string | null;
    snippet: string | null;
    sent_at: string;
    thread: { job_id: string | null } | null;
    reading:
      | { id: string; signals: Signals | null }
      | Array<{ id: string; signals: Signals | null }>;
  };
  type TextSource = {
    body: string | null;
    sent_at: string;
    contact_id: string | null;
    customer_number?: string | null;
    counterpart_number?: string | null;
  };
  type TextRow = {
    id: string;
    signals: Signals | null;
    matched_job_id: string | null;
    sms: TextSource | null;
    personal: TextSource | null;
  };
  type SentEmail = {
    id: string;
    thread_id: string;
    sent_at: string;
    to_addresses: string[] | null;
    cc_addresses: string[] | null;
    thread: { job_id: string | null } | null;
  };
  type SentText = {
    id: string;
    sent_at: string;
    contact_id: string | null;
    phone: string | null;
  };

  const [emails, texts, sentEmails, sentTexts, sentPersonal, marks] =
    await Promise.all([
      supabase
        .from("email_messages")
        .select(
          "id,thread_id,subject,from_name,from_address,body_text,snippet,sent_at,thread:email_threads(job_id),reading:message_readings!inner(id,signals,sender_kind)",
        )
        .eq("direction", "inbound")
        .eq("reading.sender_kind", "customer")
        .gte("sent_at", cutoff)
        .order("sent_at", { ascending: false })
        .range(0, 999)
        .then((r) => unwrap<EmailRow[]>(r)),
      supabase
        .from("message_readings")
        .select(
          "id,signals,matched_job_id,sms:messages!message_readings_message_sid_fkey(body,sent_at,contact_id,customer_number),personal:personal_texts!message_readings_personal_text_id_fkey(body,sent_at,contact_id,counterpart_number)",
        )
        .eq("sender_kind", "customer")
        .is("email_message_id", null)
        .gte("read_at", cutoff)
        .range(0, 999)
        .then((r) => unwrap<TextRow[]>(r)),
      supabase
        .from("email_messages")
        .select(
          "id,thread_id,sent_at,to_addresses,cc_addresses,thread:email_threads(job_id)",
        )
        .eq("direction", "outbound")
        .gte("sent_at", cutoff)
        .order("sent_at", { ascending: false })
        .range(0, 999)
        .then((r) => unwrap<SentEmail[]>(r)),
      supabase
        .from("messages")
        .select("id:message_sid,sent_at,contact_id,phone:customer_number")
        .eq("direction", "outbound")
        .or("contact_id.not.is.null,customer_number.not.is.null")
        .gte("sent_at", cutoff)
        .order("sent_at", { ascending: false })
        .range(0, 999)
        .then((r) => unwrap<SentText[]>(r)),
      supabase
        .from("personal_texts")
        .select("id,sent_at,contact_id,phone:counterpart_number")
        .eq("direction", "outbound")
        .is("deleted_at", null)
        .gte("sent_at", cutoff)
        .order("sent_at", { ascending: false })
        .range(0, 999)
        .then((r) => unwrap<SentText[]>(r)),
      supabase
        .from("message_handled")
        .select("reading_id")
        .gte("handled_at", cutoff)
        .order("handled_at", { ascending: false })
        .range(0, 999)
        .then((r) => unwrap<Array<{ reading_id: string }>>(r)),
    ]);

  const messages: InboxMessage[] = [];
  const base = {
    contact_id: null,
    thread_id: null,
    job_id: null,
    from: null,
    subject: null,
    text: "",
    signals: null,
  } as const;
  for (const email of emails) {
    const reading = Array.isArray(email.reading)
      ? email.reading[0]
      : email.reading;
    if (!reading) continue;
    messages.push({
      ...base,
      id: reading.id,
      direction: "inbound",
      channel: "email",
      sent_at: email.sent_at,
      thread_id: email.thread_id,
      job_id: threadJob(email),
      from: email.from_name || email.from_address,
      from_address: email.from_address,
      subject: email.subject,
      text: replyText(email.body_text, email.snippet, SORTED_TEXT_MAX),
      signals: reading.signals,
    });
  }
  for (const text of texts) {
    const source = text.sms ?? text.personal;
    if (!source || Date.parse(source.sent_at) < Date.parse(cutoff)) continue;
    messages.push({
      ...base,
      id: text.id,
      direction: "inbound",
      channel: "text",
      sent_at: source.sent_at,
      contact_id: source.contact_id,
      phone: textNumber(source),
      job_id: text.matched_job_id,
      text: replyText(source.body, null, SORTED_TEXT_MAX),
      signals: text.signals,
    });
  }
  for (const email of sentEmails) {
    messages.push({
      ...base,
      id: email.id,
      direction: "outbound",
      channel: "email",
      sent_at: email.sent_at,
      thread_id: email.thread_id,
      job_id: threadJob(email),
      to_addresses: sentTo(email),
    });
  }
  for (const text of [...sentTexts, ...sentPersonal]) {
    messages.push({
      ...base,
      id: text.id,
      direction: "outbound",
      channel: "text",
      sent_at: text.sent_at,
      contact_id: text.contact_id,
      phone: text.phone,
    });
  }
  return { messages, handled: new Set(marks.map((m) => m.reading_id)) };
}

/**
 * Marks a customer's unanswered messages handled, in the caller's name
 * (ADR 0227). A mark already there is left as it was.
 */
export async function markMessagesHandled(
  readingIds: readonly string[],
): Promise<void> {
  if (!readingIds.length) return;
  unwrap(
    await supabase.from("message_handled").upsert(
      readingIds.map((reading_id) => ({ reading_id })),
      { onConflict: "reading_id", ignoreDuplicates: true },
    ),
  );
}

/** Every column the materials heads-up reads (ADR 0234). */
const MATERIALS_CHECK_COLUMNS =
  "job_id,fingerprint,probability,order_number,order_draft,line,quote," +
  "quote_kind,quote_at,dismissed_fingerprint,about,dismissed_about,summary";

/**
 * What Jev found comparing this job's material orders with its notes, emails
 * and texts (ADR 0234), or null when it has not been checked.
 * `materialsWarning` decides whether it says anything.
 */
export async function fetchMaterialsCheck(
  jobId: string,
): Promise<MaterialsCheckRow | null> {
  return unwrap<MaterialsCheckRow | null>(
    await supabase
      .from("materials_checks")
      .select(MATERIALS_CHECK_COLUMNS)
      .eq("job_id", jobId)
      .maybeSingle(),
  );
}

/** "Dismiss": hides the heads-up until the orders or the messages change. */
export async function dismissMaterialsCheck(jobId: string): Promise<void> {
  const { error } = await supabase.rpc("dismiss_materials_check", {
    p_job_id: jobId,
  });
  if (error) throw new Error(error.message);
}

// ------------------------------------------------------------- AI Team --

/** Everything the AI Team page reads (ADR 0259, issue #682). */
export interface AiTeamData {
  terms: AiTermsRow[];
  materials: AiMaterialsRow[];
  readings: AiReadingRow[];
  payments: { job_id: string; at: string }[];
}

/**
 * Each AI employee's findings, open and dismissed, as rows. `aiFindings` in
 * core decides which are open; this only fetches. Dismissed ones are kept for
 * `AI_DISMISSED_DAYS`, so that is how far back anything is read.
 */
export async function fetchAiTeam(): Promise<AiTeamData> {
  const cutoff = new Date(
    Date.now() - AI_DISMISSED_DAYS * 86_400_000,
  ).toISOString();
  const dismissal = "checked_at,dismissed_reason,dismissed_by,dismissed_at";

  type Text = { body: string | null; sent_at: string } | null;
  type Handled = {
    reason: string | null;
    handled_by: string | null;
    handled_at: string;
  };
  type ReadingRow = {
    id: string;
    meaning: string;
    probability: number;
    sender_kind: string | null;
    matched_job_id: string | null;
    email: {
      body_text: string | null;
      snippet: string | null;
      sent_at: string;
      rfc822_message_id: string | null;
      thread: { job_id: string | null } | null;
    } | null;
    sms: Text;
    personal: Text;
    handled: Handled | Handled[] | null;
  };

  const [terms, materials, readings, payments] = await Promise.all([
    supabase
      .from("payment_terms_checks")
      .select(`${TERMS_CHECK_COLUMNS},${dismissal}`)
      .gte("probability", TERMS_WARN_AT)
      .then((r) => unwrap<AiTermsRow[]>(r)),
    supabase
      .from("materials_checks")
      .select(`${MATERIALS_CHECK_COLUMNS},${dismissal}`)
      .gte("probability", MATERIALS_WARN_AT)
      .then((r) => unwrap<AiMaterialsRow[]>(r)),
    supabase
      .from("message_readings")
      .select(
        "id,meaning,probability,sender_kind,matched_job_id," +
          "email:email_messages!message_readings_email_message_id_fkey(body_text,snippet,sent_at,rfc822_message_id,thread:email_threads(job_id))," +
          "sms:messages!message_readings_message_sid_fkey(body,sent_at)," +
          "personal:personal_texts!message_readings_personal_text_id_fkey(body,sent_at)," +
          "handled:message_handled(reason,handled_by,handled_at)",
      )
      .in("meaning", ["accepted", "paid"])
      .eq("sender_kind", "customer")
      .gte("read_at", cutoff)
      .range(0, 999)
      .then((r) => unwrap<ReadingRow[]>(r)),
    fetchPaymentsSince(cutoff, null),
  ]);

  return {
    terms,
    materials,
    readings: readings.flatMap((row): AiReadingRow[] => {
      const message = row.email
        ? {
            body: row.email.body_text ?? row.email.snippet,
            sent_at: row.email.sent_at,
          }
        : (row.sms ?? row.personal);
      if (!message) return [];
      const handled = Array.isArray(row.handled) ? row.handled[0] : row.handled;
      return [
        {
          id: row.id,
          meaning: row.meaning,
          probability: Number(row.probability),
          sender_kind: row.sender_kind,
          job_id: row.email
            ? (row.email.thread?.job_id ?? null)
            : row.matched_job_id,
          sent_at: message.sent_at,
          kind: row.email ? "email" : "text",
          copy_of: row.email?.rfc822_message_id ?? null,
          body: message.body,
          handled: handled
            ? {
                reason: handled.reason,
                by: handled.handled_by,
                at: handled.handled_at,
              }
            : null,
        },
      ];
    }),
    payments,
  };
}

/**
 * Dismiss a finding as wrong, or as right but fine. The same dismissal the job
 * page, My list and the inbox honour, with the reason kept. Anyone using
 * the app; a practice job only a tester.
 */
export async function dismissAiFinding(
  source: AiSource,
  id: string,
  reason: AiReason,
): Promise<void> {
  const { error } = await supabase.rpc("dismiss_ai_finding", {
    p_source: source,
    p_id: id,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

/** Put a dismissed finding back. Same rule as dismissing. */
export async function reopenAiFinding(
  source: AiSource,
  id: string,
): Promise<void> {
  const { error } = await supabase.rpc("reopen_ai_finding", {
    p_source: source,
    p_id: id,
  });
  if (error) throw new Error(error.message);
}

/* ==========================================================================
 * Supplier mail to orders@ (ADR 0311)
 * ======================================================================== */

/** One attachment on a supplier's email, or its body as a PDF. */
export interface SupplierMailFileRow {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number | null;
  bucket: string;
  path: string;
  is_body: boolean;
  job_file_id: string | null;
  /** SHA-256 of the bytes: how a forwarded copy is recognised. */
  content_sha256?: string | null;
  /** Where the document says the materials go, as the reader copied it (ADR 0317). */
  ship_to_name?: string | null;
  ship_to_address?: string | null;
}

/** An email a supplier sent to orders@, as the review screen reads it. */
export interface SupplierMailRow extends TaskSupplierMail {
  material_order_id: string | null;
  body_text: string | null;
  files: SupplierMailFileRow[];
}

const SUPPLIER_MAIL_COLUMNS =
  "id,status,kind,job_id,material_order_id,subject,from_name,from_address," +
  "sent_at,review_reasons,body_text," +
  "material_order:material_orders(order_number)," +
  "files:supplier_mail_files(id,filename,mime_type,size_bytes,bucket,path,is_body,job_file_id,content_sha256,ship_to_name,ship_to_address)";

function shapeSupplierMail(row: Record<string, unknown>): SupplierMailRow {
  const order = row.material_order as { order_number?: number } | null;
  return {
    ...(row as unknown as SupplierMailRow),
    order_number: order?.order_number ?? null,
    files: ((row.files as SupplierMailFileRow[] | null) ?? []).sort((a, b) =>
      a.filename.localeCompare(b.filename),
    ),
  };
}

/** The emails waiting for a person, newest first. */
export async function fetchSupplierMailForReview(): Promise<SupplierMailRow[]> {
  const rows = unwrap<Record<string, unknown>[]>(
    await supabase
      .from("supplier_mail")
      .select(SUPPLIER_MAIL_COLUMNS)
      .eq("status", "review")
      .order("sent_at", { ascending: false })
      .limit(100),
  );
  return rows.map(shapeSupplierMail);
}

/**
 * What supplier mail puts on My List: everything waiting for a person, and
 * every filed credit memo, with the returns that would settle one.
 *
 * Not scoped to this person's jobs: an email with no job is everybody's
 * (`supplierMailTasks` decides whose list each one lands on).
 */
export async function fetchSupplierMailSignals(): Promise<{
  supplierMail: TaskSupplierMail[];
  materialReturns: TaskMaterialReturn[];
}> {
  const rows = unwrap<Record<string, unknown>[]>(
    await supabase
      .from("supplier_mail")
      .select(
        "id,status,kind,job_id,material_order_id,subject,from_name,from_address,sent_at,review_reasons," +
          "material_order:material_orders(order_number)",
      )
      .or("status.eq.review,and(status.eq.filed,kind.eq.credit_memo)")
      .order("sent_at", { ascending: false })
      .limit(200),
  );
  const supplierMail = rows.map((row) => {
    const order = row.material_order as { order_number?: number } | null;
    return {
      ...(row as unknown as TaskSupplierMail),
      order_number: order?.order_number ?? null,
    };
  });
  const memoOrders = [
    ...new Set(
      supplierMail
        .filter((m) => m.status === "filed" && m.material_order_id)
        .map((m) => m.material_order_id as string),
    ),
  ];
  const materialReturns = memoOrders.length
    ? unwrap<TaskMaterialReturn[]>(
        await supabase
          .from("material_returns")
          .select("material_order_id,created_at")
          .in("material_order_id", memoOrders),
      )
    : [];
  return { supplierMail, materialReturns };
}

/** An order a waiting email can be filed on. */
export interface OrderForSupplierMail {
  id: string;
  job_id: string;
  order_number: number;
  status: string;
  supplier: { name: string | null; email?: string | null } | null;
  job: {
    job_number: number | null;
    address: string | null;
    contact: { full_name: string | null } | null;
  } | null;
}

/** Orders to file an email on, newest first: the one it is about is usually recent. */
export async function fetchOrdersForSupplierMail(): Promise<
  OrderForSupplierMail[]
> {
  return unwrap<OrderForSupplierMail[]>(
    await supabase
      .from("material_orders")
      .select(
        "id,job_id,order_number,status,supplier:suppliers(name,email)," +
          "job:jobs(job_number,address,contact:contacts(full_name))",
      )
      .order("order_number", { ascending: false })
      .limit(300),
  );
}

/** "MO-900095 · ABC Supply · Kyle Clark, 1 Windsor Cir" */
export function orderChoiceLabel(order: OrderForSupplierMail): string {
  return [
    `MO-${order.order_number}`,
    order.supplier?.name ?? null,
    [order.job?.contact?.full_name, order.job?.address]
      .filter(Boolean)
      .join(", ") || null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * Documents from orders@ already on a job, by fingerprint, other than this
 * email's own: the same forwarded-copy check the function makes, keyed the
 * way `planSupplierMail` takes it. Maps each hash to the file it was filed as.
 */
async function supplierMailOnJob(
  mail: SupplierMailRow,
  jobId: string,
): Promise<{ hashes: Map<string, Set<string>>; earlier: Map<string, string> }> {
  const sums = mail.files
    .map((f) => f.content_sha256)
    .filter((h): h is string => !!h);
  const hashes = new Map<string, Set<string>>();
  const earlier = new Map<string, string>();
  if (sums.length === 0) return { hashes, earlier };
  const rows = unwrap<
    {
      id: string;
      content_sha256: string;
      job_file: { job_id: string | null } | null;
    }[]
  >(
    await supabase
      .from("supplier_mail_files")
      .select(
        "id,content_sha256,job_file:job_files!supplier_mail_files_job_file_id_fkey(job_id)",
      )
      .in("content_sha256", sums)
      .not("job_file_id", "is", null)
      .neq("supplier_mail_id", mail.id),
  );
  const onJob = new Set<string>();
  for (const row of rows) {
    if (row.job_file?.job_id !== jobId) continue;
    onJob.add(row.content_sha256);
    earlier.set(row.content_sha256, row.id);
  }
  hashes.set(jobId, onJob);
  return { hashes, earlier };
}

/**
 * File a waiting email on the order a person chose, as what they said it is.
 *
 * `planManualSupplierFiling` says what happens to each file: a new
 * `job_files` row (the same register the function writes, titled the same
 * way), a correction to the row the function already wrote, so Docs shows
 * the kind and order the person chose, or, for a copy of a document already
 * on that job, a note of which and nothing filed. The email is marked filed
 * with the person's name on it. Nothing is moved: the bytes stay where the
 * function put them.
 *
 * The title carries the supplier's name only when the email came from that
 * supplier's address on file; otherwise the sender's own address, so nobody
 * reads a stranger's document as the supplier's (ADR 0311).
 */
export async function fileSupplierMail(entry: {
  mail: SupplierMailRow;
  order: OrderForSupplierMail;
  kind: SupplierMailKind;
  profileId: string | null;
}): Promise<void> {
  const { mail, order, kind, profileId } = entry;
  const { hashes, earlier } = await supplierMailOnJob(mail, order.job_id);
  const steps = planManualSupplierFiling({
    files: mail.files,
    jobId: order.job_id,
    filedHashes: hashes,
  });
  const supplier = senderMatchesSupplier(
    mail.from_address,
    order.supplier?.email,
  )
    ? (order.supplier?.name ?? null)
    : mail.from_address || mail.from_name || null;
  const byId = new Map(mail.files.map((f) => [f.id, f]));

  for (const step of steps) {
    const file = byId.get(step.fileId);
    if (!file) continue;
    if (step.action === "duplicate") {
      await updateRow("supplier_mail_files", file.id, {
        duplicate_of_id: earlier.get(file.content_sha256 ?? "") ?? null,
      });
      continue;
    }
    const filing = {
      job_id: order.job_id,
      material_order_id: order.id,
      kind: SUPPLIER_MAIL_FILE_KIND[kind],
      title: supplierDocumentTitle({
        orderNumber: order.order_number,
        kind,
        supplier,
        sentAt: mail.sent_at,
        sequence: step.sequence,
      }),
    };
    if (step.action === "update" && file.job_file_id) {
      await updateRow("job_files", file.job_file_id, filing);
      continue;
    }
    const registered = await insertRow<{ id: string }>("job_files", {
      ...filing,
      supplier_mail_id: mail.id,
      bucket: file.bucket,
      path: file.path,
      mime_type: file.mime_type,
      size_bytes: file.size_bytes,
      uploaded_by: profileId,
    });
    await updateRow("supplier_mail_files", file.id, {
      job_file_id: registered.id,
    });
  }
  await updateRow("supplier_mail", mail.id, {
    status: "filed",
    kind,
    kind_source: "person",
    material_order_id: order.id,
    job_id: order.job_id,
    review_reasons: [],
    filed_at: new Date().toISOString(),
    reviewed_by: profileId,
    reviewed_at: new Date().toISOString(),
  });
}

/** Set an email aside: it was never a supplier document. */
export async function ignoreSupplierMail(
  id: string,
  reason: string,
  profileId: string | null,
): Promise<void> {
  await updateRow("supplier_mail", id, {
    status: "ignored",
    ignored_reason: reason.trim() || null,
    reviewed_by: profileId,
    reviewed_at: new Date().toISOString(),
  });
}

/**
 * Delete a waiting email from Tools → Supplier Mail (ADR 0316).
 *
 * The email is marked deleted first, rather than removed: the orders-inbox
 * function stores every email it has no row for, thirty days back, so a
 * row that is really gone is read again within minutes. The database
 * refuses the mark on an email whose document is already on a job, and
 * nothing has been destroyed yet when it does. Only once the mark is in do
 * the file rows and then the bytes go.
 */
export async function deleteSupplierMail(
  mail: Pick<SupplierMailRow, "id" | "files">,
  profileId: string | null,
): Promise<void> {
  await updateRow("supplier_mail", mail.id, {
    status: "deleted",
    deleted_by: profileId,
    deleted_at: new Date().toISOString(),
  });
  const { error: filesError } = await supabase
    .from("supplier_mail_files")
    .delete()
    .eq("supplier_mail_id", mail.id);
  if (filesError) throw new Error(filesError.message);
  const byBucket = new Map<string, string[]>();
  for (const file of mail.files) {
    byBucket.set(file.bucket, [
      ...(byBucket.get(file.bucket) ?? []),
      file.path,
    ]);
  }
  for (const [bucket, paths] of byBucket) {
    const { error } = await supabase.storage.from(bucket).remove(paths);
    if (error) throw new Error(error.message);
  }
}

/** Whether any of the email's documents is already on a job: then no Delete here. */
export function supplierMailIsOnJob(
  mail: Pick<SupplierMailRow, "files">,
): boolean {
  return mail.files.some((f) => f.job_file_id !== null);
}

/** A signed link to one of an email's files, good for an hour. */
export async function signSupplierMailFile(
  file: SupplierMailFileRow,
): Promise<string> {
  return signJobFile(file);
}

/**
 * A filed document's bytes, as a File, so the order screen can run the same
 * check on it that an upload gets (ADR 0145).
 */
export async function downloadJobFile(entry: JobFileEntry): Promise<File> {
  const { data, error } = await supabase.storage
    .from(entry.bucket)
    .download(entry.path);
  if (error) throw new Error(error.message);
  const name = entry.path.split("/").pop() ?? entry.title;
  return new File([data], name, {
    type: entry.mime_type ?? data.type ?? "application/octet-stream",
  });
}

/** Ask the function to read orders@ now, rather than waiting for its schedule. */
export async function checkOrdersInbox(): Promise<{
  read: number;
  filed: number;
  review: number;
  /** Of those waiting, how many Jonathon was emailed about. */
  notified: number;
  errors: string[];
}> {
  const { data, error } = await supabase.functions.invoke("orders-inbox", {
    body: {},
  });
  if (error) {
    const context = (error as { context?: unknown }).context;
    if (context instanceof Response) {
      const payload = await context.json().catch(() => null);
      if (payload && typeof payload.error === "string")
        throw new Error(payload.error);
    }
    throw new Error(error.message);
  }
  const payload = (data ?? {}) as Record<string, unknown>;
  return {
    read: Number(payload.read ?? 0),
    filed: Number(payload.filed ?? 0),
    review: Number(payload.review ?? 0),
    notified: Number(payload.notified ?? 0),
    errors: Array.isArray(payload.errors) ? payload.errors.map(String) : [],
  };
}
